import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import Auth from './Auth';
import { ThemeStyles, C, S, applyTheme, primaryFill, primaryText } from './theme';

const EXCHANGE_API_KEY = '8d9b50901859d1976856cfe7';

// App version — bump this each time you deploy an update so you can confirm the live build.
const APP_VERSION = 'v1.11.4';

// Comprehensive global currency list (code, symbol, Chinese name, English name)
const WORLD_CURRENCIES = [
  { code: 'USD', symbol: '$', name_zh: '美元', name_en: 'US Dollar' },
  { code: 'EUR', symbol: '€', name_zh: '欧元', name_en: 'Euro' },
  { code: 'CNY', symbol: '¥', name_zh: '人民币', name_en: 'Chinese Yuan' },
  { code: 'JPY', symbol: '¥', name_zh: '日元', name_en: 'Japanese Yen' },
  { code: 'GBP', symbol: '£', name_zh: '英镑', name_en: 'British Pound' },
  { code: 'HKD', symbol: 'HK$', name_zh: '港币', name_en: 'Hong Kong Dollar' },
  { code: 'TWD', symbol: 'NT$', name_zh: '新台币', name_en: 'Taiwan Dollar' },
  { code: 'SGD', symbol: 'S$', name_zh: '新加坡元', name_en: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name_zh: '澳元', name_en: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name_zh: '加元', name_en: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'Fr', name_zh: '瑞士法郎', name_en: 'Swiss Franc' },
  { code: 'KRW', symbol: '₩', name_zh: '韩元', name_en: 'South Korean Won' },
  { code: 'INR', symbol: '₹', name_zh: '印度卢比', name_en: 'Indian Rupee' },
  { code: 'THB', symbol: '฿', name_zh: '泰铢', name_en: 'Thai Baht' },
  { code: 'MYR', symbol: 'RM', name_zh: '马来西亚林吉特', name_en: 'Malaysian Ringgit' },
  { code: 'PHP', symbol: '₱', name_zh: '菲律宾比索', name_en: 'Philippine Peso' },
  { code: 'IDR', symbol: 'Rp', name_zh: '印尼盾', name_en: 'Indonesian Rupiah' },
  { code: 'VND', symbol: '₫', name_zh: '越南盾', name_en: 'Vietnamese Dong' },
  { code: 'NZD', symbol: 'NZ$', name_zh: '新西兰元', name_en: 'New Zealand Dollar' },
  { code: 'AED', symbol: 'د.إ', name_zh: '阿联酋迪拉姆', name_en: 'UAE Dirham' },
  { code: 'SAR', symbol: '﷼', name_zh: '沙特里亚尔', name_en: 'Saudi Riyal' },
  { code: 'RUB', symbol: '₽', name_zh: '俄罗斯卢布', name_en: 'Russian Ruble' },
  { code: 'BRL', symbol: 'R$', name_zh: '巴西雷亚尔', name_en: 'Brazilian Real' },
  { code: 'MXN', symbol: 'Mex$', name_zh: '墨西哥比索', name_en: 'Mexican Peso' },
  { code: 'ZAR', symbol: 'R', name_zh: '南非兰特', name_en: 'South African Rand' },
  { code: 'TRY', symbol: '₺', name_zh: '土耳其里拉', name_en: 'Turkish Lira' },
  { code: 'SEK', symbol: 'kr', name_zh: '瑞典克朗', name_en: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name_zh: '挪威克朗', name_en: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name_zh: '丹麦克朗', name_en: 'Danish Krone' },
  { code: 'PLN', symbol: 'zł', name_zh: '波兰兹罗提', name_en: 'Polish Zloty' },
];


// ─── Translations ────────────────────────────────────────────────────────────
const TRANSLATIONS = {
  zh: {
    overview: '总览', transactions: '交易记录', reports: '报表', settings: '设置',
    myAccounts: '我的账户', addAccount: '+ 添加账户',
    totalAssets: '总资产', monthlyIncome: '本月收入', monthlyExpense: '本月支出', monthlySurplus: '本月结余',
    recentTxn: '最近交易', addTxn: '+ 新增交易',
    date: '日期', merchant: '商家', note: '备注', category: '分类', account: '账户', amount: '金额',
    income: '收入', expense: '支出', both: '两者', balance: '结余', transfer: '转账',
    transferFrom: '从账户', transferTo: '转入账户',
    newTxn: '新增交易', cancel: '取消', save: '保存', delete: '删除',
    amountLabel: '金额', merchantLabel: '商家（选填）', dateLabel: '日期',
    addCategory: '+ 添加分类', manageCategories: '管理分类',
    parentCategory: '归入大类（选填）', noParent: '无（设为大类）',
    categoryName: '分类名称（中文）', categoryNameEn: '分类名称（英文）', categoryType: '类型',
    currencySettings: '币种设置', defaultCurrency: '默认币种',
    exchangeRateSettings: '汇率设置',
    autoMode: '自动汇率（实时更新）', manualMode: '手动汇率（自己输入）',
    refreshRates: '刷新汇率', rateUpdated: '汇率已更新',
    manualRateHint: '请输入 1 单位该币种 = 多少默认币种',
    expenseBreakdown: '支出分类统计', noTxn: '暂无交易记录',
    rptThisMonth: '本月', rptLastMonth: '上月', rptThisYear: '今年', rptAll: '全部',
    rptExpenseByCat: '支出分类', rptIncomeBySource: '收入来源', rptAccountDist: '账户余额分布',
    rptIncomeVsExpense: '收支对比', rptIncome: '收入', rptExpense: '支出', rptNoData: '该时间段暂无数据',
    rptNetSavings: '净结余',
    english: '🌐 English', chinese: '🌐 中文',
    confirmDelete: '确定要删除这笔交易吗？',
    confirmDeleteAccount: '确定删除此账户？所有相关交易也会一并删除。',
    confirmDeleteCategory: '确定删除此分类？',
    useDefault: '使用默认', accountCurrency: '账户币种',
    totalByDefault: '折合默认币种合计', assetsByCurrency: '各币种资产',
    cryptoSearch: '搜寻加密货币', cryptoSelected: '已选择',
    cryptoPlaceholder: '输入名称搜寻，如 bitcoin、ethereum',
    cryptoSearching: '搜寻中...', cryptoAmountLabel: '持有数量（枚）',
    cryptoSelectFirst: '请先搜寻并选择加密货币',
    cryptoEnterAmount: '请输入持有数量',
    cryptoPriceFail: '无法取得价格，请确认是否为正确的 CoinGecko ID（如 bitcoin、ethereum）',
    acctName: '账户名称', initialBalance: '初始余额',
    acctType: '账户类型', color: '颜色',
    checking: '支票账户', savings: '储蓄账户', credit: '信用卡账户', crypto: '加密货币钱包', cash: '现金账户', stock: '股票账户',
    stockHoldings: '持仓', addStock: '添加股票', buyStock: '买入', sellStock: '卖出',
    stockSymbol: '股票代码', companyName: '公司名称', shares: '股数', avgCost: '成本价', currentPrice: '现价',
    marketValue: '市值', unrealizedPnl: '浮动盈亏', stockMarket: '市场', manualPrice: '手动填价',
    totalCost: '总成本', refreshPrices: '刷新股价', noHoldings: '暂无持仓，点"添加股票"开始',
    pricePerShare: '每股价格', usMarket: '美股', cnMarket: 'A股', hkMarket: '港股',
    cashBalance: '现金', depositCash: '存入现金', withdrawCash: '取出现金', deposit: '存入', withdraw: '取出',
    totalAssetsAcct: '账户总额', tradeHistory: '买卖记录', amount2: '金额',
    budget: '预算', addBudget: '添加预算', budgetName: '预算名称', budgetAmount: '预算金额',
    budgetPeriod: '周期', periodWeek: '每周', periodMonth: '每月', periodYear: '每年',
    budgetCategory: '关联分类', budgetTotal: '总预算（所有支出）', spent: '已花', remaining: '剩余',
    overBudget: '超支', noBudgets: '还没有预算，点"添加预算"开始规划', budgetFor: '预算',
    onTrack: '正常', warning: '接近上限',
    recurring: '定期交易', addRecurring: '添加定期交易', recurringDesc: '自动按周期生成交易（如房租、工资、订阅）',
    periodDaily: '每天', periodWeekly: '每周', periodMonthly: '每月', periodYearly: '每年',
    everyMonthDay: '每月几号', advanceDays: '提前生成天数', advanceHint: '到期前几天自动生成（0=当天）', nextDue: '下次到期',
    pendingLabel: '待确认', confirmBtn: '确认', autoGenerated: '已自动记录', recurDay: '号', weekdayNames: '日一二三四五六',
    creditLimit: '信用额度', spend: '消费', repay: '还款', availableCredit: '剩余额度', owed: '当前欠款',
    openingDebt: '开户初始欠款',
    exportData: '导出 / 导入数据', export: '导出', import: '导入',
    exportCSV: '导出 CSV', exportJSON: '导出 JSON（完整备份）',
    importCSV: '导入 CSV', importJSON: '导入 JSON（还原备份）',
    importedTxns: (n) => `成功导入 ${n} 笔交易`,
    invalidFormat: '格式不正确',
    perCoin: '/ 枚',
    incomeExpenseTrend: '收支趋势（近12个月）', monthRangeEnd: '截止月份',
  },
  en: {
    overview: 'Overview', transactions: 'Transactions', reports: 'Reports', settings: 'Settings',
    myAccounts: 'My Accounts', addAccount: '+ Add Account',
    totalAssets: 'Total Assets', monthlyIncome: 'Monthly Income', monthlyExpense: 'Monthly Expense', monthlySurplus: 'Net Savings',
    recentTxn: 'Recent Transactions', addTxn: '+ Add Transaction',
    date: 'Date', merchant: 'Merchant', note: 'Note', category: 'Category', account: 'Account', amount: 'Amount',
    income: 'Income', expense: 'Expense', both: 'Both', balance: 'Balance', transfer: 'Transfer',
    transferFrom: 'From Account', transferTo: 'To Account',
    newTxn: 'New Transaction', cancel: 'Cancel', save: 'Save', delete: 'Delete',
    amountLabel: 'Amount', merchantLabel: 'Merchant (optional)', dateLabel: 'Date',
    addCategory: '+ Add Category', manageCategories: 'Manage Categories',
    parentCategory: 'Parent Category (optional)', noParent: 'None (top-level)',
    categoryName: 'Category Name (Chinese)', categoryNameEn: 'Category Name (English)', categoryType: 'Type',
    currencySettings: 'Currency Settings', defaultCurrency: 'Default Currency',
    exchangeRateSettings: 'Exchange Rate Settings',
    autoMode: 'Auto (Real-time)', manualMode: 'Manual (Enter yourself)',
    refreshRates: 'Refresh Rates', rateUpdated: 'Rates updated',
    manualRateHint: 'Enter: 1 unit of this currency = how many default currency',
    expenseBreakdown: 'Expense Breakdown', noTxn: 'No transactions yet',
    rptThisMonth: 'This Month', rptLastMonth: 'Last Month', rptThisYear: 'This Year', rptAll: 'All Time',
    rptExpenseByCat: 'Expenses by Category', rptIncomeBySource: 'Income by Source', rptAccountDist: 'Account Balances',
    rptIncomeVsExpense: 'Income vs Expense', rptIncome: 'Income', rptExpense: 'Expense', rptNoData: 'No data for this period',
    rptNetSavings: 'Net Savings',
    english: '🌐 English', chinese: '🌐 中文',
    confirmDelete: 'Delete this transaction?',
    confirmDeleteAccount: 'Delete this account? All related transactions will also be deleted.',
    confirmDeleteCategory: 'Delete this category?',
    useDefault: 'Use Default', accountCurrency: 'Account Currency',
    totalByDefault: 'Total in Default Currency', assetsByCurrency: 'Assets by Currency',
    cryptoSearch: 'Search Cryptocurrency', cryptoSelected: 'Selected',
    cryptoPlaceholder: 'Search, e.g. bitcoin, ethereum',
    cryptoSearching: 'Searching...', cryptoAmountLabel: 'Amount (coins)',
    cryptoSelectFirst: 'Please search and select a cryptocurrency',
    cryptoEnterAmount: 'Please enter the amount held',
    cryptoPriceFail: 'Cannot get price. Please use a valid CoinGecko ID (e.g. bitcoin, ethereum)',
    acctName: 'Account Name', initialBalance: 'Initial Balance',
    acctType: 'Account Type', color: 'Color',
    checking: 'Checking', savings: 'Savings', credit: 'Credit Card', crypto: 'Crypto Wallet', cash: 'Cash', stock: 'Stock Account',
    stockHoldings: 'Holdings', addStock: 'Add Stock', buyStock: 'Buy', sellStock: 'Sell',
    stockSymbol: 'Symbol', companyName: 'Company', shares: 'Shares', avgCost: 'Avg Cost', currentPrice: 'Price',
    marketValue: 'Market Value', unrealizedPnl: 'Unrealized P&L', stockMarket: 'Market', manualPrice: 'Manual price',
    totalCost: 'Total Cost', refreshPrices: 'Refresh Prices', noHoldings: 'No holdings yet. Tap "Add Stock" to start',
    pricePerShare: 'Price/Share', usMarket: 'US', cnMarket: 'China A', hkMarket: 'HK',
    cashBalance: 'Cash', depositCash: 'Deposit Cash', withdrawCash: 'Withdraw Cash', deposit: 'Deposit', withdraw: 'Withdraw',
    totalAssetsAcct: 'Total Value', tradeHistory: 'Trade History', amount2: 'Amount',
    budget: 'Budget', addBudget: 'Add Budget', budgetName: 'Budget Name', budgetAmount: 'Amount',
    budgetPeriod: 'Period', periodWeek: 'Weekly', periodMonth: 'Monthly', periodYear: 'Yearly',
    budgetCategory: 'Category', budgetTotal: 'Total (all expenses)', spent: 'Spent', remaining: 'Left',
    overBudget: 'Over', noBudgets: 'No budgets yet. Tap "Add Budget" to start planning', budgetFor: 'Budget',
    onTrack: 'On track', warning: 'Near limit',
    recurring: 'Schedule', addRecurring: 'Add Schedule', recurringDesc: 'Auto-generate transactions on a schedule (rent, salary, subscriptions)',
    periodDaily: 'Daily', periodWeekly: 'Weekly', periodMonthly: 'Monthly', periodYearly: 'Yearly',
    everyMonthDay: 'Day of month', advanceDays: 'Generate in advance', advanceHint: 'Days before due to auto-generate (0=on the day)', nextDue: 'Next due',
    pendingLabel: 'Pending', confirmBtn: 'Confirm', autoGenerated: 'Auto-recorded', recurDay: '', weekdayNames: 'SunMonTueWedThuFriSat',
    creditLimit: 'Credit Limit', spend: 'Spend', repay: 'Repay', availableCredit: 'Available Credit', owed: 'Current Balance Owed',
    openingDebt: 'Opening Balance Owed',
    exportData: 'Export / Import Data', export: 'Export', import: 'Import',
    exportCSV: 'Export CSV', exportJSON: 'Export JSON (Full Backup)',
    importCSV: 'Import CSV', importJSON: 'Import JSON (Restore Backup)',
    importedTxns: (n) => `Imported ${n} transactions`,
    invalidFormat: 'Invalid format',
    perCoin: '/ coin',
    incomeExpenseTrend: 'Income vs Expense (last 12 months)', monthRangeEnd: 'End month',
  },
};

// ─── Shared Styles ────────────────────────────────────────────────────────────
// ─── App ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [lang, setLang] = useState('zh');
  const t = TRANSLATIONS[lang];

  // Detect mobile (narrow screen) to switch layout
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Theme: 'light' (violet) | 'dark' (neon). Default light. Persisted.
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('em_theme') || 'light'; } catch { return 'light'; }
  });
  applyTheme(theme);

  function toggleTheme() {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    applyTheme(next);
    try { localStorage.setItem('em_theme', next); } catch {}
    if (session?.user?.id) {
      (async () => {
        await supabase.from('settings').delete().eq('user_id', session.user.id).eq('key', 'theme');
        await supabase.from('settings').insert({ key: 'theme', value: next, user_id: session.user.id });
      })();
    }
  }

  // Auth
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);     // { id, email, role, status }
  const [authChecked, setAuthChecked] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false); // admin panel toggle
  const userId = session?.user?.id;
  // Pro membership: true if plan is 'pro' and not expired. Used to gate Pro-only
  // features like Plaid auto-import. Admins are always treated as Pro.
  const isPro = profile?.role === 'admin' || (
    profile?.plan === 'pro' &&
    (!profile?.plan_expires || new Date(profile.plan_expires) > new Date())
  );

  // Data
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [enabledCurrencyCodes, setEnabledCurrencyCodes] = useState([]); // codes the user enabled
  const [defaultCurrency, setDefaultCurrency] = useState({ symbol: '¥', code: 'CNY' });
  const [exchangeRates, setExchangeRates] = useState({});
  const [exchangeRateMode, setExchangeRateMode] = useState('auto');
  const [manualRates, setManualRates] = useState({});
  const [cryptoPrices, setCryptoPrices] = useState({});

  // UI
  const [tab, setTab] = useState('overview');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverDown, setServerDown] = useState(false); // true when loadAll fails (DB outage)
  const [ratesLoading, setRatesLoading] = useState(false);
  const [plaidBusy, setPlaidBusy] = useState(false); // Plaid connect/sync in progress
  // Mapping modal: after connecting, user decides new/existing/skip for each bank sub-account
  const [plaidMapping, setPlaidMapping] = useState(null); // { item_id, institution_name, rows: [...] }
  // Batch categorize modal: assign a category to many uncategorized transactions at once
  const [batchCat, setBatchCat] = useState(null); // { accountId } | null
  const [batchSelected, setBatchSelected] = useState([]); // selected transaction ids
  const [batchCatId, setBatchCatId] = useState(''); // chosen category to apply
  const [showDupReview, setShowDupReview] = useState(false); // review possible-duplicate transactions

  // Transaction modal
  const [showModal, setShowModal] = useState(false);
  // 批量记账
  const [showBatch, setShowBatch] = useState(false);
  const [batchType, setBatchType] = useState('expense');       // expense | income
  const [batchAcctId, setBatchAcctId] = useState('');
  const [batchDate, setBatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [batchLines, setBatchLines] = useState([{ amount: '', catId: '', merchant: '', note: '', showMore: false }]);
  const [batchSaving, setBatchSaving] = useState(false);
  const [savingTxn, setSavingTxn] = useState(false); // 单笔记账保存中，防重复点击
  const [showGuide, setShowGuide] = useState(false); // user guide modal
  // 账户分组折叠状态：{ checking:true, credit:false, ... }，记到 localStorage
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('em_collapsed_groups') || '{}'); } catch { return {}; }
  });
  function toggleGroup(type) {
    setCollapsedGroups(prev => {
      const next = { ...prev, [type]: !prev[type] };
      try { localStorage.setItem('em_collapsed_groups', JSON.stringify(next)); } catch {}
      return next;
    });
  }
  const [showTopMenu, setShowTopMenu] = useState(false); // 手机版顶部 ⋯ 小菜单
  // 记账界面精简：钉住的分类/账户(用户自定义，存localStorage) + 更多展开开关
  const [pinnedCats, setPinnedCats] = useState(() => {
    try { return JSON.parse(localStorage.getItem('em_pinned_cats') || '[]'); } catch { return []; }
  });
  const [pinnedAccts, setPinnedAccts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('em_pinned_accts') || '[]'); } catch { return []; }
  });
  const [showAllCats, setShowAllCats] = useState(false);
  const [showAllAccts, setShowAllAccts] = useState(false);
  function togglePinCat(id) {
    setPinnedCats(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try { localStorage.setItem('em_pinned_cats', JSON.stringify(next)); } catch {}
      return next;
    });
  }
  function togglePinAcct(id) {
    setPinnedAccts(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try { localStorage.setItem('em_pinned_accts', JSON.stringify(next)); } catch {}
      return next;
    });
  }
  // 新手引导：完成/跳过后记到 localStorage，永不再显示
  const [onboardDone, setOnboardDone] = useState(() => {
    try { return localStorage.getItem('em_onboard_done') === '1'; } catch { return false; }
  });
  function finishOnboard() {
    try { localStorage.setItem('em_onboard_done', '1'); } catch {}
    setOnboardDone(true);
  }
  const [txnType, setTxnType] = useState('expense');
  const [amount, setAmount] = useState('');
  const [merchant, setMerchant] = useState('');
  const [note, setNote] = useState('');
  const [catId, setCatId] = useState('');
  const [acctId, setAcctId] = useState('');
  const [transferToAcctId, setTransferToAcctId] = useState('');
  const [transferCryptoHoldingId, setTransferCryptoHoldingId] = useState(''); // which coin to use when paying from a crypto wallet
  const [txnDate, setTxnDate] = useState(new Date().toISOString().split('T')[0]);
  const [showExtras, setShowExtras] = useState(false); // merchant/note collapsed by default
  const [scanning, setScanning] = useState(false);      // receipt OCR in progress
  const [scanMsg, setScanMsg] = useState('');           // scan status/error message
  const [editTxnMobile, setEditTxnMobile] = useState(null); // txn being edited on mobile (modal)
  const [reportRange, setReportRange] = useState('month');  // report time filter: month|lastMonth|year|all
  const [needsSetup, setNeedsSetup] = useState(false);      // first-time currency setup wizard
  const [setupChosen, setSetupChosen] = useState(['USD']);  // currencies picked in wizard
  const [setupDefault, setSetupDefault] = useState('USD');  // default currency picked in wizard
  const [setupSaving, setSetupSaving] = useState(false);
  const [stockHoldings, setStockHoldings] = useState([]);     // all holdings for stock accounts
  const [stockPrices, setStockPrices] = useState({});         // symbol -> {current, changePct}
  const [showStockModal, setShowStockModal] = useState(false); // add/buy/sell stock modal
  const [stockModalMode, setStockModalMode] = useState('add'); // 'add' | 'buy' | 'sell'
  const [stockModalHolding, setStockModalHolding] = useState(null); // holding being bought/sold
  const [stockForm, setStockForm] = useState({ symbol: '', company: '', market: 'US', shares: '', price: '' });
  const [stockSaving, setStockSaving] = useState(false);
  const [cryptoHoldings, setCryptoHoldings] = useState([]);
  const [plaidItems, setPlaidItems] = useState([]); // connected banks (Plaid)
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [cryptoModalMode, setCryptoModalMode] = useState('add'); // 'add' | 'buy' | 'sell'
  const [cryptoModalHolding, setCryptoModalHolding] = useState(null);
  const [cryptoForm, setCryptoForm] = useState({ coin_id: '', ticker: '', coin_name: '', amount: '', price: '', total: '' });
  const [cryptoSaving, setCryptoSaving] = useState(false);
  const [holdingSearchResults, setHoldingSearchResults] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ name: '', category_id: '', amount: '', period: 'month' });
  const [editingBudget, setEditingBudget] = useState(null);
  const [recurring, setRecurring] = useState([]);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [recurForm, setRecurForm] = useState({ type: 'expense', amount: '', account_id: '', transfer_to_account_id: '', category_id: '', description: '', note: '', period_type: 'monthly', period_day: 1, period_month: 1, advance_days: 0 });
  const [recurringMsg, setRecurringMsg] = useState('');
  const recurringProcessedRef = useRef(false);
  const [editingTxn, setEditingTxn] = useState(null);

  // ── Sidebar resize state ─────────────────────────────────────────────────
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem('em_sidebar_width'), 10);
      if (saved && saved >= 160 && saved <= 400) return saved;
    } catch {}
    return 232;
  });
  const sidebarDragging = useRef(false);
  const sidebarWidthRef = useRef(232); // mirrors sidebarWidth for use inside stable event handlers
  const sidebarDragX    = useRef(0);
  const sidebarDragW    = useRef(232);

  // Add Account modal
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAcctName, setNewAcctName] = useState('');
  const [newAcctBalance, setNewAcctBalance] = useState('');
  const [newAcctColor, setNewAcctColor] = useState('#1D9E75');
  const [newAcctType, setNewAcctType] = useState('checking');
  const [newAcctCurrency, setNewAcctCurrency] = useState('');
  const [newCreditLimit, setNewCreditLimit] = useState(''); // credit card limit
  const [newAcctNumber, setNewAcctNumber] = useState('');    // account number (optional)
  // Edit Account modal
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [editAcct, setEditAcct] = useState(null);   // { id, name, color, account_number, credit_limit, ... }
  // Crypto-specific (new account form)
  const [newCryptoTicker, setNewCryptoTicker] = useState('');   // Display ticker, e.g. "ETH"

  // Add Category modal
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCatZh, setNewCatZh] = useState('');
  const [newCatEn, setNewCatEn] = useState('');
  const [newCatType, setNewCatType] = useState('expense');
  const [newCatParent, setNewCatParent] = useState(''); // parent category id, '' = top-level

  // Chart: end month (anchor). Shows 12 months ending at this month. Default = current month.
  const [chartEndMonth, setChartEndMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // ── Auth: track session and load profile ──────────────────────────────────
  useEffect(() => {
    // Shared helper: only change session state when the actual user id changes,
    // so a token refresh on tab refocus does NOT reload the app / clear forms.
    const applySession = (sess) => {
      setAuthChecked(true);
      setSession(prev => {
        const prevId = prev?.user?.id || null;
        const nextId = sess?.user?.id || null;
        if (prevId === nextId) return prev; // same user (or still logged out) → no reload
        return sess;
      });
    };

    // Get current session on mount
    supabase.auth.getSession().then(({ data }) => applySession(data.session));

    // Listen for login/logout. Ignore token-refresh / user-update events.
    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        setAuthChecked(true);
        return;
      }
      applySession(sess);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // When a session appears, load that user's profile + their data
  useEffect(() => {
    if (!session) { setProfile(null); return; }
    (async () => {
      const { data: prof } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      setProfile(prof);
      // Only load app data if the account is active
      if (prof && prof.status === 'active') {
        // Record this sign-in time (best-effort, don't block on it)
        supabase.from('profiles').update({ last_sign_in_at: new Date().toISOString() }).eq('id', session.user.id);
        await ensureDefaultData(session.user.id);
        await loadAll();
      } else {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // Fetch live US stock prices whenever holdings change (after load)
  useEffect(() => {
    if (stockHoldings.length > 0) {
      const usHoldings = stockHoldings.filter(h => h.market === 'US' && !h.manual_price);
      if (usHoldings.length > 0) fetchStockPrices(usHoldings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockHoldings.length]);

  // ── Sidebar resize drag (global mouse tracking) ──────────────────────────
  useEffect(() => {
    function onMove(e) {
      if (!sidebarDragging.current) return;
      const w = Math.min(400, Math.max(160, sidebarDragW.current + e.clientX - sidebarDragX.current));
      sidebarWidthRef.current = w;
      setSidebarWidth(w);
    }
    function onUp() {
      if (!sidebarDragging.current) return;
      sidebarDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try { localStorage.setItem('em_sidebar_width', String(sidebarWidthRef.current)); } catch {}
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  async function signOut() {
    if (!window.confirm(lang === 'zh' ? '确定要退出登录吗？' : 'Sign out?')) return;
    await supabase.auth.signOut();
    setSession(null); setProfile(null);
    setAccounts([]); setCategories([]); setTransactions([]);
    setShowAdmin(false); setSelectedAccount(null); setTab('overview');
  }

  // First-time users get a default set of categories + enabled currencies
  async function ensureDefaultData(uid) {
    try {
      // Check if this user already has categories
      const { data: existing, error: checkErr } = await supabase
        .from('categories').select('id').eq('user_id', uid).limit(1);
      if (checkErr) { console.error('ensureDefaultData check failed:', checkErr); return; }
      if (existing && existing.length > 0) return; // already seeded

      const defaultCats = [
        // ── 支出 Expense ──
        { name_zh: '餐饮', name_en: 'Food & Drink', type: 'expense' },
        { name_zh: '购物', name_en: 'Shopping', type: 'expense' },
        { name_zh: '交通', name_en: 'Transportation', type: 'expense' },
        { name_zh: '旅行', name_en: 'Travel', type: 'expense' },
        { name_zh: '住房水电', name_en: 'Rent & Utilities', type: 'expense' },
        { name_zh: '娱乐', name_en: 'Entertainment', type: 'expense' },
        { name_zh: '医疗', name_en: 'Medical', type: 'expense' },
        { name_zh: '个人护理', name_en: 'Personal Care', type: 'expense' },
        { name_zh: '生活服务', name_en: 'Services', type: 'expense' },
        { name_zh: '家居', name_en: 'Home Improvement', type: 'expense' },
        { name_zh: '银行费用', name_en: 'Bank Fees', type: 'expense' },
        { name_zh: '贷款还款', name_en: 'Loan Payment', type: 'expense' },
        { name_zh: '政府/公益', name_en: 'Government & Non-Profit', type: 'expense' },
        // ── 收入 Income ──
        { name_zh: '薪资', name_en: 'Salary', type: 'income' },
        { name_zh: '投资收益', name_en: 'Investment Income', type: 'income' },
        { name_zh: '其它收入', name_en: 'Other Income', type: 'income' },
        // ── 通用 Both ──
        { name_zh: '转账', name_en: 'Transfer', type: 'both' },
        { name_zh: '其他', name_en: 'Other', type: 'both' },
        { name_zh: '初始余额', name_en: 'Opening Balance', type: 'both' },
      ].map(c => ({ ...c, color: '#F1EFE8', user_id: uid }));
      const { error: insErr } = await supabase.from('categories').insert(defaultCats);
      if (insErr) console.error('ensureDefaultData insert failed:', insErr);

      // NOTE: We intentionally do NOT set a default currency or enable currencies here.
      // New users are shown a first-time setup wizard to choose their currencies.
    } catch (e) {
      console.error('ensureDefaultData error:', e);
    }
  }

  // ── Load all data ────────────────────────────────────────────────────────
  // Supabase 单次请求默认最多返回 1000 行。交易数一旦超过 1000，
  // 按 date desc 排序会把【最早的交易(含"开户初始余额")】截掉，
  // 而 v1.9.1 起余额是由交易实时算出的 → 余额会静默算错。
  // 这里分页把所有行取全，不依赖后台 Max rows 设置。
  async function fetchAllRows(buildQuery, pageSize = 1000) {
    let from = 0;
    let all = [];
    // 安全上限：最多 100 页(10万行)，防极端情况下死循环
    for (let page = 0; page < 100; page++) {
      const { data, error } = await buildQuery().range(from, from + pageSize - 1);
      if (error) return { data: all, error };
      const batch = data || [];
      all = all.concat(batch);
      if (batch.length < pageSize) break; // 拿完了
      from += pageSize;
    }
    return { data: all, error: null };
  }

  async function loadAll() {
    setLoading(true);
    try {
      setServerDown(false);
    const [accts, cats, txns, curs, sett, rates, userCurs, holdingsRes, budgetsRes, recurRes] = await Promise.all([
      supabase.from('accounts').select('*').order('created_at'),
      supabase.from('categories').select('*').order('created_at'),
      // 分页拉全部交易（突破单次 1000 行上限，保证余额算得对）
      fetchAllRows(() => supabase.from('transactions').select('*').order('date', { ascending: false }).order('created_at', { ascending: false })),
      supabase.from('currencies').select('*'),
      supabase.from('settings').select('*'),
      supabase.from('exchange_rates').select('*'),
      supabase.from('user_currencies').select('currency_code'),
      supabase.from('stock_holdings').select('*').order('created_at'),
      supabase.from('budgets').select('*').order('created_at'),
      supabase.from('recurring_transactions').select('*').order('created_at'),
    ]);

    const holdings = holdingsRes.data || [];
    setStockHoldings(holdings);
    setBudgets(budgetsRes.data || []);
    setRecurring(recurRes.data || []);

    const allCurrencies = curs.data || [];
    const enabledCodes = (userCurs.data || []).map(r => r.currency_code);
    // If the user has enabled specific currencies, only show those; otherwise show all
    const currList = enabledCodes.length > 0
      ? allCurrencies.filter(c => enabledCodes.includes(c.code))
      : allCurrencies;

    const settList = sett.data || [];
    setAccounts(accts.data || []);
    setCategories(cats.data || []);
    setTransactions(txns.data || []);
    setCurrencies(currList);
    setEnabledCurrencyCodes(enabledCodes);

    const currSetting = settList.find(s => s.key === 'currency');
    let defCurrCode = 'CNY';
    if (currSetting) {
      const found = currList.find(c => c.code === currSetting.value);
      if (found) { setDefaultCurrency(found); defCurrCode = found.code; }
      setNeedsSetup(false);
    } else {
      // No currency setting yet → this is a first-time user, show the setup wizard
      setNeedsSetup(true);
    }

    const modeSetting = settList.find(s => s.key === 'exchange_rate_mode');
    if (modeSetting) setExchangeRateMode(modeSetting.value);

    const themeSetting = settList.find(s => s.key === 'theme');
    if (themeSetting && (themeSetting.value === 'light' || themeSetting.value === 'dark')) {
      setTheme(themeSetting.value);
      applyTheme(themeSetting.value);
      try { localStorage.setItem('em_theme', themeSetting.value); } catch {}
    }

    const rateMap = {};
    for (const r of (rates.data || [])) rateMap[r.from_currency] = r.rate;
    setExchangeRates(rateMap);
    setManualRates(rateMap);

    // Load crypto holdings + connected banks (Plaid items) —— 并行，减少网络往返
    const [cryptoHoldRes, plaidRes] = await Promise.all([
      supabase.from('crypto_holdings').select('*').order('created_at'),
      supabase.from('plaid_items').select('id, account_id, item_id, institution_name'),
    ]);
    const cryptoHolds = cryptoHoldRes.data || [];
    setCryptoHoldings(cryptoHolds);
    setPlaidItems(plaidRes.data || []);

    // Fetch crypto prices: legacy single-coin accounts + new multi-coin holdings
    const cryptoAccts = (accts.data || []).filter(a => a.account_type === 'crypto' && a.crypto_symbol);
    const allCoinIds = [...new Set([
      ...cryptoAccts.map(a => a.crypto_symbol),
      ...cryptoHolds.map(h => h.coin_id),
    ])].filter(Boolean);
    if (allCoinIds.length > 0) {
      const symbols = allCoinIds.join(',');
      const vsC = defCurrCode.toLowerCase();
      try {
        const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${symbols}&vs_currencies=${vsC}`);
        const data = await res.json();
        const prices = {};
        for (const [id, val] of Object.entries(data)) {
          prices[id] = val[vsC];
        }
        setCryptoPrices(prices);
        // Update legacy single-coin account balances
        for (const acct of cryptoAccts) {
          const price = prices[acct.crypto_symbol];
          if (price && acct.crypto_amount) {
            const newBalance = parseFloat(acct.crypto_amount) * price;
            await supabase.from('accounts').update({ balance: newBalance }).eq('id', acct.id);
          }
        }
        // Update multi-coin wallet balances = Σ(amount × price)
        const walletIds = [...new Set(cryptoHolds.map(h => h.account_id))];
        for (const wid of walletIds) {
          const walletHolds = cryptoHolds.filter(h => h.account_id === wid);
          let total = 0;
          for (const h of walletHolds) {
            const p = prices[h.coin_id] || 0;
            total += parseFloat(h.amount || 0) * p;
          }
          await supabase.from('accounts').update({ balance: total }).eq('id', wid);
        }
      } catch (e) { console.error('Crypto price fetch error:', e); }
    }

    // Process due recurring transactions (only once per app session to avoid loops)
    if (!recurringProcessedRef.current && (recurRes.data || []).length > 0) {
      recurringProcessedRef.current = true;
      const gen = await processRecurring(recurRes.data, accts.data || []);
      if (gen > 0) {
        // Reload to show the newly generated transactions
        const [{ data: newTxns }, { data: newAccts }] = await Promise.all([
          fetchAllRows(() => supabase.from('transactions').select('*').order('date', { ascending: false }).order('created_at', { ascending: false })),
          supabase.from('accounts').select('*').order('created_at'),
        ]);
        setTransactions(newTxns || []);
        setAccounts(newAccts || []);
        setRecurringMsg((lang === 'zh' ? `已自动记录 ${gen} 笔定期交易，请在交易记录中确认` : `Auto-recorded ${gen} scheduled transaction(s) — please confirm`));
        setTimeout(() => setRecurringMsg(''), 6000);
      }
    }

    } catch (e) {
      console.error('loadAll error:', e);
      setServerDown(true);
    } finally {
      setLoading(false);
    }
  }

  // ── Exchange Rates ───────────────────────────────────────────────────────
  async function fetchAutoRates(baseCurrency) {
    setRatesLoading(true);
    try {
      const res = await fetch(`https://v6.exchangerate-api.com/v6/${EXCHANGE_API_KEY}/latest/${baseCurrency}`);
      const data = await res.json();
      if (data.result === 'success') {
        setExchangeRates(data.conversion_rates);
        // Replace this user's rates: delete then insert (avoids constraint issues)
        await supabase.from('exchange_rates').delete().eq('user_id', userId);
        const rows = Object.entries(data.conversion_rates).map(([from, rate]) => ({
          from_currency: from, to_currency: baseCurrency, rate, user_id: userId,
        }));
        // Insert in one batch
        await supabase.from('exchange_rates').insert(rows);
        alert(t.rateUpdated);
      }
    } catch (e) { console.error(e); }
    setRatesLoading(false);
  }

  async function saveManualRate(fromCurrency, rate) {
    const newRates = { ...manualRates, [fromCurrency]: parseFloat(rate) };
    setManualRates(newRates);
    setExchangeRates(newRates);
    // Remove any existing row for this currency pair (this user), then insert
    await supabase.from('exchange_rates').delete()
      .eq('user_id', userId).eq('from_currency', fromCurrency).eq('to_currency', defaultCurrency.code);
    await supabase.from('exchange_rates').insert({
      from_currency: fromCurrency, to_currency: defaultCurrency.code,
      rate: parseFloat(rate), user_id: userId,
    });
  }

  async function saveExchangeRateMode(mode) {
    setExchangeRateMode(mode);
    // Upsert this user's exchange_rate_mode setting
    await supabase.from('settings').delete().eq('user_id', userId).eq('key', 'exchange_rate_mode');
    await supabase.from('settings').insert({ key: 'exchange_rate_mode', value: mode, user_id: userId });
    if (mode === 'auto') fetchAutoRates(defaultCurrency.code);
  }

  function convertToDefault(amount, fromCurrencyCode) {
    if (fromCurrencyCode === defaultCurrency.code) return parseFloat(amount);
    const rate = exchangeRates[fromCurrencyCode];
    if (!rate) return parseFloat(amount);
    return exchangeRateMode === 'auto'
      ? parseFloat(amount) / rate
      : parseFloat(amount) * rate;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────
  function getCurrencyForAccount(acct) {
    if (!acct) return defaultCurrency;
    return currencies.find(c => c.code === acct.currency_code) || defaultCurrency;
  }

  function getCatName(catId) {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return '-';
    const name = lang === 'zh' ? cat.name_zh : cat.name_en;
    // If it's a subcategory, show "Parent - Child"
    if (cat.parent_id) {
      const parent = categories.find(c => c.id === cat.parent_id);
      if (parent) {
        const parentName = lang === 'zh' ? parent.name_zh : parent.name_en;
        return `${parentName} - ${name}`;
      }
    }
    return name;
  }

  // Build an ordered list of {cat, label} for dropdowns: each parent followed by its children,
  // children shown as "Parent - Child". Optionally filter by transaction type.
  function getCategoryOptions(filterType) {
    const matchType = c => !filterType || c.type === filterType || c.type === 'both';
    const parents = categories.filter(c => !c.parent_id);
    const opts = [];
    for (const p of parents) {
      const pName = lang === 'zh' ? p.name_zh : p.name_en;
      if (matchType(p)) opts.push({ id: p.id, label: pName });
      // Children inherit parent's type, so filter by parent's type
      const children = categories.filter(c => c.parent_id === p.id);
      for (const ch of children) {
        if (matchType(p)) {
          const chName = lang === 'zh' ? ch.name_zh : ch.name_en;
          opts.push({ id: ch.id, label: `${pName} - ${chName}` });
        }
      }
    }
    return opts;
  }

  function getAcctName(acctId) {
    const acct = accounts.find(a => a.id === acctId);
    return acct ? acct.name : '-';
  }

  // Buying/selling stocks or crypto is an asset conversion, NOT real income/expense.
  // Exclude these transactions from all financial stats (monthly totals, charts, budgets, reports).
  function isInvestmentTrade(tx) {
    return !!(tx.stock_action || tx.crypto_action);
  }

  // Group accounts by type, each group sorted A-Z by name.
  // Returns ordered array of { type, label, accounts: [...] }.
  // 渲染"按类型分组、每组可折叠"的账户列表（桌面侧栏 + 手机总览共用）
  function renderAccountGroups() {
    const groupIcon = (type) => ({
      checking: '🏦', savings: '💰', cash: '💵', credit: '💳', crypto: '🪙', stock: '📈',
    }[type] || '📁');
    return groupedAccounts().map(group => {
      const isCollapsed = !!collapsedGroups[group.type];
      return (
        <div key={group.type} style={{ margin: '2px 10px' }}>
          {/* 组标题：点击折叠/展开 */}
          <div onClick={() => toggleGroup(group.type)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 12px', borderRadius: 9, cursor: 'pointer', userSelect: 'none',
              background: C.bg,
            }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: C.inkSoft, letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <span>{groupIcon(group.type)}</span>{group.label}
              <span style={{ fontSize: 10, color: C.inkFaint, fontWeight: 500 }}>({group.accounts.length})</span>
            </span>
            <span style={{ fontSize: 11, color: C.inkFaint, transition: 'transform 0.2s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none' }}>▼</span>
          </div>
          {/* 组内账户 */}
          {!isCollapsed && group.accounts.map(a => {
            const aCurr = getCurrencyForAccount(a);
            const isSelected = selectedAccount?.id === a.id;
            return (
              <div key={a.id} className="em-acct" onClick={() => { setSelectedAccount(a); setTab('account'); setShowTopMenu(false); }}
                style={{
                  margin: '2px 2px', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 12.5, color: C.inkSoft, cursor: 'pointer', borderRadius: 9,
                  background: isSelected ? C.gradSoft : 'transparent',
                  fontWeight: isSelected ? 600 : 500,
                }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: a.color, flexShrink: 0, boxShadow: `0 0 0 3px ${a.color}22` }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                </span>
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {(() => {
                    if (a.account_type === 'credit') {
                      const debt = parseFloat(a.credit_limit || 0) - acctBalance(a);
                      return <span style={{ color: debt > 0 ? C.expense : C.inkSoft }}>
                        {debt > 0 ? '-' : ''}{aCurr.symbol}{Math.abs(debt).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>;
                    }
                    return `${aCurr.symbol}${acctBalance(a).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
                  })()}
                </span>
              </div>
            );
          })}
        </div>
      );
    });
  }

  function groupedAccounts() {
    const order = ['checking', 'savings', 'cash', 'credit', 'crypto'];
    const labelOf = (type) => t[type] || type;
    const groups = {};
    accounts.forEach(a => {
      const type = a.account_type || 'checking';
      (groups[type] = groups[type] || []).push(a);
    });
    const result = [];
    // Known types first in defined order
    order.forEach(type => {
      if (groups[type]) {
        result.push({
          type,
          label: labelOf(type),
          accounts: groups[type].slice().sort((x, y) => x.name.localeCompare(y.name)),
        });
        delete groups[type];
      }
    });
    // Any other/unknown types after
    Object.keys(groups).forEach(type => {
      result.push({
        type,
        label: labelOf(type),
        accounts: groups[type].slice().sort((x, y) => x.name.localeCompare(y.name)),
      });
    });
    return result;
  }

  // Pick an emoji for a category by matching keywords in its name (zh + en).
  function catEmoji(cat) {
    if (!cat) return '🏷️';
    const txt = `${cat.name_zh || ''} ${cat.name_en || ''}`.toLowerCase();
    const map = [
      [['早餐', 'breakfast'], '🥐'],
      [['咖啡', 'coffee'], '☕'],
      [['餐', '吃', '饭', 'food', 'dining', 'eat', 'restaurant', 'meal'], '🍔'],
      [['交通', '车', 'transport', 'taxi', 'bus', 'car', 'fuel', '油'], '🚗'],
      [['购物', 'shop', 'shopping', 'buy'], '🛒'],
      [['娱乐', 'entertain', 'game', 'fun', 'movie', '电影'], '🎮'],
      [['医', '药', 'medical', 'health', 'doctor', 'hospital'], '💊'],
      [['房', '租', 'rent', 'house', 'home', 'mortgage'], '🏠'],
      [['工资', '薪', 'salary', 'wage', 'pay'], '💰'],
      [['投资', 'invest', 'stock', 'dividend', '收益'], '📈'],
      [['旅', 'travel', 'trip', 'hotel', 'flight', '机票'], '✈️'],
      [['教育', '学', 'edu', 'school', 'study', 'book', '书'], '📚'],
      [['礼', 'gift', 'present'], '🎁'],
      [['宠物', 'pet', 'dog', 'cat'], '🐾'],
      [['运动', '健身', 'sport', 'gym', 'fitness'], '🏃'],
      [['通讯', '话费', '网', 'phone', 'internet', 'bill', '账单', 'utility', '水电'], '📱'],
      [['服', '衣', 'cloth', 'apparel', 'fashion'], '👕'],
      [['美', '妆', 'beauty', 'cosmetic'], '💄'],
      [['初始', 'opening', 'balance'], '🏦'],
      [['收入', 'income'], '💵'],
      [['其他', '其它', 'other', 'misc'], '📦'],
    ];
    for (const [keys, emoji] of map) {
      if (keys.some(k => txt.includes(k))) return emoji;
    }
    return '🏷️';
  }

  // Complete the first-time currency setup wizard
  async function completeSetup() {
    if (!setupDefault) {
      alert(lang === 'zh' ? '请选择默认币种' : 'Please choose a default currency');
      return;
    }
    // Make sure the default is included in the chosen list
    const chosen = setupChosen.includes(setupDefault) ? setupChosen : [...setupChosen, setupDefault];
    if (chosen.length === 0) {
      alert(lang === 'zh' ? '请至少选择一个币种' : 'Please choose at least one currency');
      return;
    }
    setSetupSaving(true);
    try {
      // 1. Make sure every chosen currency exists in the `currencies` table
      const { data: existing } = await supabase.from('currencies').select('code');
      const existingCodes = (existing || []).map(c => c.code);
      const toCreate = chosen
        .filter(code => !existingCodes.includes(code))
        .map(code => {
          const w = WORLD_CURRENCIES.find(x => x.code === code);
          return { code: w.code, symbol: w.symbol, name_zh: w.name_zh, name_en: w.name_en };
        });
      if (toCreate.length > 0) {
        await supabase.from('currencies').insert(toCreate);
      }

      // 2. Enable the chosen currencies for this user (clear then insert)
      await supabase.from('user_currencies').delete().eq('user_id', userId);
      await supabase.from('user_currencies').insert(
        chosen.map(code => ({ user_id: userId, currency_code: code }))
      );

      // 3. Save the default currency setting
      await supabase.from('settings').delete().eq('user_id', userId).eq('key', 'currency');
      const { error: insErr } = await supabase.from('settings')
        .insert({ key: 'currency', value: setupDefault, user_id: userId });
      if (insErr) throw insErr;

      // 4. Done — close wizard and reload everything
      setNeedsSetup(false);
      await loadAll();
      // Fetch fresh auto rates for the new default currency
      await fetchAutoRates(setupDefault);
    } catch (e) {
      console.error('completeSetup error:', e);
      alert((lang === 'zh' ? '设置失败：' : 'Setup failed: ') + (e.message || e));
    }
    setSetupSaving(false);
  }

  // ── Stock functions ──────────────────────────────────────────────────────
  // Fetch live prices for US stocks via the stock-price Edge Function
  async function fetchStockPrices(holdings) {
    const usSymbols = [...new Set(
      holdings.filter(h => h.market === 'US' && !h.manual_price && h.symbol).map(h => h.symbol)
    )];
    if (usSymbols.length === 0) return;
    try {
      const { data, error } = await supabase.functions.invoke('stock-price', {
        body: { symbols: usSymbols },
      });
      if (error || !data?.success) return;
      const prices = {};
      for (const [sym, info] of Object.entries(data.prices || {})) {
        if (info && typeof info.current === 'number') prices[sym] = info;
      }
      setStockPrices(prev => ({ ...prev, ...prices }));
      // Persist current_price + recompute the account balance
      for (const h of holdings) {
        const p = prices[h.symbol];
        if (p && h.market === 'US' && !h.manual_price) {
          await supabase.from('stock_holdings').update({ current_price: p.current }).eq('id', h.id);
        }
      }
      // Recompute balances for affected stock accounts
      const affectedAccts = [...new Set(holdings.map(h => h.account_id))];
      for (const acctId of affectedAccts) {
        await recomputeStockAccountBalance(acctId, holdings, prices);
      }
    } catch (e) { console.error('fetchStockPrices error:', e); }
  }

  // Recompute a stock account's balance = cash + sum of (shares * current price)
  async function recomputeStockAccountBalance(acctId, holdings, prices) {
    const acctHoldings = holdings.filter(h => h.account_id === acctId);
    let stockValue = 0;
    for (const h of acctHoldings) {
      const price = (prices && prices[h.symbol]?.current) || h.current_price || h.avg_cost || 0;
      stockValue += parseFloat(h.shares || 0) * parseFloat(price);
    }
    // Read current cash balance fresh from DB for this account
    const { data: acctRow } = await supabase.from('accounts').select('cash_balance').eq('id', acctId).single();
    const cash = parseFloat(acctRow?.cash_balance || 0);
    await supabase.from('accounts').update({ balance: cash + stockValue }).eq('id', acctId);
  }

  // Deposit or withdraw cash in a stock account
  async function adjustStockCash(account, delta, isDeposit) {
    const newCash = parseFloat(account.cash_balance || 0) + delta;
    await supabase.from('accounts').update({ cash_balance: newCash }).eq('id', account.id);
    // Record a transaction for the deposit/withdraw
    await supabase.from('transactions').insert({
      date: new Date().toISOString().split('T')[0],
      description: isDeposit ? (lang === 'zh' ? '存入现金' : 'Deposit') : (lang === 'zh' ? '取出现金' : 'Withdraw'),
      account_id: account.id, type: isDeposit ? 'income' : 'expense', amount: Math.abs(delta),
      user_id: userId,
    });
    await loadAll();
  }

  // Add a new stock holding (first buy)
  async function addStockHolding(accountId) {
    const { symbol, company, market, shares, price } = stockForm;
    if (!symbol || !shares || !price) {
      alert(lang === 'zh' ? '请填写代码、股数和价格' : 'Please fill symbol, shares and price');
      return;
    }
    setStockSaving(true);
    try {
      const sym = market === 'US' ? symbol.trim().toUpperCase() : symbol.trim();
      const sh = parseFloat(shares);
      const pr = parseFloat(price);
      // Create the holding
      const { data: created, error } = await supabase.from('stock_holdings').insert({
        user_id: userId, account_id: accountId, symbol: sym, company_name: company || null,
        market, shares: sh, avg_cost: pr, current_price: pr, manual_price: market !== 'US',
      }).select().single();
      if (error) throw error;
      // Record the buy transaction
      await supabase.from('transactions').insert({
        date: new Date().toISOString().split('T')[0],
        description: `${lang === 'zh' ? '买入' : 'Buy'} ${sym}`,
        account_id: accountId, type: 'expense', amount: sh * pr,
        holding_id: created.id, stock_shares: sh, stock_price: pr, stock_action: 'buy',
        user_id: userId,
      });
      // Deduct cash from the account
      const acct = accounts.find(a => a.id === accountId);
      const newCash = parseFloat(acct?.cash_balance || 0) - sh * pr;
      await supabase.from('accounts').update({ cash_balance: newCash }).eq('id', accountId);
      setShowStockModal(false);
      setStockForm({ symbol: '', company: '', market: 'US', shares: '', price: '' });
      await loadAll();
      // Fetch fresh price for US stock
      if (market === 'US') await fetchStockPrices([created]);
    } catch (e) {
      console.error('addStockHolding error:', e);
      alert((lang === 'zh' ? '添加失败：' : 'Failed: ') + (e.message || e));
    }
    setStockSaving(false);
  }

  // Buy more of an existing holding (updates avg cost)
  async function buyMoreStock(holding) {
    const { shares, price } = stockForm;
    if (!shares || !price) { alert(lang === 'zh' ? '请填写股数和价格' : 'Fill shares and price'); return; }
    setStockSaving(true);
    try {
      const sh = parseFloat(shares), pr = parseFloat(price);
      const oldShares = parseFloat(holding.shares || 0);
      const oldCost = parseFloat(holding.avg_cost || 0);
      const newShares = oldShares + sh;
      // New average cost = (oldShares*oldCost + sh*pr) / newShares
      const newAvg = newShares > 0 ? (oldShares * oldCost + sh * pr) / newShares : pr;
      await supabase.from('stock_holdings').update({ shares: newShares, avg_cost: newAvg }).eq('id', holding.id);
      await supabase.from('transactions').insert({
        date: new Date().toISOString().split('T')[0],
        description: `${lang === 'zh' ? '买入' : 'Buy'} ${holding.symbol}`,
        account_id: holding.account_id, type: 'expense', amount: sh * pr,
        holding_id: holding.id, stock_shares: sh, stock_price: pr, stock_action: 'buy',
        user_id: userId,
      });
      // Deduct cash
      const acctB = accounts.find(a => a.id === holding.account_id);
      await supabase.from('accounts').update({ cash_balance: parseFloat(acctB?.cash_balance || 0) - sh * pr }).eq('id', holding.account_id);
      setShowStockModal(false);
      setStockForm({ symbol: '', company: '', market: 'US', shares: '', price: '' });
      await loadAll();
    } catch (e) { alert((lang === 'zh' ? '失败：' : 'Failed: ') + (e.message || e)); }
    setStockSaving(false);
  }

  // Sell part/all of a holding (records realized P&L)
  async function sellStock(holding) {
    const { shares, price } = stockForm;
    if (!shares || !price) { alert(lang === 'zh' ? '请填写股数和价格' : 'Fill shares and price'); return; }
    setStockSaving(true);
    try {
      const sh = parseFloat(shares), pr = parseFloat(price);
      const oldShares = parseFloat(holding.shares || 0);
      if (sh > oldShares) { alert(lang === 'zh' ? '卖出股数超过持仓' : 'Selling more than held'); setStockSaving(false); return; }
      const avgCost = parseFloat(holding.avg_cost || 0);
      const realizedPnl = (pr - avgCost) * sh; // profit/loss on the sold shares
      const newShares = oldShares - sh;
      if (newShares <= 0) {
        // Sold everything → remove the holding
        await supabase.from('stock_holdings').delete().eq('id', holding.id);
      } else {
        await supabase.from('stock_holdings').update({ shares: newShares }).eq('id', holding.id);
      }
      await supabase.from('transactions').insert({
        date: new Date().toISOString().split('T')[0],
        description: `${lang === 'zh' ? '卖出' : 'Sell'} ${holding.symbol}`,
        account_id: holding.account_id, type: 'income', amount: sh * pr,
        holding_id: newShares > 0 ? holding.id : null, stock_shares: sh, stock_price: pr,
        stock_action: 'sell', realized_pnl: realizedPnl,
        user_id: userId,
      });
      // Add proceeds to cash
      const acctS = accounts.find(a => a.id === holding.account_id);
      await supabase.from('accounts').update({ cash_balance: parseFloat(acctS?.cash_balance || 0) + sh * pr }).eq('id', holding.account_id);
      setShowStockModal(false);
      setStockForm({ symbol: '', company: '', market: 'US', shares: '', price: '' });
      await loadAll();
    } catch (e) { alert((lang === 'zh' ? '失败：' : 'Failed: ') + (e.message || e)); }
    setStockSaving(false);
  }

  // Update a manual (non-US) stock's price
  async function updateManualStockPrice(holding, newPrice) {
    const pr = parseFloat(newPrice);
    if (isNaN(pr)) return;
    await supabase.from('stock_holdings').update({ current_price: pr }).eq('id', holding.id);
    await loadAll();
  }

  async function deleteStockHolding(holding) {
    if (!window.confirm(lang === 'zh' ? `确定删除持仓 ${holding.symbol}？相关买卖记录会保留。` : `Delete holding ${holding.symbol}?`)) return;
    await supabase.from('stock_holdings').delete().eq('id', holding.id);
    await loadAll();
  }

  // ── Crypto holding functions (multi-coin wallet) ─────────────────────────
  // Recompute a crypto wallet's balance = Σ(amount × current price)
  // If `holdings` is omitted, fetch the latest holdings from the database.
  async function recomputeCryptoWalletBalance(acctId, holdings) {
    let source = holdings;
    if (!source) {
      const { data } = await supabase.from('crypto_holdings').select('*').eq('account_id', acctId);
      source = data || [];
    }
    const walletHolds = source.filter(h => h.account_id === acctId);
    let total = 0;
    for (const h of walletHolds) {
      const p = cryptoPrices[h.coin_id] || 0;
      total += parseFloat(h.amount || 0) * p;
    }
    await supabase.from('accounts').update({ balance: total }).eq('id', acctId);
  }

  // Search CoinGecko for the holding modal
  async function searchCryptoForHolding(query) {
    if (!query || query.length < 2) { setHoldingSearchResults([]); return; }
    try {
      const res = await fetch(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(query)}`);
      const data = await res.json();
      setHoldingSearchResults((data.coins || []).slice(0, 6));
    } catch { setHoldingSearchResults([]); }
  }

  // Add a new coin holding (first buy)
  async function addCryptoHolding(accountId) {
    const { coin_id, ticker, coin_name, amount, price } = cryptoForm;
    if (!coin_id) { alert(lang === 'zh' ? '请先搜索并选择币种' : 'Search and pick a coin'); return; }
    if (!amount || !price) { alert(lang === 'zh' ? '请填写数量和买入价' : 'Fill amount and price'); return; }
    setCryptoSaving(true);
    try {
      const amt = parseFloat(amount), pr = parseFloat(price);
      // If this wallet already holds the coin, treat as buy-more instead
      const existing = cryptoHoldings.find(h => h.account_id === accountId && h.coin_id === coin_id);
      if (existing) {
        const oldAmt = parseFloat(existing.amount || 0), oldCost = parseFloat(existing.avg_cost || 0);
        const newAmt = oldAmt + amt;
        const newAvg = newAmt > 0 ? (oldAmt * oldCost + amt * pr) / newAmt : pr;
        await supabase.from('crypto_holdings').update({ amount: newAmt, avg_cost: newAvg }).eq('id', existing.id);
        await supabase.from('transactions').insert({
          date: new Date().toISOString().split('T')[0],
          description: `${lang === 'zh' ? '买入' : 'Buy'} ${existing.ticker || coin_id}`,
          account_id: accountId, type: 'expense', amount: amt * pr,
          crypto_holding_id: existing.id, crypto_amount: amt, crypto_price: pr, crypto_action: 'buy',
          user_id: userId,
        });
      } else {
        const { data: created, error } = await supabase.from('crypto_holdings').insert({
          user_id: userId, account_id: accountId, coin_id, ticker: (ticker || '').toUpperCase(),
          coin_name: coin_name || null, amount: amt, avg_cost: pr,
        }).select().single();
        if (error) throw error;
        await supabase.from('transactions').insert({
          date: new Date().toISOString().split('T')[0],
          description: `${lang === 'zh' ? '买入' : 'Buy'} ${(ticker || coin_id).toUpperCase()}`,
          account_id: accountId, type: 'expense', amount: amt * pr,
          crypto_holding_id: created.id, crypto_amount: amt, crypto_price: pr, crypto_action: 'buy',
          user_id: userId,
        });
      }
      setShowCryptoModal(false);
      setCryptoForm({ coin_id: '', ticker: '', coin_name: '', amount: '', price: '', total: '' });
      setHoldingSearchResults([]);
      await loadAll();
    } catch (e) {
      console.error('addCryptoHolding error:', e);
      alert((lang === 'zh' ? '添加失败：' : 'Failed: ') + (e.message || e));
    }
    setCryptoSaving(false);
  }

  // Buy more of an existing coin (updates avg cost)
  async function buyMoreCrypto(holding) {
    const { amount, price } = cryptoForm;
    if (!amount || !price) { alert(lang === 'zh' ? '请填写数量和价格' : 'Fill amount and price'); return; }
    setCryptoSaving(true);
    try {
      const amt = parseFloat(amount), pr = parseFloat(price);
      const oldAmt = parseFloat(holding.amount || 0), oldCost = parseFloat(holding.avg_cost || 0);
      const newAmt = oldAmt + amt;
      const newAvg = newAmt > 0 ? (oldAmt * oldCost + amt * pr) / newAmt : pr;
      await supabase.from('crypto_holdings').update({ amount: newAmt, avg_cost: newAvg }).eq('id', holding.id);
      await supabase.from('transactions').insert({
        date: new Date().toISOString().split('T')[0],
        description: `${lang === 'zh' ? '买入' : 'Buy'} ${holding.ticker || holding.coin_id}`,
        account_id: holding.account_id, type: 'expense', amount: amt * pr,
        crypto_holding_id: holding.id, crypto_amount: amt, crypto_price: pr, crypto_action: 'buy',
        user_id: userId,
      });
      setShowCryptoModal(false);
      setCryptoForm({ coin_id: '', ticker: '', coin_name: '', amount: '', price: '', total: '' });
      await loadAll();
    } catch (e) { alert((lang === 'zh' ? '失败：' : 'Failed: ') + (e.message || e)); }
    setCryptoSaving(false);
  }

  // Sell part/all of a coin (records realized P&L)
  async function sellCrypto(holding) {
    const { amount, price } = cryptoForm;
    if (!amount || !price) { alert(lang === 'zh' ? '请填写数量和价格' : 'Fill amount and price'); return; }
    setCryptoSaving(true);
    try {
      const amt = parseFloat(amount), pr = parseFloat(price);
      const oldAmt = parseFloat(holding.amount || 0);
      if (amt > oldAmt) { alert(lang === 'zh' ? '卖出数量超过持有量' : 'Selling more than held'); setCryptoSaving(false); return; }
      const avgCost = parseFloat(holding.avg_cost || 0);
      const realizedPnl = (pr - avgCost) * amt;
      const newAmt = oldAmt - amt;
      if (newAmt <= 0) {
        await supabase.from('crypto_holdings').delete().eq('id', holding.id);
      } else {
        await supabase.from('crypto_holdings').update({ amount: newAmt }).eq('id', holding.id);
      }
      await supabase.from('transactions').insert({
        date: new Date().toISOString().split('T')[0],
        description: `${lang === 'zh' ? '卖出' : 'Sell'} ${holding.ticker || holding.coin_id}`,
        account_id: holding.account_id, type: 'income', amount: amt * pr,
        crypto_holding_id: newAmt > 0 ? holding.id : null, crypto_amount: amt, crypto_price: pr,
        crypto_action: 'sell', realized_pnl: realizedPnl,
        user_id: userId,
      });
      setShowCryptoModal(false);
      setCryptoForm({ coin_id: '', ticker: '', coin_name: '', amount: '', price: '', total: '' });
      await loadAll();
    } catch (e) { alert((lang === 'zh' ? '失败：' : 'Failed: ') + (e.message || e)); }
    setCryptoSaving(false);
  }

  async function deleteCryptoHolding(holding) {
    if (!window.confirm(lang === 'zh' ? `确定删除持仓 ${holding.ticker || holding.coin_id}？相关买卖记录会保留。` : `Delete holding ${holding.ticker}?`)) return;
    await supabase.from('crypto_holdings').delete().eq('id', holding.id);
    await loadAll();
  }

  // ── Budget functions ─────────────────────────────────────────────────────
  async function saveBudget() {
    const { name, category_id, amount, period } = budgetForm;
    if (!amount || parseFloat(amount) <= 0) {
      alert(lang === 'zh' ? '请填写预算金额' : 'Please enter an amount');
      return;
    }
    const payload = {
      name: name || null,
      category_id: category_id || null,
      amount: parseFloat(amount),
      period,
      user_id: userId,
    };
    if (editingBudget) {
      await supabase.from('budgets').update(payload).eq('id', editingBudget.id);
    } else {
      await supabase.from('budgets').insert(payload);
    }
    setShowBudgetModal(false);
    setBudgetForm({ name: '', category_id: '', amount: '', period: 'month' });
    setEditingBudget(null);
    await loadAll();
  }

  async function deleteBudget(b) {
    if (!window.confirm(lang === 'zh' ? '确定删除这个预算？' : 'Delete this budget?')) return;
    await supabase.from('budgets').delete().eq('id', b.id);
    await loadAll();
  }

  // Compute how much has been spent for a budget in its current period
  function budgetPeriodStart(b) {
    const now = new Date();
    if (b.period === 'week') {
      const day = now.getDay() || 7; // Mon=1..Sun=7
      const start = new Date(now); start.setDate(now.getDate() - day + 1); start.setHours(0, 0, 0, 0);
      return start;
    } else if (b.period === 'year') {
      return new Date(now.getFullYear(), 0, 1);
    }
    return new Date(now.getFullYear(), now.getMonth(), 1); // month
  }

  // Return the expense transactions that count toward a budget (current period)
  function budgetTxns(b) {
    const start = budgetPeriodStart(b);
    return transactions
      .filter(tx => tx.type === 'expense' && !isInvestmentTrade(tx))
      .filter(tx => !b.category_id || tx.category_id === b.category_id)
      .filter(tx => new Date(tx.date) >= start);
  }

  function budgetSpent(b) {
    return budgetTxns(b).reduce((sum, tx) => {
      const acct = accounts.find(a => a.id === tx.account_id);
      return sum + convertToDefault(tx.amount, getCurrencyForAccount(acct).code);
    }, 0);
  }

  // ── Recurring transaction functions ──────────────────────────────────────
  function computeNextDue(periodType, periodDay, periodMonth, from) {
    const base = new Date(from); base.setHours(0, 0, 0, 0);
    const d = new Date(base);
    if (periodType === 'daily') {
      d.setDate(d.getDate() + 1);
    } else if (periodType === 'weekly') {
      const cur = d.getDay();
      let diff = (periodDay - cur + 7) % 7;
      if (diff === 0) diff = 7;
      d.setDate(d.getDate() + diff);
    } else if (periodType === 'yearly') {
      d.setMonth((periodMonth || 1) - 1); d.setDate(periodDay || 1);
      if (d <= base) d.setFullYear(d.getFullYear() + 1);
    } else { // monthly
      d.setMonth(d.getMonth() + 1);
      d.setDate(Math.min(periodDay || 1, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
    }
    return d.toISOString().split('T')[0];
  }

  function firstDue(periodType, periodDay, periodMonth) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (periodType === 'daily') return today.toISOString().split('T')[0];
    if (periodType === 'weekly') {
      const diff = (periodDay - today.getDay() + 7) % 7;
      const d = new Date(today); d.setDate(d.getDate() + diff);
      return d.toISOString().split('T')[0];
    }
    if (periodType === 'yearly') {
      const d = new Date(today.getFullYear(), (periodMonth || 1) - 1, periodDay || 1);
      if (d < today) d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().split('T')[0];
    }
    const dim = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const d = new Date(today.getFullYear(), today.getMonth(), Math.min(periodDay || 1, dim));
    if (d < today) { d.setMonth(d.getMonth() + 1); d.setDate(Math.min(periodDay || 1, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate())); }
    return d.toISOString().split('T')[0];
  }

  async function saveRecurring() {
    const f = recurForm;
    if (!f.amount || parseFloat(f.amount) <= 0) { alert(lang === 'zh' ? '请填写金额' : 'Enter amount'); return; }
    if (!f.account_id) { alert(lang === 'zh' ? '请选择账户' : 'Select account'); return; }
    if (f.type === 'transfer' && !f.transfer_to_account_id) { alert(lang === 'zh' ? '请选择转入账户' : 'Select destination'); return; }
    const payload = {
      type: f.type, amount: parseFloat(f.amount), account_id: f.account_id,
      transfer_to_account_id: f.type === 'transfer' ? f.transfer_to_account_id : null,
      category_id: f.type !== 'transfer' ? (f.category_id || null) : null,
      description: f.description || null, note: f.note || null,
      period_type: f.period_type, period_day: parseInt(f.period_day) || 1,
      period_month: parseInt(f.period_month) || 1, advance_days: parseInt(f.advance_days) || 0,
      user_id: userId,
    };
    if (editingRecurring) {
      await supabase.from('recurring_transactions').update(payload).eq('id', editingRecurring.id);
    } else {
      payload.next_due = firstDue(f.period_type, parseInt(f.period_day) || 1, parseInt(f.period_month) || 1);
      payload.active = true;
      await supabase.from('recurring_transactions').insert(payload);
    }
    setShowRecurringModal(false);
    setEditingRecurring(null);
    await loadAll();
  }

  async function deleteRecurring(r) {
    if (!window.confirm(lang === 'zh' ? '确定删除这个定期交易规则？已生成的交易会保留。' : 'Delete this schedule? Generated transactions stay.')) return;
    await supabase.from('recurring_transactions').delete().eq('id', r.id);
    await loadAll();
  }

  async function toggleRecurringActive(r) {
    await supabase.from('recurring_transactions').update({ active: !r.active }).eq('id', r.id);
    await loadAll();
  }

  async function processRecurring(rules, accts) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let generated = 0;
    for (const r of rules) {
      if (!r.active) continue;
      let dueStr = r.next_due;
      let safety = 0;
      while (dueStr && safety < 60) {
        const due = new Date(dueStr); due.setHours(0, 0, 0, 0);
        const triggerDate = new Date(due); triggerDate.setDate(triggerDate.getDate() - (r.advance_days || 0));
        if (triggerDate > today) break;
        if (r.type === 'transfer') {
          const fromAcct = accts.find(a => a.id === r.account_id);
          const toAcct = accts.find(a => a.id === r.transfer_to_account_id);
          if (fromAcct) await supabase.from('accounts').update({ balance: parseFloat(fromAcct.balance) - parseFloat(r.amount) }).eq('id', fromAcct.id);
          if (toAcct) await supabase.from('accounts').update({ balance: parseFloat(toAcct.balance) + parseFloat(r.amount) }).eq('id', toAcct.id);
          await supabase.from('transactions').insert([
            { date: dueStr, description: r.description || '', note: r.note || '', account_id: r.account_id, type: 'transfer', amount: parseFloat(r.amount), transfer_to_account_id: r.transfer_to_account_id, is_transfer_out: true, pending: true, user_id: userId },
            { date: dueStr, description: r.description || '', note: r.note || '', account_id: r.transfer_to_account_id, type: 'transfer', amount: parseFloat(r.amount), transfer_to_account_id: r.account_id, is_transfer_out: false, pending: true, user_id: userId },
          ]);
        } else {
          const acct = accts.find(a => a.id === r.account_id);
          if (acct) {
            const delta = r.type === 'income' ? parseFloat(r.amount) : -parseFloat(r.amount);
            await supabase.from('accounts').update({ balance: parseFloat(acct.balance) + delta }).eq('id', acct.id);
          }
          await supabase.from('transactions').insert({
            date: dueStr, description: r.description || '', note: r.note || '', category_id: r.category_id || null,
            account_id: r.account_id, type: r.type, amount: parseFloat(r.amount), pending: true, user_id: userId,
          });
        }
        generated++;
        dueStr = computeNextDue(r.period_type, r.period_day, r.period_month, dueStr);
        safety++;
      }
      if (dueStr !== r.next_due) {
        await supabase.from('recurring_transactions').update({ next_due: dueStr }).eq('id', r.id);
      }
    }
    return generated;
  }

  async function confirmPending(tx) {
    await supabase.from('transactions').update({ pending: false }).eq('id', tx.id);
    if (tx.type === 'transfer') {
      await supabase.from('transactions').update({ pending: false })
        .eq('account_id', tx.transfer_to_account_id).eq('type', 'transfer').eq('date', tx.date)
        .eq('amount', tx.amount).eq('pending', true);
    }
    await loadAll();
  }

  async function saveDefaultCurrency(code) {
    const found = currencies.find(c => c.code === code);
    if (!found) return;
    const oldCode = defaultCurrency.code;

    // Convert all budget amounts from the OLD default currency to the NEW one (one-time, using current rate)
    if (oldCode !== code && budgets.length > 0) {
      // rate = how many NEW units per 1 OLD unit
      let rate = null;
      if (exchangeRateMode === 'auto') {
        // exchangeRates[code] = units of `code` per 1 oldCode  → that's exactly NEW per OLD
        rate = exchangeRates[code];
      } else {
        // manual mode stores oldCode->code as multiply factor
        rate = exchangeRates[code];
      }
      if (rate && rate > 0) {
        for (const b of budgets) {
          const newAmount = parseFloat(b.amount) * rate;
          await supabase.from('budgets').update({ amount: newAmount }).eq('id', b.id);
        }
      }
    }

    setDefaultCurrency(found);
    // Delete any existing currency setting rows for this user, then insert fresh
    // (handles duplicates / stale rows that can block a plain update)
    const { error: delErr } = await supabase.from('settings')
      .delete().eq('user_id', userId).eq('key', 'currency');
    const { error: insErr } = await supabase.from('settings')
      .insert({ key: 'currency', value: code, user_id: userId });
    if (delErr || insErr) {
      console.error('saveDefaultCurrency error:', delErr || insErr);
      alert((lang === 'zh' ? '保存默认币种失败：' : 'Failed to save: ') + ((delErr || insErr).message || ''));
      return;
    }
    if (exchangeRateMode === 'auto') await fetchAutoRates(code);
    // Reload so crypto prices & balances re-fetch in the new default currency
    await loadAll();
  }

  // Enable/disable a currency for this user
  async function toggleUserCurrency(code) {
    const isEnabled = enabledCurrencyCodes.includes(code);
    if (isEnabled) {
      // Don't allow disabling the current default currency
      if (code === defaultCurrency.code) {
        alert(lang === 'zh' ? '不能停用当前默认币种' : 'Cannot disable the current default currency');
        return;
      }
      await supabase.from('user_currencies').delete()
        .eq('user_id', userId).eq('currency_code', code);
    } else {
      // Make sure this currency exists in the currencies table first
      const { data: exists } = await supabase.from('currencies').select('code').eq('code', code).limit(1);
      if (!exists || exists.length === 0) {
        const w = WORLD_CURRENCIES.find(x => x.code === code);
        if (w) await supabase.from('currencies').insert({ code: w.code, symbol: w.symbol, name_zh: w.name_zh, name_en: w.name_en });
      }
      await supabase.from('user_currencies').insert({ user_id: userId, currency_code: code });
    }
    loadAll();
  }

  // ── Monthly stats ────────────────────────────────────────────────────────
  const now = new Date();
  const monthTxns = transactions.filter(tx => {
    const d = new Date(tx.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const monthIncome = monthTxns.filter(tx => tx.type === 'income' && !isInvestmentTrade(tx)).reduce((s, tx) => {
    const acct = accounts.find(a => a.id === tx.account_id);
    return s + convertToDefault(tx.amount, getCurrencyForAccount(acct).code);
  }, 0);

  const monthExpense = monthTxns.filter(tx => tx.type === 'expense' && !isInvestmentTrade(tx)).reduce((s, tx) => {
    const acct = accounts.find(a => a.id === tx.account_id);
    return s + convertToDefault(tx.amount, getCurrencyForAccount(acct).code);
  }, 0);

  // ⭐ 余额根治：账户余额一律【实时从交易记录算出】，不再依赖 accounts.balance 字段
  //   （balance 字段容易因竞态/编辑/删除而和交易脱节，导致总资产和实际对不上）
  // 普通账户：余额 = income(+) − expense(−) + 转入腿(+) − 转出腿(−)
  // 信用卡：balance 字段语义=可用额度=额度−欠款。这里返回同口径的"可用额度"，
  //         交易净额(同样是 income−expense±转账)直接加到额度上：available = limit + 交易净额
  const txnNetByAccount = (() => {
    const map = {};
    for (const tx of transactions) {
      const id = tx.account_id;
      if (!id) continue;
      let delta = 0;
      if (tx.type === 'income') delta = parseFloat(tx.amount || 0);
      else if (tx.type === 'expense') delta = -parseFloat(tx.amount || 0);
      else if (tx.type === 'transfer') delta = tx.is_transfer_out ? -parseFloat(tx.amount || 0) : parseFloat(tx.amount || 0);
      map[id] = (map[id] || 0) + delta;
    }
    return map;
  })();
  function acctBalance(a) {
    if (!a) return 0;
    // 加密钱包余额是按持仓市值实时算的，仍用其 balance 字段（由 recomputeCryptoWalletBalance 维护）
    if (a.account_type === 'crypto') return parseFloat(a.balance || 0);
    const net = txnNetByAccount[a.id] || 0;
    if (a.account_type === 'credit') {
      // 可用额度口径：额度 + 交易净额（刷卡expense使净额变负→可用减少；还款income→可用增加）
      return parseFloat(a.credit_limit || 0) + net;
    }
    return net;
  }

  const balanceByCurrency = accounts.reduce((map, a) => {
    const curr = getCurrencyForAccount(a);
    let contribution = acctBalance(a);
    if (a.account_type === 'credit') {
      // 净资产里信用卡算负债：欠款 = 额度 − 可用，贡献 -欠款
      const limit = parseFloat(a.credit_limit || 0);
      const debt = limit - acctBalance(a);
      contribution = -debt;
    }
    map[curr.code] = (map[curr.code] || 0) + contribution;
    return map;
  }, {});

  const totalBalanceInDefault = Object.entries(balanceByCurrency).reduce(
    (sum, [code, bal]) => sum + convertToDefault(bal, code), 0
  );

  // ── 12-month income/expense data for the chart ──────────────────────────
  // chartEndMonth is "YYYY-MM"; build 12 months ending at (and including) it.
  const chartData = (() => {
    const [endY, endM] = chartEndMonth.split('-').map(Number);
    const months = [];
    // Go back 11 months from the anchor to get 12 total
    for (let i = 11; i >= 0; i--) {
      const d = new Date(endY, endM - 1 - i, 1);
      const y = d.getFullYear();
      const m = d.getMonth(); // 0-indexed
      const label = lang === 'zh'
        ? `${y}/${m + 1}`
        : `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m]} ${String(y).slice(2)}`;

      let income = 0, expense = 0;
      transactions.forEach(tx => {
        if (tx.type === 'transfer') return;
        if (isInvestmentTrade(tx)) return; // asset conversion, not real income/expense
        const td = new Date(tx.date);
        if (td.getFullYear() === y && td.getMonth() === m) {
          const acct = accounts.find(a => a.id === tx.account_id);
          const val = convertToDefault(tx.amount, getCurrencyForAccount(acct).code);
          if (tx.type === 'income') income += val;
          else if (tx.type === 'expense') expense += val;
        }
      });
      months.push({ label, income: Math.round(income), expense: Math.round(expense) });
    }
    return months;
  })();

  // ── Add Account ──────────────────────────────────────────────────────────
  async function addAccount() {
    if (!newAcctName) return;
    const currCode = newAcctCurrency || defaultCurrency.code;
    let balance = parseFloat(newAcctBalance || 0);
    let cryptoSymbol = null;
    let cryptoAmount = null;
    let creditLimit = null;
    let openingDebt = 0; // for credit cards: how much was owed at opening

    if (newAcctType === 'crypto') {
      // Multi-coin wallet: created empty; coins are added inside the wallet
      balance = 0;
      cryptoSymbol = null;
      cryptoAmount = null;
    } else if (newAcctType === 'credit') {
      // Credit card: the "initial balance" field is actually the opening DEBT (amount owed).
      // The stored balance represents AVAILABLE CREDIT = limit - debt.
      creditLimit = newCreditLimit ? parseFloat(newCreditLimit) : 0;
      openingDebt = parseFloat(newAcctBalance || 0);
      balance = creditLimit - openingDebt; // available credit
    }

    const { error } = await supabase.from('accounts').insert({
      name: newAcctName,
      balance,
      color: newAcctColor,
      currency_code: currCode,
      account_type: newAcctType,
      crypto_symbol: cryptoSymbol,                                    // CoinGecko ID
      crypto_amount: cryptoAmount,
      crypto_ticker: newCryptoTicker || cryptoSymbol?.toUpperCase(),  // Display ticker
      credit_limit: creditLimit,
      account_number: newAcctNumber || null,
      user_id: userId,
    });

    if (error) {
      console.error('Insert account error:', error);
      alert('新增失败: ' + error.message);
      return;
    }

    // 新增账户后，插入开户交易
    const needOpeningTxn = (newAcctType === 'credit' && openingDebt > 0) || (newAcctType !== 'credit' && balance > 0);
    if (needOpeningTxn) {
      const { data: newAcctData } = await supabase
        .from('accounts')
        .select('id')
        .eq('name', newAcctName)
        .order('created_at', { ascending: false })
        .limit(1);
      const newAcctId = newAcctData?.[0]?.id;

      if (newAcctId) {
        const initCat = categories.find(c =>
          c.name_zh === '初始余额' || c.name_en === 'Opening Balance'
        );
        if (newAcctType === 'credit') {
          // Opening debt → a "spend" (expense) transaction
          await supabase.from('transactions').insert({
            date: new Date().toISOString().split('T')[0],
            description: t.openingDebt,
            category_id: initCat?.id || null,
            account_id: newAcctId,
            type: 'expense',
            amount: openingDebt,
            user_id: userId,
          });
        } else {
          await supabase.from('transactions').insert({
            date: new Date().toISOString().split('T')[0],
            description: lang === 'zh' ? '开户初始余额' : 'Opening Balance',
            category_id: initCat?.id || null,
            account_id: newAcctId,
            type: 'income',
            amount: balance,
            crypto_quantity: cryptoAmount,
            user_id: userId,
          });
        }
      }
    }

    // Reset form
    setNewAcctName(''); setNewAcctBalance(''); setNewAcctColor('#1D9E75');
    setNewAcctCurrency(''); setNewAcctType('checking'); setNewCreditLimit(''); setNewAcctNumber('');
    setNewCryptoTicker('');
    setShowAddAccount(false);
    loadAll();
  }

  async function deleteAccount(id) {
    if (!window.confirm(t.confirmDeleteAccount)) return;
    // Remove everything that references this account, otherwise the DB blocks deletion (409).
    await supabase.from('plaid_items').delete().eq('account_id', id);          // connected bank
    await supabase.from('transactions').delete().eq('account_id', id);         // its transactions
    await supabase.from('transactions').delete().eq('transfer_to_account_id', id); // transfers INTO it
    await supabase.from('stock_holdings').delete().eq('account_id', id);       // stock holdings
    await supabase.from('crypto_holdings').delete().eq('account_id', id);      // crypto holdings
    await supabase.from('recurring_transactions').delete().eq('account_id', id);
    await supabase.from('recurring_transactions').delete().eq('transfer_to_account_id', id);
    const { error } = await supabase.from('accounts').delete().eq('id', id);
    if (error) {
      console.error('deleteAccount error:', error);
      alert(lang === 'zh' ? '删除失败：' + error.message : 'Delete failed: ' + error.message);
      return;
    }
    setSelectedAccount(null); setTab('overview'); loadAll();
  }

  // Open the edit dialog, prefilled with the account's current values.
  function openEditAccount(acct) {
    if (!acct) return;
    setEditAcct({
      id: acct.id,
      name: acct.name || '',
      color: acct.color || '#6D5BF0',
      account_number: acct.account_number || '',
      account_type: acct.account_type,
      credit_limit: acct.credit_limit != null ? String(acct.credit_limit) : '',
      // For credit cards, current debt = limit - available balance
      current_debt: acct.account_type === 'credit'
        ? String((parseFloat(acct.credit_limit || 0) - parseFloat(acct.balance || 0)))
        : '',
    });
    setShowEditAccount(true);
  }

  async function saveEditAccount() {
    if (!editAcct || !editAcct.name) return;
    const update = {
      name: editAcct.name,
      color: editAcct.color,
      account_number: editAcct.account_number || null,
    };
    // Credit card: recompute available balance from (possibly changed) limit & debt
    if (editAcct.account_type === 'credit') {
      const limit = parseFloat(editAcct.credit_limit || 0);
      const debt = parseFloat(editAcct.current_debt || 0);
      update.credit_limit = limit;
      update.balance = limit - debt;
    }
    await supabase.from('accounts').update(update).eq('id', editAcct.id);
    setShowEditAccount(false);
    setEditAcct(null);
    loadAll();
  }

  // Mask an account number, showing only the last 4 chars: ****1234
  function maskNumber(num) {
    if (!num) return '';
    const s = String(num).replace(/\s/g, '');
    if (s.length <= 4) return s;
    return '****' + s.slice(-4);
  }

  // ── Add Transaction ──────────────────────────────────────────────────────
  // Scan a receipt photo: upload -> Edge Function (DeepSeek) -> auto-fill the form
  // Compress an image file to a JPEG data URL (max edge 1600px, quality 0.85).
  // Receipts stay perfectly readable for AI while upload size drops ~90%.
  function compressImage(file, maxEdge = 1600, quality = 0.85) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        const scale = Math.min(1, maxEdge / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('图片读取失败')); };
      img.src = url;
    });
  }

  async function scanReceipt(file) {
    if (!file) return;
    setScanning(true);
    setScanMsg(lang === 'zh' ? '正在识别小票...' : 'Scanning receipt...');
    try {
      // Compress the photo before upload (much faster on slow networks)
      const dataUrl = await compressImage(file);

      // Category names to help the AI pick one
      const catNames = categories
        .filter(c => !c.parent_id)
        .map(c => lang === 'zh' ? c.name_zh : c.name_en);

      const { data, error } = await supabase.functions.invoke('scan-receipt', {
        body: { image: dataUrl, categories: catNames },
      });

      if (error) throw error;
      if (!data || !data.success) {
        throw new Error(data?.error || (lang === 'zh' ? '识别失败' : 'Scan failed'));
      }

      const r = data.data || {};
      // Fill the form with recognized values
      if (r.amount && !isNaN(parseFloat(r.amount))) setAmount(String(r.amount));
      if (r.merchant) setMerchant(r.merchant);
      if (r.note) setNote(r.note);
      if (r.date && /^\d{4}-\d{2}-\d{2}$/.test(r.date)) setTxnDate(r.date);
      // Match category by name
      if (r.category) {
        const match = categories.find(c => {
          const zh = (c.name_zh || '').toLowerCase();
          const en = (c.name_en || '').toLowerCase();
          const target = String(r.category).toLowerCase();
          return zh === target || en === target || zh.includes(target) || target.includes(zh);
        });
        if (match) setCatId(match.id);
      }
      // Show merchant/note since they were filled
      if (r.merchant || r.note) setShowExtras(true);
      setScanMsg(lang === 'zh' ? '✓ 识别完成，请核对后保存' : '✓ Done, please review & save');
      setTimeout(() => setScanMsg(''), 4000);
    } catch (e) {
      setScanMsg((lang === 'zh' ? '识别失败：' : 'Failed: ') + (e.message || e));
    } finally {
      setScanning(false);
    }
  }

  const addTxnLock = useRef(false);
  async function addTransaction() {
    // 防重复提交：连点/慢网络时立即拦截后续调用
    if (addTxnLock.current) return;
    if (!acctId) {
      alert(lang === 'zh' ? '请先选择账户。如果下面没有账户可选，说明你还没有创建账户，请先到“账户”页新增一个账户。' : 'Please select an account. If none appear below, create an account first on the Accounts page.');
      return;
    }
    const selectedAcct = accounts.find(a => a.id === acctId);
    const isCrypto = selectedAcct?.account_type === 'crypto';
    let parsedAmount = parseFloat(amount);

    // Validation: amount always required
    if (!amount || parseFloat(amount) <= 0) {
      alert(lang === 'zh' ? '请输入有效金额。' : 'Please enter a valid amount.');
      return;
    }
    // Category required only for non-transfer, non-crypto transactions
    if (txnType !== 'transfer' && !isCrypto && !catId) {
      alert(lang === 'zh' ? '请先选择分类。如果上面没有分类可选，请点右上角菜单里的“分类管理”添加分类，或退出重新登录一次以自动生成默认分类。' : 'Please select a category. If none appear, add categories in Category settings, or sign out and back in to auto-create the defaults.');
      return;
    }

    // 校验通过 → 上锁，防止连点重复插入
    addTxnLock.current = true;
    setSavingTxn(true);
    try {

    if (isCrypto && txnType !== 'transfer') {
      const symbol = selectedAcct.crypto_symbol;
      const price = cryptoPrices[symbol];
      if (price) parsedAmount = parseFloat(amount) * price;
      // Update crypto_amount
      const currentCryptoAmt = parseFloat(selectedAcct.crypto_amount || 0);
      const updatedCryptoAmt = txnType === 'income'
        ? currentCryptoAmt + parseFloat(amount)
        : currentCryptoAmt - parseFloat(amount);
      await supabase.from('accounts').update({ crypto_amount: updatedCryptoAmt }).eq('id', acctId);
    }

    if (txnType === 'transfer') {
      if (!transferToAcctId || transferToAcctId === acctId) return;
      const fromAcct = accounts.find(a => a.id === acctId);
      const toAcct = accounts.find(a => a.id === transferToAcctId);
      const isWalletToWallet = fromAcct?.account_type === 'crypto' && toAcct?.account_type === 'crypto';

      // ── Case 1: Crypto wallet → Crypto wallet (move coins, carry cost basis) ──
      if (isWalletToWallet && transferCryptoHoldingId) {
        const srcHolding = cryptoHoldings.find(h => h.id === transferCryptoHoldingId);
        if (!srcHolding) { alert(lang === 'zh' ? '请选择要转移的币种' : 'Please select a coin to move'); return; }
        const moveQty = parseFloat(amount); // amount is the COIN QUANTITY here
        const srcQty = parseFloat(srcHolding.amount || 0);
        if (moveQty <= 0 || moveQty > srcQty + 1e-12) {
          alert(lang === 'zh' ? `数量超出可用余额（可用 ${srcQty}）` : `Amount exceeds available (${srcQty})`); return;
        }
        const costPer = parseFloat(srcHolding.avg_cost || 0); // cost basis carried over
        const fiatValue = moveQty * (cryptoPrices[srcHolding.coin_id] || costPer);

        // Source: reduce quantity (avg_cost unchanged for remaining coins)
        const newSrcQty = srcQty - moveQty;
        await supabase.from('crypto_holdings').update({ amount: newSrcQty }).eq('id', srcHolding.id);

        // Destination: find same coin in target wallet, or create it
        const destHolding = cryptoHoldings.find(h => h.account_id === transferToAcctId && h.coin_id === srcHolding.coin_id);
        if (destHolding) {
          // Weighted-average merge of cost basis
          const dQty = parseFloat(destHolding.amount || 0);
          const dCost = parseFloat(destHolding.avg_cost || 0);
          const mergedQty = dQty + moveQty;
          const mergedCost = mergedQty > 0 ? (dQty * dCost + moveQty * costPer) / mergedQty : costPer;
          await supabase.from('crypto_holdings').update({ amount: mergedQty, avg_cost: mergedCost }).eq('id', destHolding.id);
        } else {
          // New holding in destination wallet, carry the source cost basis
          await supabase.from('crypto_holdings').insert({
            account_id: transferToAcctId, coin_id: srcHolding.coin_id,
            ticker: srcHolding.ticker, coin_name: srcHolding.coin_name,
            amount: moveQty, avg_cost: costPer, user_id: userId,
          });
        }

        // Recompute both wallet balances
        const updatedHoldings = cryptoHoldings
          .map(h => h.id === srcHolding.id ? { ...h, amount: newSrcQty } : h);
        await recomputeCryptoWalletBalance(acctId, updatedHoldings);
        await recomputeCryptoWalletBalance(transferToAcctId);

        const moveDesc = merchant || `${lang === 'zh' ? '转移' : 'Move'} ${srcHolding.ticker || srcHolding.coin_id}`;
        await supabase.from('transactions').insert([
          { date: txnDate, description: moveDesc, note: note || '', account_id: acctId, type: 'transfer', amount: fiatValue, transfer_to_account_id: transferToAcctId, is_transfer_out: true, crypto_action: 'transfer', crypto_holding_id: srcHolding.id, crypto_amount: moveQty, user_id: userId },
          { date: txnDate, description: moveDesc, note: note || '', account_id: transferToAcctId, type: 'transfer', amount: fiatValue, transfer_to_account_id: acctId, is_transfer_out: false, crypto_action: 'transfer', crypto_amount: moveQty, user_id: userId },
        ]);

        setShowModal(false);
        setAmount(''); setMerchant(''); setNote(''); setCatId(''); setAcctId(''); setTransferToAcctId(''); setTransferCryptoHoldingId('');
        await loadAll();
        return;
      }

      // ── Case 2: Crypto wallet → non-crypto (e.g. pay credit card with coins) ──
      if (fromAcct?.account_type === 'crypto' && transferCryptoHoldingId) {
        // Paying from a specific coin holding → reduce that coin's amount, then recompute wallet total
        const holding = cryptoHoldings.find(h => h.id === transferCryptoHoldingId);
        if (holding) {
          const coinPrice = cryptoPrices[holding.coin_id] || parseFloat(holding.avg_cost || 1);
          const coinsToSell = coinPrice > 0 ? parsedAmount / coinPrice : 0;
          const newCoinAmt = Math.max(0, parseFloat(holding.amount || 0) - coinsToSell);
          await supabase.from('crypto_holdings').update({ amount: newCoinAmt }).eq('id', holding.id);
          const updatedHoldings = cryptoHoldings.map(h => h.id === holding.id ? { ...h, amount: newCoinAmt } : h);
          await recomputeCryptoWalletBalance(acctId, updatedHoldings);
        }
      } else if (fromAcct) {
        await supabase.from('accounts').update({ balance: parseFloat(fromAcct.balance) - parsedAmount }).eq('id', acctId);
        if (fromAcct.account_type === 'stock') {
          await supabase.from('accounts').update({ cash_balance: parseFloat(fromAcct.cash_balance || 0) - parsedAmount }).eq('id', acctId);
        }
      }

      // To account: add. If it's a stock account, the money becomes cash.
      if (toAcct) {
        await supabase.from('accounts').update({ balance: parseFloat(toAcct.balance) + parsedAmount }).eq('id', transferToAcctId);
        if (toAcct.account_type === 'stock') {
          await supabase.from('accounts').update({ cash_balance: parseFloat(toAcct.cash_balance || 0) + parsedAmount }).eq('id', transferToAcctId);
        }
      }

      // Auto-generate description if merchant is empty and paying from crypto
      const selectedCoinHolding = cryptoHoldings.find(h => h.id === transferCryptoHoldingId);
      const txnDesc = merchant || (selectedCoinHolding
        ? `${lang === 'zh' ? '转出' : 'Transfer'} ${selectedCoinHolding.ticker || selectedCoinHolding.coin_id}`
        : '');

      await supabase.from('transactions').insert([
        { date: txnDate, description: txnDesc, note: note || '', category_id: catId || null, account_id: acctId, type: 'transfer', amount: parsedAmount, transfer_to_account_id: transferToAcctId, is_transfer_out: true, user_id: userId },
        { date: txnDate, description: txnDesc, note: note || '', category_id: catId || null, account_id: transferToAcctId, type: 'transfer', amount: parsedAmount, transfer_to_account_id: acctId, is_transfer_out: false, user_id: userId },
      ]);
    } else {
      const { error } = await supabase.from('transactions').insert({
        date: txnDate, description: merchant || '', note: note || '',
        category_id: catId || null, account_id: acctId,
        type: txnType, amount: parsedAmount,
        // Store original coin quantity for crypto accounts so display is exact
        crypto_quantity: isCrypto ? parseFloat(amount) : null,
        user_id: userId,
      });
      if (!error) {
        const acct = accounts.find(a => a.id === acctId);
        if (acct) {
          const newBalance = txnType === 'income'
            ? parseFloat(acct.balance) + parsedAmount
            : parseFloat(acct.balance) - parsedAmount;
          await supabase.from('accounts').update({ balance: newBalance }).eq('id', acctId);
        }
      }
    }
    // Remember the account used for next time
    try { if (acctId) localStorage.setItem('em_last_account', acctId); } catch {}
    setAmount(''); setMerchant(''); setNote(''); setCatId(''); setAcctId(''); setTransferToAcctId(''); setTransferCryptoHoldingId('');
    setShowModal(false); loadAll();

    } finally {
      // 无论成功、失败还是中途 return，都解锁，避免按钮永久卡住
      addTxnLock.current = false;
      setSavingTxn(false);
    }
  }

  // 批量记账：一次性插入多行（同账户、同日期、同类型），合并余额更新
  const saveBatchLock = useRef(false);
  async function saveBatch() {
    if (saveBatchLock.current) return;
    const acct = accounts.find(a => a.id === batchAcctId);
    if (!acct) { alert(lang === 'zh' ? '请选择账户' : 'Please select an account'); return; }
    if (acct.account_type === 'crypto' || acct.account_type === 'stock') {
      alert(lang === 'zh' ? '批量记账暂不支持加密/股票账户，请用普通记账。' : 'Batch entry not supported for crypto/stock accounts.');
      return;
    }
    // 过滤出有效行：金额>0 且 选了分类
    const valid = batchLines
      .map(l => ({ ...l, amt: parseFloat(l.amount) }))
      .filter(l => l.amt > 0 && l.catId);
    if (valid.length === 0) {
      alert(lang === 'zh' ? '请至少填写一行有效记录（金额和分类都要填）。' : 'Please fill at least one valid line (amount + category).');
      return;
    }
    saveBatchLock.current = true;
    setBatchSaving(true);
    try {
      const rows = valid.map(l => ({
        date: batchDate,
        description: l.merchant || '',
        note: l.note || '',
        category_id: l.catId,
        account_id: batchAcctId,
        type: batchType,
        amount: l.amt,
        user_id: userId,
      }));
      const { error } = await supabase.from('transactions').insert(rows);
      if (error) {
        alert(lang === 'zh' ? '保存失败：' + error.message : 'Save failed: ' + error.message);
        return;
      }
      // 合并余额更新（一次读一次写，避免逐笔竞态）
      const net = valid.reduce((s, l) => s + (batchType === 'income' ? l.amt : -l.amt), 0);
      await supabase.from('accounts').update({ balance: parseFloat(acct.balance || 0) + net }).eq('id', batchAcctId);
      try { localStorage.setItem('em_last_account', batchAcctId); } catch {}
      // 重置并关闭
      setBatchLines([{ amount: '', catId: '', merchant: '', note: '', showMore: false }]);
      setShowBatch(false);
      loadAll();
    } catch (e) {
      console.error('saveBatch error:', e);
      alert(lang === 'zh' ? '出错了，请重试。' : 'Something went wrong.');
    } finally {
      saveBatchLock.current = false;
      setBatchSaving(false);
    }
  }

  function addBatchLine() {
    setBatchLines(prev => [...prev, { amount: '', catId: '', merchant: '', note: '', showMore: false }]);
  }
  function updateBatchLine(idx, patch) {
    setBatchLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  }
  function removeBatchLine(idx) {
    setBatchLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  }
  function openBatch() {
    // 默认账户：上次用的非信用卡账户，或第一个现金类
    let last = '';
    try { last = localStorage.getItem('em_last_account') || ''; } catch {}
    const lastAcct = accounts.find(a => a.id === last && a.account_type !== 'crypto' && a.account_type !== 'stock');
    const cashLike = accounts.find(a => ['checking', 'savings', 'cash'].includes(a.account_type));
    setBatchAcctId((lastAcct || cashLike || accounts.find(a => a.account_type !== 'crypto' && a.account_type !== 'stock') || accounts[0])?.id || '');
    setBatchType('expense');
    setBatchDate(new Date().toISOString().split('T')[0]);
    setBatchLines([{ amount: '', catId: '', merchant: '', note: '', showMore: false }]);
    setShowBatch(true);
  }

  async function saveEditTxn(txn, updated) {
    const newType = updated.type || txn.type;
    const newAmount = parseFloat(updated.amount || txn.amount);
    const fromAcctId = updated.account_id || txn.account_id;

    // ── Special case: converting a non-transfer txn INTO a transfer ──
    if (newType === 'transfer' && txn.type !== 'transfer') {
      const toAcctId = updated.transfer_to_account_id;
      if (!toAcctId) {
        alert(lang === 'zh' ? '请选择转账到哪个账户。' : 'Please choose a destination account.');
        return;
      }
      const fromAcct = accounts.find(a => a.id === fromAcctId);
      const toAcct = accounts.find(a => a.id === toAcctId);

      // 1. Undo the original txn's effect on the source balance
      if (fromAcct) {
        let bal = parseFloat(fromAcct.balance);
        if (txn.type === 'income') bal -= parseFloat(txn.amount);
        else bal += parseFloat(txn.amount);
        // 2. Apply transfer-out (money leaves source)
        bal -= newAmount;
        await supabase.from('accounts').update({ balance: bal }).eq('id', fromAcct.id);
      }
      // 3. Money arrives in destination
      if (toAcct) {
        await supabase.from('accounts').update({ balance: parseFloat(toAcct.balance) + newAmount }).eq('id', toAcct.id);
      }
      // 4. Turn the original row into the transfer-out leg
      await supabase.from('transactions').update({
        date: updated.date || txn.date,
        description: updated.description ?? txn.description,
        note: updated.note ?? txn.note,
        category_id: null,
        account_id: fromAcctId,
        type: 'transfer',
        amount: newAmount,
        transfer_to_account_id: toAcctId,
        is_transfer_out: true,
      }).eq('id', txn.id);
      // 5. Create the matching transfer-in leg in the destination account
      await supabase.from('transactions').insert({
        date: updated.date || txn.date,
        description: updated.description ?? txn.description,
        note: updated.note ?? txn.note,
        account_id: toAcctId,
        type: 'transfer',
        amount: newAmount,
        transfer_to_account_id: fromAcctId,
        is_transfer_out: false,
        user_id: userId,
      });
      setEditingTxn(null); loadAll();
      return;
    }

    // ── Special case: editing an EXISTING transfer (amount / date / destination / desc / note) ──
    // (direction itself, i.e. is_transfer_out, is not changed here — only which account it pairs with)
    if (newType === 'transfer' && txn.type === 'transfer') {
      const oldAmount = parseFloat(txn.amount);
      const oldOtherId = txn.transfer_to_account_id;
      const newOtherId = updated.transfer_to_account_id || txn.transfer_to_account_id;
      if (!newOtherId) {
        alert(lang === 'zh' ? '请选择转账账户。' : 'Please choose the other account.');
        return;
      }
      const oldOutId = txn.is_transfer_out ? txn.account_id : oldOtherId;
      const oldInId = txn.is_transfer_out ? oldOtherId : txn.account_id;
      const newOutId = txn.is_transfer_out ? txn.account_id : newOtherId;
      const newInId = txn.is_transfer_out ? newOtherId : txn.account_id;

      // Net delta per account (avoids double-counting when an account appears in both old & new state)
      const deltas = {};
      const addDelta = (id, amt) => { if (!id) return; deltas[id] = (deltas[id] || 0) + amt; };
      addDelta(oldOutId, oldAmount);   // undo old "money left this account"
      addDelta(oldInId, -oldAmount);   // undo old "money arrived in this account"
      addDelta(newOutId, -newAmount);  // apply new "money leaves this account"
      addDelta(newInId, newAmount);    // apply new "money arrives in this account"

      for (const id of Object.keys(deltas)) {
        if (deltas[id] === 0) continue;
        const acct = accounts.find(a => a.id === id);
        if (acct) {
          await supabase.from('accounts').update({ balance: parseFloat(acct.balance) + deltas[id] }).eq('id', id);
        }
      }

      // Update this leg
      await supabase.from('transactions').update({
        date: updated.date || txn.date,
        description: updated.description ?? txn.description,
        note: updated.note ?? txn.note,
        amount: newAmount,
        transfer_to_account_id: newOtherId,
      }).eq('id', txn.id);

      // Find and update the paired leg (it currently sits on the OLD "other" account)
      const { data: pairedRows } = await supabase.from('transactions').select('id')
        .eq('account_id', oldOtherId).eq('transfer_to_account_id', txn.account_id)
        .eq('date', txn.date).eq('amount', oldAmount).eq('type', 'transfer');
      if (pairedRows && pairedRows.length) {
        await supabase.from('transactions').update({
          date: updated.date || txn.date,
          description: updated.description ?? txn.description,
          note: updated.note ?? txn.note,
          amount: newAmount,
          account_id: newOtherId, // moves to the new "other" account if destination changed
          transfer_to_account_id: txn.account_id,
        }).eq('id', pairedRows[0].id);
      }

      setEditingTxn(null); loadAll();
      return;
    }

    // ── Normal edit (no transfer conversion) ──
    const acct = accounts.find(a => a.id === txn.account_id);
    if (acct && txn.type !== 'transfer') {
      let balance = parseFloat(acct.balance);
      if (txn.type === 'income') balance -= parseFloat(txn.amount);
      else balance += parseFloat(txn.amount);
      if (newType === 'income') balance += newAmount;
      else balance -= newAmount;
      await supabase.from('accounts').update({ balance }).eq('id', acct.id);
    }
    await supabase.from('transactions').update({
      date: updated.date || txn.date,
      description: updated.description ?? txn.description,
      note: updated.note ?? txn.note,
      category_id: updated.category_id || txn.category_id,
      account_id: updated.account_id || txn.account_id,
      type: newType,
      amount: newAmount,
    }).eq('id', txn.id);
    setEditingTxn(null); loadAll();
  }

  async function deleteTransaction(txn) {
    if (!window.confirm(t.confirmDelete)) return;
    if (txn.type === 'transfer') {
      // 无论用户点的是转出腿还是转入腿，都要正确算出哪个账户是"出"、哪个是"入"，
      // 并分别独立回退余额（不要求两个账户都存在才回退，避免漏单边）
      const outAcctId = txn.is_transfer_out ? txn.account_id : txn.transfer_to_account_id;
      const inAcctId = txn.is_transfer_out ? txn.transfer_to_account_id : txn.account_id;
      const outAcct = accounts.find(a => a.id === outAcctId);
      const inAcct = accounts.find(a => a.id === inAcctId);
      if (outAcct) {
        await supabase.from('accounts').update({ balance: parseFloat(outAcct.balance) + parseFloat(txn.amount) }).eq('id', outAcct.id);
      }
      if (inAcct) {
        await supabase.from('accounts').update({ balance: parseFloat(inAcct.balance) - parseFloat(txn.amount) }).eq('id', inAcct.id);
      }
      await supabase.from('transactions').delete().eq('account_id', txn.account_id).eq('transfer_to_account_id', txn.transfer_to_account_id).eq('date', txn.date).eq('amount', txn.amount);
      await supabase.from('transactions').delete().eq('account_id', txn.transfer_to_account_id).eq('transfer_to_account_id', txn.account_id).eq('date', txn.date).eq('amount', txn.amount);
    } else {
      const acct = accounts.find(a => a.id === txn.account_id);
      if (acct) {
        const newBalance = txn.type === 'income'
          ? parseFloat(acct.balance) - parseFloat(txn.amount)
          : parseFloat(acct.balance) + parseFloat(txn.amount);
        await supabase.from('accounts').update({ balance: newBalance }).eq('id', acct.id);
      }
      await supabase.from('transactions').delete().eq('id', txn.id);
    }
    loadAll();
  }

  // ── Categories ───────────────────────────────────────────────────────────
  async function addCategory() {
    if (!newCatZh || !newCatEn) return;
    // If a parent is chosen, the subcategory inherits the parent's type
    let typeToUse = newCatType;
    if (newCatParent) {
      const parent = categories.find(c => c.id === newCatParent);
      if (parent) typeToUse = parent.type;
    }
    await supabase.from('categories').insert({
      name_zh: newCatZh, name_en: newCatEn, type: typeToUse,
      color: '#F1EFE8', parent_id: newCatParent || null,
      user_id: userId,
    });
    setNewCatZh(''); setNewCatEn(''); setNewCatType('expense'); setNewCatParent('');
    setShowAddCategory(false); loadAll();
  }

  async function deleteCategory(id) {
    if (!window.confirm(t.confirmDeleteCategory)) return;
    await supabase.from('categories').delete().eq('id', id); loadAll();
  }

  // ── CSV / JSON Export / Import ───────────────────────────────────────────
  function exportCSV(txnList, filename) {
    const headers = lang === 'zh'
      ? ['日期', '商家', '备注', '分类', '账户', '类型', '金额', '币种']
      : ['Date', 'Merchant', 'Note', 'Category', 'Account', 'Type', 'Amount', 'Currency'];
    const rows = txnList.map(tx => {
      const cat = categories.find(c => c.id === tx.category_id);
      const acct = accounts.find(a => a.id === tx.account_id);
      const curr = getCurrencyForAccount(acct);
      return [tx.date, tx.description || '', tx.note || '', cat ? (lang === 'zh' ? cat.name_zh : cat.name_en) : '', acct ? acct.name : '', tx.type, tx.amount, curr.code];
    });
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
  }

  function exportAllCSV() { exportCSV(transactions, `easymoney_${new Date().toISOString().split('T')[0]}.csv`); }

  // Full backup export.
  // ⚠️ MAINTENANCE: when adding a NEW data table to the app, add it to BOTH
  //   (1) the `tables` list below, and (2) importJSON's restore logic.
  // ── Plaid: connect a bank and sync transactions ──────────────────────────
  // Dynamically load the Plaid Link script (once).
  function loadPlaidScript() {
    return new Promise((resolve, reject) => {
      if (window.Plaid) return resolve();
      const s = document.createElement('script');
      s.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load Plaid'));
      document.body.appendChild(s);
    });
  }

  // Connect a bank. After Plaid auth, the backend returns the bank's sub-accounts.
  // We then show a mapping modal so the user picks new/existing/skip for each.
  async function connectBank() {
    setPlaidBusy(true);
    try {
      await loadPlaidScript();
      const { data, error } = await supabase.functions.invoke('plaid-link-token', {
        body: { user_id: userId },
      });
      if (error || !data?.link_token) {
        alert(lang === 'zh' ? '无法连接银行服务，请稍后再试。' : 'Could not start bank connection.');
        setPlaidBusy(false);
        return;
      }
      const handler = window.Plaid.create({
        token: data.link_token,
        onSuccess: async (public_token, metadata) => {
          const { data: exData, error: exErr } = await supabase.functions.invoke('plaid-exchange-token', {
            body: {
              public_token, user_id: userId,
              institution_name: metadata?.institution?.name || null,
            },
          });
          setPlaidBusy(false);
          if (exErr || !exData?.success) {
            alert(lang === 'zh' ? '连接失败，请重试。' : 'Connection failed, please retry.');
            return;
          }
          // Build mapping rows; default action = 'new'
          const rows = (exData.accounts || []).map(a => ({
            ...a,
            action: 'new',            // 'new' | 'existing' | 'skip'
            existing_account_id: '',
          }));
          setPlaidMapping({
            item_id: exData.item_id,
            institution_name: exData.institution_name,
            rows,
          });
        },
        onExit: () => { setPlaidBusy(false); },
      });
      handler.open();
    } catch (e) {
      console.error('connectBank error:', e);
      alert(lang === 'zh' ? '出错了，请稍后再试。' : 'Something went wrong.');
      setPlaidBusy(false);
    }
  }

  // Finalize: send the user's mapping choices to the backend, then sync.
  async function finalizeBank() {
    if (!plaidMapping) return;
    setPlaidBusy(true);
    try {
      const mappings = plaidMapping.rows.map(r => ({
        plaid_account_id: r.plaid_account_id,
        action: r.action,
        existing_account_id: r.action === 'existing' ? r.existing_account_id : null,
        name: r.name ? `${plaidMapping.institution_name || ''} ${r.name}`.trim() : null,
        mask: r.mask,
        suggested_type: r.suggested_type,
        balance: r.balance,
        credit_limit: r.credit_limit,
        currency: r.currency,
      }));
      // Validation: 'existing' rows must have a chosen account
      for (const m of mappings) {
        if (m.action === 'existing' && !m.existing_account_id) {
          alert(lang === 'zh' ? '请为"并入已有账户"的项目选择一个账户。' : 'Please pick an account for each "merge into existing" item.');
          setPlaidBusy(false);
          return;
        }
      }
      const { data, error } = await supabase.functions.invoke('plaid-finalize-accounts', {
        body: { user_id: userId, item_id: plaidMapping.item_id, mappings },
      });
      if (error || data?.error) {
        alert(lang === 'zh' ? '保存失败，请重试。' : 'Save failed, please retry.');
        setPlaidBusy(false);
        return;
      }
      setPlaidMapping(null);
      await loadAll();
      await syncBank();
    } catch (e) {
      console.error('finalizeBank error:', e);
      alert(lang === 'zh' ? '出错了。' : 'Something went wrong.');
      setPlaidBusy(false);
    }
  }

  // Pull new transactions. If accountId is given, sync only that account; otherwise all.
  // 更新模式：在已有连接上补勾之前漏选的账户（不消耗名额）
  async function updateBankAccounts(itemId, institutionName) {
    setPlaidBusy(true);
    try {
      await loadPlaidScript();
      // 1. 用已有 access_token 生成"更新模式" link_token
      const { data, error } = await supabase.functions.invoke('plaid-update-link-token', {
        body: { user_id: userId, item_id: itemId },
      });
      if (error || !data?.link_token) {
        alert(lang === 'zh' ? '无法打开更新界面，请稍后再试。' : 'Could not open update flow.');
        setPlaidBusy(false);
        return;
      }
      const handler = window.Plaid.create({
        token: data.link_token,
        onSuccess: async () => {
          // 2. 补勾后，重新读取账户、过滤出"新账户"
          const { data: rData, error: rErr } = await supabase.functions.invoke('plaid-refresh-accounts', {
            body: { user_id: userId, item_id: itemId, institution_name: institutionName || null },
          });
          setPlaidBusy(false);
          if (rErr || !rData?.success) {
            alert(lang === 'zh' ? '读取账户失败，请重试。' : 'Failed to read accounts, please retry.');
            return;
          }
          if (!rData.accounts || rData.accounts.length === 0) {
            alert(lang === 'zh'
              ? '没有发现新的账户。可能那张卡在银行端没有授权给 Plaid，或已经导入过了。'
              : 'No new accounts found. The card may not be shared with Plaid, or is already imported.');
            return;
          }
          // 3. 弹映射界面，让用户选 新建/并入/不导入
          const rows = rData.accounts.map(a => ({ ...a, action: 'new', existing_account_id: '' }));
          setPlaidMapping({ item_id: rData.item_id, institution_name: rData.institution_name || institutionName, rows });
        },
        onExit: () => { setPlaidBusy(false); },
      });
      handler.open();
    } catch (e) {
      console.error('updateBankAccounts error:', e);
      alert(lang === 'zh' ? '出错了，请稍后再试。' : 'Something went wrong.');
      setPlaidBusy(false);
    }
  }

  async function syncBank(accountId) {
    setPlaidBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('plaid-sync-transactions', {
        body: { user_id: userId, account_id: accountId || null },
      });
      if (error || data?.error) {
        alert(lang === 'zh' ? '同步失败，请稍后再试。' : 'Sync failed, please try again.');
      } else {
        const dupMsg = data.duplicates > 0
          ? (lang === 'zh' ? `，其中 ${data.duplicates} 笔疑似重复（已标记）` : `, ${data.duplicates} possible duplicate(s) flagged`)
          : '';
        alert(lang === 'zh' ? `同步完成：新增 ${data.added || 0} 笔交易${dupMsg}。` : `Sync done: ${data.added || 0} new transaction(s)${dupMsg}.`);
        await loadAll();
      }
    } catch (e) {
      console.error('syncBank error:', e);
      alert(lang === 'zh' ? '同步出错。' : 'Sync error.');
    } finally {
      setPlaidBusy(false);
    }
  }

  // Apply the chosen category to all selected transactions at once.
  async function applyBatchCategory() {
    if (!batchCatId || batchSelected.length === 0) {
      alert(lang === 'zh' ? '请选择交易和分类。' : 'Please select transactions and a category.');
      return;
    }
    await supabase.from('transactions').update({ category_id: batchCatId }).in('id', batchSelected);
    setBatchCat(null); setBatchSelected([]); setBatchCatId('');
    await loadAll();
  }

  // Keep a flagged transaction: clear the possible_duplicate mark.
  async function keepDuplicate(txnId) {
    await supabase.from('transactions').update({ possible_duplicate: false }).eq('id', txnId);
    await loadAll();
  }

  async function exportJSON() {
    // Fetch every table fresh from the DB so the backup is always complete & current
    const tables = ['accounts', 'categories', 'transactions', 'currencies',
                    'user_currencies', 'settings', 'exchange_rates',
                    'budgets', 'recurring_transactions', 'stock_holdings', 'crypto_holdings'];
    const data = { exportedAt: new Date().toISOString(), version: 2 };
    for (const tbl of tables) {
      const { data: rows } = await supabase.from(tbl).select('*');
      data[tbl] = rows || [];
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `easymoney_backup_${new Date().toISOString().split('T')[0]}.json`; a.click(); URL.revokeObjectURL(url);
  }

  async function importCSV(file) {
    const text = await file.text();
    const lines = text.replace(/^\uFEFF/, '').split('\n').filter(l => l.trim());
    // Columns (match exportCSV): date, merchant, note, category, account, type, amount, currency
    const rows = lines.slice(1).map(line => {
      const cols = line.split(',').map(c => c.replace(/^"|"$/g, '').trim());
      return {
        date: cols[0], description: cols[1], note: cols[2],
        catName: cols[3], acctName: cols[4], type: cols[5], amount: parseFloat(cols[6]),
      };
    });
    let imported = 0;
    for (const row of rows) {
      if (!row.date || !row.type || isNaN(row.amount)) continue;
      const cat = categories.find(c => c.name_zh === row.catName || c.name_en === row.catName);
      const acct = accounts.find(a => a.name === row.acctName);
      if (!acct) continue;
      await supabase.from('transactions').insert({
        date: row.date, description: row.description, note: row.note || '',
        category_id: cat?.id || null, account_id: acct.id, type: row.type, amount: row.amount,
        user_id: userId,
      });
      const newBalance = row.type === 'income' ? parseFloat(acct.balance) + row.amount : parseFloat(acct.balance) - row.amount;
      await supabase.from('accounts').update({ balance: newBalance }).eq('id', acct.id);
      imported++;
    }
    alert(t.importedTxns(imported)); loadAll();
  }

  // Full backup restore. Remaps old IDs → new IDs to preserve relationships.
  // ⚠️ MAINTENANCE: when adding a new table, handle its restore here too.
  async function importJSON(file) {
    const text = await file.text();
    let data;
    try { data = JSON.parse(text); } catch { alert(t.invalidFormat); return; }
    if (!data.transactions && !data.accounts) { alert(t.invalidFormat); return; }

    if (!window.confirm(lang === 'zh'
      ? '智能合并导入：只补充当前账户中缺少的数据，已存在的记录会自动跳过（不会产生重复）。继续？'
      : 'Smart merge import: only missing data will be added; existing records are skipped automatically (no duplicates). Continue?')) return;

    try {
      const idMap = { account: {}, category: {}, holding: {}, cryptoHolding: {} };
      let skipped = 0;

      // Load current data once, to detect existing records
      const [curAccts, curCats, curStocks, curCryptos, curTxns, curBudgets, curRecurs] = await Promise.all([
        supabase.from('accounts').select('*').eq('user_id', userId),
        supabase.from('categories').select('*').eq('user_id', userId),
        supabase.from('stock_holdings').select('*').eq('user_id', userId),
        supabase.from('crypto_holdings').select('*').eq('user_id', userId),
        supabase.from('transactions').select('*').eq('user_id', userId),
        supabase.from('budgets').select('*').eq('user_id', userId),
        supabase.from('recurring_transactions').select('*').eq('user_id', userId),
      ]);
      const existAccts = curAccts.data || [];
      const existCats = curCats.data || [];
      const existStocks = curStocks.data || [];
      const existCryptos = curCryptos.data || [];
      const existTxns = curTxns.data || [];
      const existBudgets = curBudgets.data || [];
      const existRecurs = curRecurs.data || [];

      // 1. Currencies (ensure they exist)
      for (const c of (data.currencies || [])) {
        const { data: ex } = await supabase.from('currencies').select('code').eq('code', c.code).limit(1);
        if (!ex || ex.length === 0) {
          await supabase.from('currencies').insert({ code: c.code, symbol: c.symbol, name_zh: c.name_zh, name_en: c.name_en });
        }
      }
      // user_currencies (enable)
      for (const uc of (data.user_currencies || [])) {
        const { data: ex } = await supabase.from('user_currencies').select('id').eq('user_id', userId).eq('currency_code', uc.currency_code).limit(1);
        if (!ex || ex.length === 0) await supabase.from('user_currencies').insert({ user_id: userId, currency_code: uc.currency_code });
      }

      // 2. Accounts — dedup by name + account_type; reuse if exists
      for (const a of (data.accounts || [])) {
        const match = existAccts.find(e => e.name === a.name && e.account_type === a.account_type);
        if (match) { idMap.account[a.id] = match.id; continue; }
        const { data: created } = await supabase.from('accounts').insert({
          name: a.name, balance: a.balance, color: a.color, account_type: a.account_type,
          currency_code: a.currency_code, account_number: a.account_number,
          credit_limit: a.credit_limit, cash_balance: a.cash_balance,
          crypto_symbol: a.crypto_symbol, crypto_ticker: a.crypto_ticker, crypto_amount: a.crypto_amount,
          user_id: userId,
        }).select().single();
        if (created) { idMap.account[a.id] = created.id; existAccts.push(created); }
      }

      // 3. Categories — dedup by name_zh + type; reuse if exists
      for (const c of (data.categories || [])) {
        const match = existCats.find(e => e.name_zh === c.name_zh && e.type === c.type);
        if (match) { idMap.category[c.id] = match.id; continue; }
        const { data: created } = await supabase.from('categories').insert({
          name_zh: c.name_zh, name_en: c.name_en, type: c.type, color: c.color, user_id: userId,
        }).select().single();
        if (created) { idMap.category[c.id] = created.id; existCats.push(created); }
      }
      // fix subcategory parent links (only for newly created ones)
      for (const c of (data.categories || [])) {
        if (c.parent_id && idMap.category[c.id] && idMap.category[c.parent_id]) {
          await supabase.from('categories').update({ parent_id: idMap.category[c.parent_id] }).eq('id', idMap.category[c.id]);
        }
      }

      // 4. Stock holdings — dedup by account + symbol
      for (const h of (data.stock_holdings || [])) {
        const mappedAcct = idMap.account[h.account_id] || null;
        const match = existStocks.find(e => e.account_id === mappedAcct && e.symbol === h.symbol);
        if (match) { idMap.holding[h.id] = match.id; continue; }
        const { data: created } = await supabase.from('stock_holdings').insert({
          account_id: mappedAcct, symbol: h.symbol, company_name: h.company_name,
          market: h.market, shares: h.shares, avg_cost: h.avg_cost, current_price: h.current_price,
          manual_price: h.manual_price, user_id: userId,
        }).select().single();
        if (created) { idMap.holding[h.id] = created.id; existStocks.push(created); }
      }

      // 4b. Crypto holdings — dedup by account + coin_id
      for (const h of (data.crypto_holdings || [])) {
        const mappedAcct = idMap.account[h.account_id] || null;
        const match = existCryptos.find(e => e.account_id === mappedAcct && e.coin_id === h.coin_id);
        if (match) { idMap.cryptoHolding[h.id] = match.id; continue; }
        const { data: created } = await supabase.from('crypto_holdings').insert({
          account_id: mappedAcct, coin_id: h.coin_id, ticker: h.ticker,
          coin_name: h.coin_name, amount: h.amount, avg_cost: h.avg_cost, user_id: userId,
        }).select().single();
        if (created) { idMap.cryptoHolding[h.id] = created.id; existCryptos.push(created); }
      }

      // 5. Transactions — dedup by date + amount + type + account + description
      let imported = 0;
      for (const tx of (data.transactions || [])) {
        const mappedAcct = idMap.account[tx.account_id] || null;
        const dup = existTxns.find(e =>
          e.date === tx.date &&
          Number(e.amount) === Number(tx.amount) &&
          e.type === tx.type &&
          e.account_id === mappedAcct &&
          (e.description || '') === (tx.description || '')
        );
        if (dup) { skipped++; continue; }
        await supabase.from('transactions').insert({
          date: tx.date, description: tx.description, note: tx.note,
          category_id: idMap.category[tx.category_id] || null,
          account_id: mappedAcct,
          transfer_to_account_id: idMap.account[tx.transfer_to_account_id] || null,
          is_transfer_out: tx.is_transfer_out, type: tx.type, amount: tx.amount,
          holding_id: idMap.holding[tx.holding_id] || null,
          stock_shares: tx.stock_shares, stock_price: tx.stock_price, stock_action: tx.stock_action,
          crypto_holding_id: idMap.cryptoHolding[tx.crypto_holding_id] || null,
          crypto_amount: tx.crypto_amount, crypto_price: tx.crypto_price, crypto_action: tx.crypto_action,
          realized_pnl: tx.realized_pnl, pending: tx.pending, user_id: userId,
        });
        imported++;
      }

      // 6. Budgets — dedup by name + category
      for (const b of (data.budgets || [])) {
        const mappedCat = idMap.category[b.category_id] || null;
        const dup = existBudgets.find(e => (e.name || '') === (b.name || '') && e.category_id === mappedCat);
        if (dup) continue;
        await supabase.from('budgets').insert({
          name: b.name, category_id: mappedCat,
          amount: b.amount, period: b.period, user_id: userId,
        });
      }

      // 7. Recurring — dedup by description + amount + type
      for (const r of (data.recurring_transactions || [])) {
        const dup = existRecurs.find(e =>
          (e.description || '') === (r.description || '') &&
          Number(e.amount) === Number(r.amount) &&
          e.type === r.type
        );
        if (dup) continue;
        await supabase.from('recurring_transactions').insert({
          type: r.type, amount: r.amount, account_id: idMap.account[r.account_id] || null,
          transfer_to_account_id: idMap.account[r.transfer_to_account_id] || null,
          category_id: idMap.category[r.category_id] || null,
          description: r.description, note: r.note, period_type: r.period_type,
          period_day: r.period_day, period_month: r.period_month, advance_days: r.advance_days,
          next_due: r.next_due, active: r.active, user_id: userId,
        });
      }

      alert(lang === 'zh'
        ? `合并完成：新增 ${imported} 笔交易，跳过 ${skipped} 笔重复。`
        : `Merge done: ${imported} transactions added, ${skipped} duplicates skipped.`);
      recurringProcessedRef.current = true; // don't auto-generate right after import
      await loadAll();
    } catch (e) {
      console.error('importJSON error:', e);
      alert((lang === 'zh' ? '导入出错：' : 'Import error: ') + (e.message || e));
    }
  }

  // ── Auth gates ─────────────────────────────────────────────────────────────
  // Still checking session
  if (!authChecked) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 15, color: C.inkFaint, background: C.bg }}>
      <ThemeStyles mode={theme} key={theme} />
      {lang === 'zh' ? '载入中...' : 'Loading...'}
    </div>
  );

  // Not logged in → login/register screen
  if (!session) return <Auth lang={lang} onToggleLang={() => setLang(lang === 'zh' ? 'en' : 'zh')} theme={theme} onToggleTheme={toggleTheme} />;

  // Logged in but profile not yet loaded
  if (!profile) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 15, color: C.inkFaint, background: C.bg }}>
      <ThemeStyles mode={theme} key={theme} />
      {lang === 'zh' ? '载入账号...' : 'Loading account...'}
    </div>
  );

  // Account pending approval or disabled
  if (profile.status !== 'active') {
    const pending = profile.status === 'pending';
    return (
      <div className="em-mesh" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 20 }}>
        <ThemeStyles mode={theme} key={theme} />
        <div className="display" style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px' }}>
          <span style={{ marginRight: 4 }}>💰</span><span className="em-grad-text">EasyMoney</span>
        </div>
        <div className="em-pop" style={{ background: C.mode === 'dark' ? 'rgba(21,21,31,0.82)' : 'rgba(255,255,255,0.78)', backdropFilter: 'blur(20px)', borderRadius: 22, padding: '32px 40px', textAlign: 'center', maxWidth: 400, boxShadow: C.mode === 'dark' ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(91,69,217,0.16)', border: C.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.6)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>{pending ? '⏳' : '🔒'}</div>
          <div style={{ fontSize: 16, color: C.ink, marginBottom: 8, fontWeight: 600 }}>
            {pending
              ? (lang === 'zh' ? '账号等待管理员审核' : 'Awaiting admin approval')
              : (lang === 'zh' ? '账号已被停用' : 'Account disabled')}
          </div>
          <div style={{ fontSize: 13, color: C.inkFaint, marginBottom: 22 }}>
            {pending
              ? (lang === 'zh' ? '审核通过后即可开始使用。' : 'You can start once approved.')
              : (lang === 'zh' ? '如有疑问请联系管理员。' : 'Please contact the administrator.')}
          </div>
          <button type="button" onClick={signOut} className="em-btn-primary"
            style={{ padding: '10px 24px', background: C.grad, color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 13.5, fontWeight: 600, boxShadow: C.glow }}>
            {lang === 'zh' ? '退出登录' : 'Sign Out'}
          </button>
        </div>
      </div>
    );
  }

  // Admin panel
  if (showAdmin && profile.role === 'admin') {
    return <AdminPanel lang={lang} theme={theme} onBack={() => setShowAdmin(false)} onSignOut={signOut} />;
  }

  // ── Derived view state ───────────────────────────────────────────────────
  if (serverDown && !loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 44, marginBottom: 16 }}>⚠️</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 8 }}>
        {lang === 'zh' ? '服务器故障，请稍后再试。' : 'Server error, please try again later.'}
      </div>
      <div style={{ fontSize: 13.5, color: C.inkFaint, marginBottom: 22, maxWidth: 320, lineHeight: 1.6 }}>
        {lang === 'zh' ? '无法连接到服务器，你的数据是安全的。请稍后重试。' : 'Could not reach the server. Your data is safe. Please try again shortly.'}
      </div>
      <button type="button" onClick={() => loadAll()} style={{
        padding: '11px 28px', background: primaryFill(), color: primaryText(),
        border: 'none', borderRadius: 11, cursor: 'pointer', fontSize: 14.5, fontWeight: 600, boxShadow: C.glow,
      }}>
        {lang === 'zh' ? '重试' : 'Retry'}
      </button>
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 15, color: C.inkFaint }}>
      {lang === 'zh' ? '载入中...' : 'Loading...'}
    </div>
  );

  const acctTxns = selectedAccount ? transactions.filter(tx => tx.account_id === selectedAccount.id) : [];
  const currentAcct = selectedAccount ? accounts.find(a => a.id === selectedAccount.id) : null;
  const currentAcctCurrency = getCurrencyForAccount(currentAcct);

  // ── First-time setup wizard (choose currencies) ──
  if (needsSetup) {
    const toggleChoose = (code) => {
      setSetupChosen(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
    };
    return (
      <div className="em-mesh" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <ThemeStyles mode={theme} key={theme} />
        <div className="em-pop" style={{
          background: C.mode === 'dark' ? 'rgba(21,21,31,0.85)' : 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)',
          borderRadius: 22, padding: isMobile ? '26px 20px' : '34px 38px', width: '100%', maxWidth: 520,
          maxHeight: '92vh', overflow: 'auto',
          boxShadow: C.mode === 'dark' ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(91,69,217,0.16)',
          border: C.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.6)',
        }}>
          <div className="display" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 6 }}>
            <span style={{ marginRight: 4 }}>💰</span><span className="em-grad-text">EasyMoney</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginBottom: 4 }}>
            {lang === 'zh' ? '欢迎！先做个简单设置' : 'Welcome! A quick setup'}
          </div>
          <div style={{ fontSize: 13, color: C.inkFaint, marginBottom: 20 }}>
            {lang === 'zh' ? '选择你要使用的币种，并指定一个默认币种。' : 'Pick the currencies you use, and choose one as default.'}
          </div>

          {/* Step 1: choose which currencies to use */}
          <div style={{ fontSize: 12, color: C.inkSoft, fontWeight: 600, marginBottom: 8 }}>
            {lang === 'zh' ? '① 选择要使用的币种（可多选）' : '① Choose currencies (multiple)'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 22 }}>
            {WORLD_CURRENCIES.map(c => {
              const on = setupChosen.includes(c.code);
              return (
                <button key={c.code} type="button" onClick={() => toggleChoose(c.code)}
                  style={{
                    padding: '7px 12px', borderRadius: 10, cursor: 'pointer', fontSize: 12.5,
                    border: on ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                    background: on ? C.grad : C.card, color: on ? '#fff' : C.inkSoft,
                    fontWeight: on ? 600 : 500, boxShadow: on ? C.glow : 'none',
                  }}>
                  {on ? '✓ ' : ''}{c.symbol} {lang === 'zh' ? c.name_zh : c.name_en} ({c.code})
                </button>
              );
            })}
          </div>

          {/* Step 2: choose default among chosen */}
          <div style={{ fontSize: 12, color: C.inkSoft, fontWeight: 600, marginBottom: 8 }}>
            {lang === 'zh' ? '② 设为默认币种（必选）' : '② Default currency (required)'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
            {(setupChosen.length > 0 ? setupChosen : ['USD']).map(code => {
              const c = WORLD_CURRENCIES.find(x => x.code === code);
              if (!c) return null;
              const on = setupDefault === code;
              return (
                <button key={code} type="button" onClick={() => setSetupDefault(code)}
                  style={{
                    padding: '7px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 12.5,
                    border: on ? `1.5px solid ${C.primary}` : `1.5px solid ${C.border}`,
                    background: on ? C.gradSoft : C.card, color: on ? C.primary : C.inkSoft,
                    fontWeight: on ? 700 : 500,
                  }}>
                  {on ? '★ ' : ''}{c.symbol} {c.code}
                </button>
              );
            })}
          </div>

          <button type="button" disabled={setupSaving} onClick={completeSetup} className="em-btn-primary"
            style={{ width: '100%', padding: '13px', background: C.grad, color: '#fff', border: 'none', borderRadius: 12, cursor: setupSaving ? 'default' : 'pointer', fontSize: 14.5, fontWeight: 600, boxShadow: C.glow, opacity: setupSaving ? 0.6 : 1 }}>
            {setupSaving ? (lang === 'zh' ? '保存中...' : 'Saving...') : (lang === 'zh' ? '完成，开始使用' : 'Done, start')}
          </button>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const navItems = [['overview', t.overview], ['transactions', t.transactions], ['budget', t.budget], ['reports', t.reports], ['settings', t.settings]];
  // 记账精简：统计分类/账户使用频率（用于"最常用"排序），最近200笔即可
  const catUseCount = {};
  const acctUseCount = {};
  for (const tx of transactions.slice(0, 200)) {
    if (tx.category_id) catUseCount[tx.category_id] = (catUseCount[tx.category_id] || 0) + 1;
    if (tx.account_id) acctUseCount[tx.account_id] = (acctUseCount[tx.account_id] || 0) + 1;
  }
  // 记账弹窗的分类 chips（父分类 + "父·子"），只构建一次，渲染和数量判断共用
  const txnCatChips = (() => {
    const matchType = c => c.type === txnType || c.type === 'both';
    const list = [];
    categories.filter(c => !c.parent_id).forEach(p => {
      if (matchType(p)) list.push({ id: p.id, label: lang === 'zh' ? p.name_zh : p.name_en, cat: p });
      categories.filter(c => c.parent_id === p.id).forEach(ch => {
        if (matchType(p)) {
          const pn = lang === 'zh' ? p.name_zh : p.name_en;
          const cn = lang === 'zh' ? ch.name_zh : ch.name_en;
          list.push({ id: ch.id, label: `${pn}·${cn}`, cat: ch });
        }
      });
    });
    return list;
  })();
  // 新手引导步骤：0=不显示；1=还没账户(引导加账户)；2=有账户但没交易(引导记第一笔)
  // 老用户(已有账户+交易)天然=0，不被打扰；完成/跳过后 onboardDone=true 也=0
  let onboardStep = 0;
  if (!onboardDone && !loading) {
    if (accounts.length === 0) onboardStep = 1;
    else if (transactions.length === 0) onboardStep = 2;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontSize: 14, overflow: 'hidden', background: C.bg, color: C.ink }}>
      <ThemeStyles mode={theme} key={theme} />
      {/* iOS 修复：输入框字号 <16px 时 Safari 会自动放大页面，关闭弹窗后缩放不恢复 →
          页面右边被挤出屏幕("走位")。这里强制手机上输入框 ≥16px，从根上避免触发自动放大。
          同时兜底禁止横向溢出。 */}
      <style>{`
        @media (max-width: 820px) {
          input, select, textarea {
            font-size: 16px !important;
          }
        }
        html, body {
          max-width: 100%;
          overflow-x: hidden;
        }
      `}</style>

      {/* ══ Top navigation bar (桌面 + 手机通用) ══ */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        padding: isMobile ? '8px 12px' : '0 18px', height: isMobile ? 'auto' : 56,
        background: C.card, borderBottom: `1px solid ${C.border}`, position: 'relative', zIndex: 20,
      }}>
        <div className="display" style={{ fontWeight: 800, fontSize: isMobile ? 18 : 17, display: 'flex', alignItems: 'center', gap: 5, marginRight: isMobile ? 8 : 14, whiteSpace: 'nowrap' }}>
          <span>💰</span>{!isMobile && <span className="em-grad-text">EasyMoney</span>}
        </div>
        {/* 导航项横排 */}
        <div style={{ display: 'flex', gap: 2, flex: 1, overflowX: 'auto' }}>
          {navItems.map(([key, label]) => {
            const active = tab === key && !selectedAccount;
            return (
              <div key={key} onClick={() => { setTab(key); setSelectedAccount(null); setShowTopMenu(false); }}
                style={{
                  padding: isMobile ? '8px 10px' : '8px 16px', borderRadius: 10, cursor: 'pointer',
                  fontSize: isMobile ? 12.5 : 13.5, fontWeight: active ? 700 : 500,
                  color: active ? '#fff' : C.inkSoft, background: active ? C.grad : 'transparent',
                  boxShadow: active ? C.glow : 'none', whiteSpace: 'nowrap', textAlign: 'center',
                  flex: isMobile ? 1 : 'none', transition: 'all 0.15s ease',
                }}>
                {label}
              </div>
            );
          })}
        </div>
        {/* 右侧：使用说明 + 语言/主题/退出（桌面）｜ 📖 + ⋯菜单（手机） */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
          {!isMobile ? (
            <>
              <button type="button" onClick={() => setShowGuide(true)}
                style={{ padding: '7px 13px', border: `1.5px solid ${C.primary}`, color: C.primary, background: C.gradSoft, borderRadius: 9, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                📖 {lang === 'zh' ? '使用说明' : 'Guide'}
              </button>
              <button type="button" onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')}
                style={{ width: 34, height: 34, borderRadius: 9, border: `1.5px solid ${C.border}`, background: C.card, fontSize: 12, color: C.inkSoft, fontWeight: 600, cursor: 'pointer' }}>
                {lang === 'zh' ? 'EN' : '中'}
              </button>
              <button type="button" onClick={toggleTheme}
                style={{ width: 34, height: 34, borderRadius: 9, border: `1.5px solid ${C.border}`, background: C.card, fontSize: 14, cursor: 'pointer' }}>
                {theme === 'light' ? '🌙' : '☀️'}
              </button>
              {profile?.role === 'admin' && (
                <button type="button" onClick={() => setShowAdmin(true)}
                  style={{ width: 34, height: 34, borderRadius: 9, border: `1.5px solid ${C.border}`, background: C.card, fontSize: 14, cursor: 'pointer' }} title={lang === 'zh' ? '管理后台' : 'Admin'}>
                  ⚙
                </button>
              )}
              <button type="button" onClick={signOut}
                style={{ width: 34, height: 34, borderRadius: 9, border: `1.5px solid ${C.border}`, background: C.card, fontSize: 14, color: C.expense, cursor: 'pointer' }} title={lang === 'zh' ? '退出登录' : 'Sign Out'}>
                ⎋
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => setShowGuide(true)}
                style={{ width: 32, height: 32, borderRadius: 9, border: `1.5px solid ${C.border}`, background: C.card, fontSize: 14, cursor: 'pointer' }} title={lang === 'zh' ? '使用说明' : 'Guide'}>
                📖
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setShowTopMenu(v => !v); }}
                style={{ width: 32, height: 32, borderRadius: 9, border: `1.5px solid ${C.border}`, background: C.card, fontSize: 16, color: C.inkSoft, cursor: 'pointer' }}>
                ⋯
              </button>
              {showTopMenu && (
                <div onClick={(e) => e.stopPropagation()}
                  style={{ position: 'absolute', top: 40, right: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 6, minWidth: 160, zIndex: 40 }}>
                  <div onClick={() => { setLang(lang === 'zh' ? 'en' : 'zh'); setShowTopMenu(false); }}
                    style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: C.inkSoft }}>
                    🌐 {lang === 'zh' ? '切换到 English' : '切换到中文'}
                  </div>
                  <div onClick={() => { toggleTheme(); setShowTopMenu(false); }}
                    style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: C.inkSoft }}>
                    {theme === 'light' ? '🌙' : '☀️'} {lang === 'zh' ? '切换主题' : 'Toggle theme'}
                  </div>
                  {profile?.role === 'admin' && (
                    <div onClick={() => { setShowAdmin(true); setShowTopMenu(false); }}
                      style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: C.inkSoft }}>
                      ⚙ {lang === 'zh' ? '管理后台' : 'Admin'}
                    </div>
                  )}
                  <div onClick={() => { setShowTopMenu(false); signOut(); }}
                    style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: C.expense }}>
                    ⎋ {lang === 'zh' ? '退出登录' : 'Sign Out'}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ══ 下方：侧栏 + 主区（横向） ══ */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }} onClick={() => showTopMenu && setShowTopMenu(false)}>

      {/* ── Sidebar (hidden on mobile) ── */}
      <div style={{ width: sidebarWidth, background: C.card, display: isMobile ? 'none' : 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'auto' }}>
        <div className="display" style={{ padding: '16px 18px 6px', fontWeight: 700, fontSize: 14, color: C.ink, letterSpacing: '-0.2px' }}>{t.myAccounts}</div>

        {renderAccountGroups()}

        <div style={{ padding: '12px 14px' }}>
          <button type="button" onClick={() => setShowAddAccount(true)} className="em-btn-ghost"
            style={{ ...S.btn(), width: '100%', fontSize: 12.5, padding: '9px 0',
              ...(onboardStep === 1 ? { border: `2px solid ${C.primary}`, color: C.primary, fontWeight: 700, boxShadow: C.glow } : {}) }}>
            {onboardStep === 1 ? (lang === 'zh' ? '👉 ' : '👉 ') : ''}{t.addAccount}
          </button>
          {isPro && (
            <button type="button" disabled={plaidBusy} onClick={() => connectBank()}
              style={{ width: '100%', marginTop: 8, padding: '9px 0', background: primaryFill(), color: primaryText(), border: 'none', borderRadius: 10, cursor: plaidBusy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 600, opacity: plaidBusy ? 0.6 : 1 }}>
              {plaidBusy ? (lang === 'zh' ? '处理中...' : 'Working...') : (lang === 'zh' ? '🏦 连接银行' : '🏦 Connect Bank')}
            </button>
          )}
        </div>

        <div style={{ marginTop: 'auto', padding: 14, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.inkFaint, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {profile?.email}
          </div>
        </div>
      </div>

      {/* ── Sidebar resize handle ── */}
      {!isMobile && (
        <div
          onMouseDown={e => {
            sidebarDragging.current = true;
            sidebarDragX.current = e.clientX;
            sidebarDragW.current = sidebarWidth;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            e.preventDefault();
          }}
          title={lang === 'zh' ? '拖动调整宽度' : 'Drag to resize'}
          style={{
            width: 6, flexShrink: 0, cursor: 'col-resize', position: 'relative', zIndex: 5,
            background: `linear-gradient(to right, transparent 33%, ${C.border} 33%, ${C.border} 67%, transparent 67%)`,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = `linear-gradient(to right, transparent 25%, ${C.primary} 25%, ${C.primary} 75%, transparent 75%)`;
          }}
          onMouseLeave={e => {
            if (!sidebarDragging.current)
              e.currentTarget.style.background = `linear-gradient(to right, transparent 33%, ${C.border} 33%, ${C.border} 67%, transparent 67%)`;
          }}
        />
      )}

      {/* ── Main content ── */}
      <div style={{ flex: 1, overflow: 'auto', background: C.bg, paddingBottom: isMobile ? 16 : 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '14px 16px' : '18px 28px', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, background: C.mode === 'dark' ? 'rgba(12,12,20,0.80)' : 'rgba(244,243,251,0.85)', backdropFilter: 'blur(12px)', zIndex: 10 }}>
          <div className="display" style={{ fontWeight: isMobile ? 800 : 700, fontSize: isMobile ? 16 : 18, letterSpacing: '-0.3px' }}>
            {selectedAccount ? currentAcct?.name || selectedAccount.name
              : tab === 'overview' ? t.overview
              : tab === 'transactions' ? t.transactions
              : tab === 'reports' ? t.reports : tab === 'budget' ? t.budget : t.settings}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {(tab === 'overview' || tab === 'transactions' || tab === 'account') && !isMobile && (
              <button type="button" onClick={() => {
                setShowExtras(false);
                setScanMsg('');
              setTxnType('expense');
              if (tab === 'account' && selectedAccount) {
                setAcctId(selectedAccount.id);
              } else {
                // Remember last used account
                let last = '';
                try { last = localStorage.getItem('em_last_account') || ''; } catch {}
                // Only use it if that account still exists
                setAcctId(accounts.find(a => a.id === last) ? last : '');
              }
              setShowModal(true);
            }} className="em-btn-primary" style={S.btn(true)}>{t.addTxn}</button>
          )}
          </div>
        </div>

        <div style={{ padding: isMobile ? 14 : 28 }} className="em-fade-up">
          {/* 手机版总览页：账户分组（单页滚动，紧跟在收支统计下方） 见 Overview 区块内 */}
          {(() => {
            const dupCount = transactions.filter(tx => tx.possible_duplicate).length;
            if (dupCount === 0) return null;
            return (
              <div style={{ background: C.warnSoft, border: `1px solid ${C.warn}`, borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13.5, color: C.ink }}>
                  ⚠️ {lang === 'zh' ? `发现 ${dupCount} 笔疑似重复交易` : `${dupCount} possible duplicate transaction(s) found`}
                </div>
                <button type="button" onClick={() => setShowDupReview(true)}
                  style={{ padding: '7px 16px', background: C.warn, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {lang === 'zh' ? '检查重复' : 'Review'}
                </button>
              </div>
            );
          })()}
          {recurringMsg && (
            <div style={{ background: C.gradSoft, border: `1px solid ${C.primary}`, borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: C.primary, fontWeight: 600 }}>
              🔁 {recurringMsg}
            </div>
          )}

          {/* ── Overview ── */}
          {tab === 'overview' && !selectedAccount && (
            <div>
              {/* ── 新手引导卡片（没账户/没交易时显示） ── */}
              {onboardStep > 0 && (
                <div style={{
                  background: C.card, border: `2px solid ${C.primary}`, borderRadius: 18,
                  padding: isMobile ? '20px 18px' : '26px 28px', marginBottom: isMobile ? 14 : 20,
                  boxShadow: C.glow, position: 'relative',
                }}>
                  <button type="button" onClick={finishOnboard}
                    style={{ position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: C.inkFaint }}>
                    {lang === 'zh' ? '跳过' : 'Skip'}
                  </button>
                  {/* 步骤指示 */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                    {[1, 2].map(s => (
                      <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: s <= onboardStep ? C.primary : C.border }} />
                    ))}
                  </div>
                  {onboardStep === 1 ? (
                    <>
                      <div className="display" style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, marginBottom: 6 }}>
                        👋 {lang === 'zh' ? '欢迎使用 EasyMoney！' : 'Welcome to EasyMoney!'}
                      </div>
                      <div style={{ fontSize: 13.5, color: C.inkSoft, lineHeight: 1.6, marginBottom: 16 }}>
                        {lang === 'zh'
                          ? '记账第一步：先添加一个账户（比如现金、银行卡、信用卡）。之后你就能在里面记录收支了。'
                          : 'Step 1: Add an account (cash, bank card, or credit card). Then you can start recording.'}
                      </div>
                      <button type="button" onClick={() => setShowAddAccount(true)}
                        style={{ width: '100%', padding: '13px 0', background: primaryFill(), color: primaryText(), border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: C.glow }}>
                        {lang === 'zh' ? '＋ 添加我的第一个账户' : '＋ Add my first account'}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="display" style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, marginBottom: 6 }}>
                        ✅ {lang === 'zh' ? '账户已添加！' : 'Account added!'}
                      </div>
                      <div style={{ fontSize: 13.5, color: C.inkSoft, lineHeight: 1.6, marginBottom: 16 }}>
                        {lang === 'zh'
                          ? '第二步：记下你的第一笔交易吧，比如今天买了什么、收到了什么钱。'
                          : 'Step 2: Record your first transaction — something you bought or money you received.'}
                      </div>
                      <button type="button" onClick={() => {
                        setShowExtras(false); setScanMsg(''); setTxnType('expense');
                        const cashLike = accounts.find(a => ['checking', 'savings', 'cash'].includes(a.account_type));
                        const firstNonCredit = accounts.find(a => a.account_type !== 'credit');
                        setAcctId((cashLike || firstNonCredit || accounts[0])?.id || '');
                        setShowModal(true);
                      }}
                        style={{ width: '100%', padding: '13px 0', background: primaryFill(), color: primaryText(), border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: C.glow }}>
                        {lang === 'zh' ? '＋ 记第一笔账' : '＋ Record first transaction'}
                      </button>
                    </>
                  )}
                </div>
              )}
              {/* Assets by currency */}
              <div style={{ background: C.grad, borderRadius: 20, padding: isMobile ? '20px 22px' : '24px 26px', marginBottom: isMobile ? 12 : 20, boxShadow: C.glow, color: '#fff', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: -40, right: -20, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.10)' }} />
                <div style={{ position: 'absolute', bottom: -60, right: 80, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                {isMobile ? (
                  <div style={{ position: 'relative' }}>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginBottom: 6, fontWeight: 500 }}>{t.totalByDefault} ({defaultCurrency.code})</div>
                    <div className="display" style={{ fontSize: 30, fontWeight: 800, color: '#fff', letterSpacing: '-1px', marginBottom: 12 }}>
                      {defaultCurrency.symbol}{totalBalanceInDefault.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {Object.entries(balanceByCurrency).map(([code, bal]) => {
                        const curr = currencies.find(c => c.code === code);
                        return (
                          <div key={code} style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                            {curr?.symbol || code}{bal.toLocaleString()}
                            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginLeft: 3 }}>{code}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, position: 'relative' }}>
                  <div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginBottom: 8, fontWeight: 500 }}>{t.assetsByCurrency}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                      {Object.entries(balanceByCurrency).map(([code, bal]) => {
                        const curr = currencies.find(c => c.code === code);
                        return (
                          <div key={code} style={{ fontSize: 15, fontWeight: 600 }}>
                            {curr?.symbol || code}{bal.toLocaleString()}
                            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginLeft: 4 }}>{code}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginBottom: 6, fontWeight: 500 }}>{t.totalByDefault} ({defaultCurrency.code})</div>
                    <div className="display" style={{ fontSize: 30, fontWeight: 800, color: '#fff', letterSpacing: '-1px' }}>
                      {defaultCurrency.symbol}{totalBalanceInDefault.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Monthly stats */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: isMobile ? 10 : 14, marginBottom: isMobile ? 12 : 24 }}>
                {[
                  { label: t.monthlyIncome, type: 'income', totalColor: C.income },
                  { label: t.monthlyExpense, type: 'expense', totalColor: C.expense },
                  { label: t.monthlySurplus, type: 'surplus', totalColor: monthIncome - monthExpense >= 0 ? C.income : C.expense },
                ].map(s => {
                  const byCurrency = {};
                  monthTxns.filter(tx => {
                    if (isInvestmentTrade(tx)) return false; // asset conversion, exclude from all stats
                    return s.type === 'surplus' ? tx.type !== 'transfer' : tx.type === s.type;
                  }).forEach(tx => {
                    const acct = accounts.find(a => a.id === tx.account_id);
                    const curr = getCurrencyForAccount(acct);
                    if (s.type === 'surplus')
                      byCurrency[curr.code] = (byCurrency[curr.code] || 0) + (tx.type === 'income' ? parseFloat(tx.amount) : -parseFloat(tx.amount));
                    else
                      byCurrency[curr.code] = (byCurrency[curr.code] || 0) + parseFloat(tx.amount);
                  });
                  const total = s.type === 'income' ? monthIncome : s.type === 'expense' ? monthExpense : monthIncome - monthExpense;
                  const multiCurrency = Object.keys(byCurrency).length > 1;
                  return (
                    <div key={s.label} style={{
                      background: C.card, borderRadius: isMobile ? 14 : 18, padding: isMobile ? '13px 15px' : '18px 20px',
                      boxShadow: C.shadowMd, border: `1px solid ${C.border}`,
                      gridColumn: isMobile && s.type === 'surplus' ? '1 / -1' : 'auto',
                    }}>
                      <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 6 }}>{s.label}</div>
                      {Object.entries(byCurrency).map(([code, amt]) => {
                        const curr = currencies.find(c => c.code === code);
                        return (
                          <div key={code} style={{ fontSize: isMobile ? 17 : 14, fontWeight: 600, color: s.type === 'expense' ? C.expense : amt >= 0 ? C.income : C.expense }}>
                            {curr?.symbol || code}{Math.abs(amt).toLocaleString()}
                            <span style={{ fontSize: 11, color: C.inkFaint, marginLeft: 4 }}>{code}</span>
                          </div>
                        );
                      })}
                      {Object.keys(byCurrency).length === 0 && (
                        <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 600, color: s.totalColor }}>{defaultCurrency.symbol}0</div>
                      )}
                      {/* On mobile: only show the "≈ converted" line when there are multiple currencies (otherwise it's redundant) */}
                      {multiCurrency && (
                        <div style={{ fontSize: isMobile ? 11 : 12, color: C.inkFaint, marginTop: 6, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
                          ≈ {defaultCurrency.symbol}{total.toLocaleString(undefined, { maximumFractionDigits: 2 })} {defaultCurrency.code}
                        </div>
                      )}
                      {!multiCurrency && Object.keys(byCurrency).length === 1 && !isMobile && (
                        <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2 }}>
                          ≈ {defaultCurrency.symbol}{total.toLocaleString(undefined, { maximumFractionDigits: 2 })} {defaultCurrency.code}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Mobile-only: 账户分组（可折叠），紧跟在收支统计下方（侧栏在手机隐藏） */}
              {isMobile && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '6px 4px 12px', marginBottom: 22, boxShadow: C.shadowMd }}>
                  <div className="display" style={{ fontSize: 15, color: C.ink, fontWeight: 700, padding: '10px 14px 4px', letterSpacing: '-0.2px' }}>{t.myAccounts}</div>
                  {renderAccountGroups()}
                  <div style={{ padding: '10px 14px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button type="button" onClick={() => setShowAddAccount(true)} className="em-btn-ghost"
                      style={{ ...S.btn(), width: '100%', padding: '10px 0' }}>{t.addAccount}</button>
                    {isPro && (
                      <button type="button" disabled={plaidBusy} onClick={() => connectBank()}
                        style={{ width: '100%', padding: '10px 0', background: primaryFill(), color: primaryText(), border: 'none', borderRadius: 10, cursor: plaidBusy ? 'default' : 'pointer', fontSize: 13, fontWeight: 600, opacity: plaidBusy ? 0.6 : 1 }}>
                        {plaidBusy ? (lang === 'zh' ? '处理中...' : 'Working...') : (lang === 'zh' ? '🏦 连接银行自动导入' : '🏦 Connect Bank')}
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div style={{ background: C.card, borderRadius: 18, padding: 20, marginBottom: 22, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontWeight: 500 }}>{t.incomeExpenseTrend}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, color: C.inkSoft }}>{t.monthRangeEnd}</span>
                    <input type="month" value={chartEndMonth} onChange={e => setChartEndMonth(e.target.value)}
                      style={{ padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, cursor: 'pointer', background: C.card, color: C.ink }} />
                  </div>
                </div>
                <LineChartSVG data={chartData} lang={lang} currency={defaultCurrency} t={t} />
              </div>

              <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 500, marginBottom: 12 }}>{t.recentTxn}</div>
                <TxnTable txns={transactions.slice(0, 8)} t={t} lang={lang}
                  defaultCurrency={defaultCurrency} currencies={currencies}
                  categories={categories} accounts={accounts}
                  getCatName={getCatName} getAcctName={getAcctName} getCategoryOptions={getCategoryOptions}
                  onDelete={deleteTransaction} onSave={saveEditTxn} onEditMobile={(t) => setEditTxnMobile({ ...t, _original: { type: t.type, transfer_to_account_id: t.transfer_to_account_id, amount: t.amount, account_id: t.account_id, is_transfer_out: t.is_transfer_out } })} onConfirmPending={confirmPending}
                  editingTxn={editingTxn} setEditingTxn={setEditingTxn}
                  cryptoPrices={cryptoPrices} showAccount hideBalance initialBalance={0} />
              </div>
            </div>
          )}

          {/* ── Transactions ── */}
          {tab === 'transactions' && !selectedAccount && (
            <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
              <TxnTable txns={transactions} t={t} lang={lang}
                defaultCurrency={defaultCurrency} currencies={currencies}
                categories={categories} accounts={accounts}
                getCatName={getCatName} getAcctName={getAcctName} getCategoryOptions={getCategoryOptions}
                onDelete={deleteTransaction} onSave={saveEditTxn} onEditMobile={(t) => setEditTxnMobile({ ...t, _original: { type: t.type, transfer_to_account_id: t.transfer_to_account_id, amount: t.amount, account_id: t.account_id, is_transfer_out: t.is_transfer_out } })} onConfirmPending={confirmPending}
                editingTxn={editingTxn} setEditingTxn={setEditingTxn}
                cryptoPrices={cryptoPrices} showAccount hideBalance initialBalance={0} />
            </div>
          )}

          {/* ── Account detail ── */}
          {tab === 'account' && selectedAccount && (
            <div>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 14 : 0, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ width: 12, height: 12, borderRadius: '50%', background: currentAcct?.color, flexShrink: 0 }} />
                  <div>
                    <span style={{ fontSize: 22, fontWeight: 500 }}>
                      {currentAcctCurrency.symbol}{acctBalance(currentAcct).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                    {currentAcct?.account_type === 'credit' && (
                      <div style={{ fontSize: 12, color: C.inkSoft, marginTop: 2 }}>
                        {t.availableCredit}
                        {currentAcct?.credit_limit != null && (
                          <span style={{ marginLeft: 8 }}>
                            {t.owed}: <span style={{ color: C.expense }}>
                              {currentAcctCurrency.symbol}{(parseFloat(currentAcct.credit_limit) - acctBalance(currentAcct)).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </span>
                            <span style={{ marginLeft: 8, color: '#bbb' }}>
                              / {t.creditLimit} {currentAcctCurrency.symbol}{parseFloat(currentAcct.credit_limit).toLocaleString()}
                            </span>
                          </span>
                        )}
                      </div>
                    )}
                    {currentAcct?.account_type === 'crypto' && currentAcct?.crypto_amount && (
                      <div style={{ fontSize: 12, color: C.inkSoft, marginTop: 2 }}>
                        {parseFloat(currentAcct.crypto_amount).toLocaleString()} {currentAcct.crypto_ticker || currentAcct.crypto_symbol?.toUpperCase()}
                        {cryptoPrices[currentAcct.crypto_symbol] && (
                          <span style={{ marginLeft: 8, color: C.income }}>
                            @ {currentAcctCurrency.symbol}{cryptoPrices[currentAcct.crypto_symbol].toLocaleString()} {t.perCoin}
                          </span>
                        )}
                      </div>
                    )}
                    {currentAcct?.account_number && (
                      <div style={{ fontSize: 12, color: C.inkFaint, marginTop: 2, letterSpacing: '1px' }}>
                        {maskNumber(currentAcct.account_number)}
                      </div>
                    )}
                  </div>
                  <select value={currentAcct?.currency_code || defaultCurrency.code}
                    onChange={async e => { await supabase.from('accounts').update({ currency_code: e.target.value }).eq('id', selectedAccount.id); loadAll(); }}
                    style={{ fontSize: 12, padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', background: C.card, color: C.ink }}>
                    {currencies.map(c => (
                      <option key={c.code} value={c.code}>{c.symbol} {lang === 'zh' ? c.name_zh : c.name_en} ({c.code})</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => openEditAccount(currentAcct)}
                    style={{ flex: isMobile ? 1 : 'none', padding: '8px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12, color: C.inkSoft, whiteSpace: 'nowrap' }}>
                    ✎ {lang === 'zh' ? '编辑' : 'Edit'}
                  </button>
                  <button type="button" onClick={() => exportCSV(acctTxns, `${currentAcct?.name}_${new Date().toISOString().split('T')[0]}.csv`)}
                    style={{ flex: isMobile ? 1 : 'none', padding: '8px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12, color: C.ink, whiteSpace: 'nowrap' }}>
                    📊 {lang === 'zh' ? '导出' : 'Export'}
                  </button>
                  <label style={{ flex: isMobile ? 1 : 'none', padding: '8px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12, color: C.ink, whiteSpace: 'nowrap', textAlign: 'center' }}>
                    📂 {lang === 'zh' ? '导入' : 'Import'}
                    <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => e.target.files[0] && importCSV(e.target.files[0])} />
                  </label>
                  <button type="button" onClick={() => { setBatchCat({ accountId: selectedAccount.id }); setBatchSelected([]); setBatchCatId(''); }}
                    style={{ flex: isMobile ? 1 : 'none', padding: '8px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12, color: C.ink, whiteSpace: 'nowrap' }}>
                    🏷️ {lang === 'zh' ? '批量分类' : 'Categorize'}
                  </button>
                  <button type="button" onClick={() => deleteAccount(selectedAccount.id)} style={{ ...S.btn(), flex: isMobile ? 1 : 'none', padding: '8px 12px', fontSize: 12, color: C.expense, borderColor: C.expense, whiteSpace: 'nowrap' }}>{t.delete}</button>
                </div>
              </div>

              {/* Per-account bank sync (Plaid) — only shows if THIS account is linked to a bank */}
              {isPro && currentAcct && (() => {
                const linkedItem = plaidItems.find(p => p.account_id === selectedAccount.id);
                if (!linkedItem) return null; // not linked → nothing here (connect from accounts overview)
                return (
                  <div style={{ background: C.card, borderRadius: 12, padding: '12px 16px', border: `1px solid ${C.border}`, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, color: C.inkSoft, display: 'flex', alignItems: 'center', gap: 8 }}>
                      🏦 <span>{lang === 'zh' ? '已连接：' : 'Connected: '}<b style={{ color: C.ink }}>{linkedItem.institution_name || (lang === 'zh' ? '银行' : 'Bank')}</b></span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" disabled={plaidBusy} onClick={() => updateBankAccounts(linkedItem.item_id, linkedItem.institution_name)}
                        style={{ padding: '7px 14px', background: C.gradSoft, color: C.primary, border: `1.5px solid ${C.primary}`, borderRadius: 8, cursor: plaidBusy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 600, opacity: plaidBusy ? 0.6 : 1 }}
                        title={lang === 'zh' ? '如果这个银行还有账户没导入，点这里补充（不消耗名额）' : 'Add accounts you missed (no extra slot used)'}>
                        {lang === 'zh' ? '➕ 补充账户' : '➕ Add accounts'}
                      </button>
                      <button type="button" disabled={plaidBusy} onClick={() => syncBank(selectedAccount.id)}
                        style={{ padding: '7px 16px', background: primaryFill(), color: primaryText(), border: 'none', borderRadius: 8, cursor: plaidBusy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 600, opacity: plaidBusy ? 0.6 : 1 }}>
                        {plaidBusy ? (lang === 'zh' ? '同步中...' : 'Syncing...') : (lang === 'zh' ? '🔄 同步交易' : '🔄 Sync')}
                      </button>
                    </div>
                  </div>
                );
              })()}
              {currentAcct?.account_type === 'crypto' && !currentAcct?.crypto_symbol ? (
                <CryptoHoldingsPanel
                  account={currentAcct} holdings={cryptoHoldings.filter(h => h.account_id === selectedAccount.id)}
                  cryptoPrices={cryptoPrices} t={t} lang={lang} isMobile={isMobile}
                  currency={defaultCurrency}
                  trades={acctTxns}
                  onAdd={() => { setCryptoModalMode('add'); setCryptoModalHolding(null); setCryptoForm({ coin_id: '', ticker: '', coin_name: '', amount: '', price: '', total: '' }); setHoldingSearchResults([]); setShowCryptoModal(true); }}
                  onBuy={(h) => { setCryptoModalMode('buy'); setCryptoModalHolding(h); setCryptoForm({ coin_id: h.coin_id, ticker: h.ticker || '', coin_name: h.coin_name || '', amount: '', price: '', total: '' }); setShowCryptoModal(true); }}
                  onSell={(h) => { setCryptoModalMode('sell'); setCryptoModalHolding(h); setCryptoForm({ coin_id: h.coin_id, ticker: h.ticker || '', coin_name: h.coin_name || '', amount: '', price: '', total: '' }); setShowCryptoModal(true); }}
                  onDeleteHolding={deleteCryptoHolding}
                  onRefresh={() => loadAll()}
                  onDeleteTrade={deleteTransaction}
                />
              ) : currentAcct?.account_type === 'stock' ? (
                <StockHoldingsPanel
                  account={currentAcct} holdings={stockHoldings.filter(h => h.account_id === selectedAccount.id)}
                  stockPrices={stockPrices} t={t} lang={lang} isMobile={isMobile}
                  currency={currentAcctCurrency}
                  trades={acctTxns}
                  getCatName={getCatName}
                  onAdd={() => { setStockModalMode('add'); setStockModalHolding(null); setStockForm({ symbol: '', company: '', market: 'US', shares: '', price: '' }); setShowStockModal(true); }}
                  onBuy={(h) => { setStockModalMode('buy'); setStockModalHolding(h); setStockForm({ symbol: h.symbol, company: h.company_name || '', market: h.market, shares: '', price: '' }); setShowStockModal(true); }}
                  onSell={(h) => { setStockModalMode('sell'); setStockModalHolding(h); setStockForm({ symbol: h.symbol, company: h.company_name || '', market: h.market, shares: '', price: '' }); setShowStockModal(true); }}
                  onDeleteHolding={deleteStockHolding}
                  onRefresh={() => fetchStockPrices(stockHoldings.filter(h => h.account_id === selectedAccount.id))}
                  onUpdateManualPrice={updateManualStockPrice}
                  onDeposit={() => { const v = window.prompt(lang === 'zh' ? '存入金额' : 'Deposit amount'); const n = parseFloat(v); if (!isNaN(n) && n > 0) adjustStockCash(currentAcct, n, true); }}
                  onWithdraw={() => { const v = window.prompt(lang === 'zh' ? '取出金额' : 'Withdraw amount'); const n = parseFloat(v); if (!isNaN(n) && n > 0) adjustStockCash(currentAcct, -n, false); }}
                  onDeleteTrade={deleteTransaction}
                />
              ) : (
              <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <TxnTable txns={acctTxns} t={t} lang={lang}
                  defaultCurrency={currentAcctCurrency} currencies={currencies}
                  categories={categories} accounts={accounts}
                  getCatName={getCatName} getAcctName={getAcctName} getCategoryOptions={getCategoryOptions}
                  onDelete={deleteTransaction} onSave={saveEditTxn} onEditMobile={(t) => setEditTxnMobile({ ...t, _original: { type: t.type, transfer_to_account_id: t.transfer_to_account_id, amount: t.amount, account_id: t.account_id, is_transfer_out: t.is_transfer_out } })} onConfirmPending={confirmPending}
                  editingTxn={editingTxn} setEditingTxn={setEditingTxn}
                  cryptoPrices={cryptoPrices}
                  initialBalance={acctBalance(currentAcct)}
                  onInlineAdd={loadAll}
                  inlineAccountId={selectedAccount?.id}
                  inlineIsCrypto={currentAcct?.account_type === 'crypto'}
                  inlineCryptoSymbol={currentAcct?.crypto_symbol}
                  inlineCryptoTicker={currentAcct?.crypto_ticker}
                  isCreditAccount={currentAcct?.account_type === 'credit'} />
              </div>
              )}
            </div>
          )}

          {/* ── Reports ── */}
          {tab === 'budget' && !selectedAccount && (
            <BudgetView
              budgets={budgets} categories={categories} t={t} lang={lang} isMobile={isMobile}
              defaultCurrency={defaultCurrency} getCatName={getCatName} budgetSpent={budgetSpent}
              budgetTxns={budgetTxns} accounts={accounts} getAcctName={getAcctName}
              getCurrencyForAccount={getCurrencyForAccount} convertToDefault={convertToDefault}
              onAdd={() => { setEditingBudget(null); setBudgetForm({ name: '', category_id: '', amount: '', period: 'month' }); setShowBudgetModal(true); }}
              onEdit={(b) => { setEditingBudget(b); setBudgetForm({ name: b.name || '', category_id: b.category_id || '', amount: String(b.amount), period: b.period }); setShowBudgetModal(true); }}
              onDelete={deleteBudget}
              onGoReports={() => setTab('reports')}
            />
          )}

          {tab === 'reports' && !selectedAccount && (
            <ReportsView
              transactions={transactions} accounts={accounts} categories={categories}
              t={t} lang={lang} isMobile={isMobile}
              defaultCurrency={defaultCurrency} convertToDefault={convertToDefault}
              getCurrencyForAccount={getCurrencyForAccount} getCatName={getCatName}
              reportRange={reportRange} setReportRange={setReportRange}
            />
          )}

          {/* ── Settings ── */}
          {tab === 'settings' && !selectedAccount && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Mobile-only: account/admin/signout (since sidebar is hidden) */}
              {isMobile && (
                <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                  <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 13, color: C.inkSoft }}>{profile?.email}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <button type="button" onClick={() => setShowAddAccount(true)} className="em-btn-ghost"
                      style={{ ...S.btn(), width: '100%', padding: '11px 0' }}>{t.addAccount}</button>
                    {profile?.role === 'admin' && (
                      <button type="button" onClick={() => setShowAdmin(true)}
                        style={{ width: '100%', padding: '11px 0', border: 'none', borderRadius: 12, cursor: 'pointer', background: C.gradSoft, fontSize: 13.5, color: C.primary, fontWeight: 600 }}>
                        {lang === 'zh' ? '⚙ 管理后台' : '⚙ Admin'}
                      </button>
                    )}
                    <button type="button" onClick={signOut}
                      style={{ width: '100%', padding: '11px 0', border: `1.5px solid ${C.border}`, borderRadius: 12, cursor: 'pointer', background: C.card, fontSize: 13.5, color: C.expense, fontWeight: 500 }}>
                      {lang === 'zh' ? '退出登录' : 'Sign Out'}
                    </button>
                  </div>
                </div>
              )}

              {/* Recurring transactions */}
              <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontWeight: 600 }}>🔁 {t.recurring}</div>
                  <button type="button" onClick={() => {
                    setEditingRecurring(null);
                    setRecurForm({ type: 'expense', amount: '', account_id: accounts[0]?.id || '', transfer_to_account_id: '', category_id: '', description: '', note: '', period_type: 'monthly', period_day: 1, period_month: 1, advance_days: 0 });
                    setShowRecurringModal(true);
                  }} className="em-btn-primary" style={{ ...S.btn(true), padding: '7px 14px', fontSize: 12.5 }}>+ {t.addRecurring}</button>
                </div>
                <div style={{ fontSize: 12, color: C.inkFaint, marginBottom: 14 }}>{t.recurringDesc}</div>
                {recurring.length === 0 ? (
                  <div style={{ fontSize: 13, color: C.inkFaint, textAlign: 'center', padding: '10px 0' }}>—</div>
                ) : (
                  recurring.map(r => {
                    const acct = accounts.find(a => a.id === r.account_id);
                    const curr = getCurrencyForAccount(acct);
                    const periodText = r.period_type === 'daily' ? t.periodDaily
                      : r.period_type === 'weekly' ? `${t.periodWeekly} ${t.weekdayNames.slice(r.period_day * (lang === 'zh' ? 1 : 3), r.period_day * (lang === 'zh' ? 1 : 3) + (lang === 'zh' ? 1 : 3))}`
                      : r.period_type === 'yearly' ? `${t.periodYearly} ${r.period_month}/${r.period_day}`
                      : `${t.periodMonthly} ${r.period_day}${t.recurDay}`;
                    const typeColor = r.type === 'income' ? C.income : r.type === 'transfer' ? C.transfer : C.expense;
                    return (
                      <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderTop: `1px solid ${C.border}`, opacity: r.active ? 1 : 0.5 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                            {r.description || (r.category_id ? getCatName(r.category_id) : (r.type === 'transfer' ? t.transfer : (r.type === 'income' ? t.income : t.expense)))}
                          </div>
                          <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2 }}>
                            {periodText} · {acct?.name || '-'} · {t.nextDue} {r.next_due}
                            {r.advance_days > 0 ? ` · ${lang === 'zh' ? '提前' : 'adv'} ${r.advance_days}${lang === 'zh' ? '天' : 'd'}` : ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <span style={{ fontWeight: 700, fontSize: 13.5, color: typeColor }}>
                            {r.type === 'income' ? '+' : r.type === 'expense' ? '-' : ''}{curr.symbol}{parseFloat(r.amount).toLocaleString()}
                          </span>
                          <button type="button" onClick={() => toggleRecurringActive(r)} title={r.active ? t.recurringActive : t.recurringPaused}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>{r.active ? '⏸' : '▶'}</button>
                          <button type="button" onClick={() => {
                            setEditingRecurring(r);
                            setRecurForm({ type: r.type, amount: String(r.amount), account_id: r.account_id, transfer_to_account_id: r.transfer_to_account_id || '', category_id: r.category_id || '', description: r.description || '', note: r.note || '', period_type: r.period_type, period_day: r.period_day || 1, period_month: r.period_month || 1, advance_days: r.advance_days || 0 });
                            setShowRecurringModal(true);
                          }} style={{ background: 'none', border: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 13 }}>✎</button>
                          <button type="button" onClick={() => deleteRecurring(r)} style={{ background: 'none', border: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 13 }}>🗑</button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Export / Import */}
              <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 500, marginBottom: 12 }}>{t.exportData}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 8 }}>{t.export}</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={exportAllCSV} style={{ padding: '7px 16px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: C.ink }}>
                        📊 {t.exportCSV}
                      </button>
                      <button type="button" onClick={exportJSON} style={{ padding: '7px 16px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: C.ink }}>
                        💾 {t.exportJSON}
                      </button>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 8 }}>{t.import}</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <label style={{ padding: '7px 16px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: C.ink }}>
                        📂 {t.importCSV}
                        <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => e.target.files[0] && importCSV(e.target.files[0])} />
                      </label>
                      <label style={{ padding: '7px 16px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: C.ink }}>
                        📂 {t.importJSON}
                        <input type="file" accept=".json" style={{ display: 'none' }} onChange={e => e.target.files[0] && importJSON(e.target.files[0])} />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Guide */}
              <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {lang === 'zh' ? '帮助' : 'Help'}
                </div>
                <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 12 }}>
                  {lang === 'zh' ? '不知道怎么用？查看图文使用说明。' : 'New here? Read the step-by-step guide.'}
                </div>
                <button type="button" onClick={() => setShowGuide(true)} style={{
                  padding: '9px 18px', background: primaryFill(), color: primaryText(),
                  border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
                  boxShadow: C.glow,
                }}>
                  📖 {lang === 'zh' ? '使用说明' : 'User Guide'}
                </button>
              </div>

              {/* Choose which currencies to use */}
              <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {lang === 'zh' ? '启用的币种' : 'Enabled Currencies'}
                </div>
                <div style={{ fontSize: 12, color: C.inkFaint, marginBottom: 12 }}>
                  {lang === 'zh' ? '勾选你要使用的币种，未勾选的会被隐藏' : 'Pick the currencies you use; unchecked ones are hidden'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {WORLD_CURRENCIES.map(c => {
                    const on = enabledCurrencyCodes.includes(c.code);
                    return (
                      <button type="button" key={c.code} onClick={() => toggleUserCurrency(c.code)}
                        style={{
                          padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                          border: on ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                          background: on ? C.grad : C.card,
                          color: on ? '#fff' : C.inkSoft,
                          fontWeight: on ? 600 : 500,
                          boxShadow: on ? C.glow : 'none',
                        }}>
                        {on ? '✓ ' : ''}{c.symbol} {lang === 'zh' ? c.name_zh : c.name_en} ({c.code})
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Default Currency */}
              <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>{t.defaultCurrency}</div>
                <div style={{ fontSize: 12, color: C.inkFaint, marginBottom: 12 }}>
                  {lang === 'zh' ? '新增账户时若不指定币种，将使用此默认币种' : 'New accounts will use this currency if not specified'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {currencies.map(c => (
                    <button type="button" key={c.code} onClick={() => saveDefaultCurrency(c.code)}
                      style={{
                        padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                        border: defaultCurrency.code === c.code ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                        background: defaultCurrency.code === c.code ? C.incomeSoft : '#fff',
                        color: defaultCurrency.code === c.code ? C.income : C.inkSoft,
                        fontWeight: defaultCurrency.code === c.code ? 500 : 400,
                      }}>
                      {c.symbol} {lang === 'zh' ? c.name_zh : c.name_en} ({c.code})
                      {defaultCurrency.code === c.code && ' ✓'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Exchange Rates */}
              <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 500, marginBottom: 12 }}>{t.exchangeRateSettings}</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  {[['auto', t.autoMode], ['manual', t.manualMode]].map(([mode, label]) => (
                    <button type="button" key={mode} onClick={() => saveExchangeRateMode(mode)}
                      style={{
                        padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                        border: exchangeRateMode === mode ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                        background: exchangeRateMode === mode ? C.incomeSoft : '#fff',
                        color: exchangeRateMode === mode ? C.income : C.inkSoft,
                        fontWeight: exchangeRateMode === mode ? 500 : 400,
                      }}>
                      {label}
                    </button>
                  ))}
                  {exchangeRateMode === 'auto' && (
                    <button type="button" onClick={() => fetchAutoRates(defaultCurrency.code)} disabled={ratesLoading}
                      style={{ padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer', border: `1px solid ${C.border}`, background: C.card, color: C.inkSoft }}>
                      {ratesLoading ? '...' : t.refreshRates}
                    </button>
                  )}
                </div>
                {exchangeRateMode === 'manual' && (
                  <div>
                    <div style={{ fontSize: 12, color: C.inkFaint, marginBottom: 10 }}>{t.manualRateHint} ({defaultCurrency.code})</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {currencies.filter(c => c.code !== defaultCurrency.code).map(c => (
                        <div key={c.code} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 120, fontSize: 13 }}>1 {c.code} =</span>
                          <input type="number" step="0.0001" defaultValue={manualRates[c.code] || ''} placeholder="0.0000"
                            onBlur={e => saveManualRate(c.code, e.target.value)}
                            style={{ width: 120, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, background: C.card, color: C.ink }} />
                          <span style={{ fontSize: 13, color: C.inkSoft }}>{defaultCurrency.code}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {exchangeRateMode === 'auto' && Object.keys(exchangeRates).length > 0 && (
                  <div style={{ fontSize: 12, color: C.inkFaint }}>
                    {lang === 'zh' ? '当前汇率基准：' : 'Base currency: '}{defaultCurrency.code}
                    {currencies.filter(c => c.code !== defaultCurrency.code).map(c => (
                      <span key={c.code} style={{ marginLeft: 12 }}>
                        1 {defaultCurrency.code} = {exchangeRates[c.code]?.toFixed(4) || '-'} {c.code}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Category management */}
              <div style={{ background: C.card, borderRadius: 18, padding: 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontWeight: 500 }}>{t.manageCategories}</div>
                  <button type="button" onClick={() => setShowAddCategory(true)} className="em-btn-primary" style={S.btn(true)}>{t.addCategory}</button>
                </div>
                {[['expense', t.expense], ['income', t.income], ['both', t.both]].map(([type, label]) => {
                  // Only top-level (parent) categories of this type
                  const parents = categories.filter(c => c.type === type && !c.parent_id);
                  if (parents.length === 0) return null;
                  return (
                    <div key={type} style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: type === 'expense' ? C.expense : type === 'income' ? C.income : C.inkSoft, marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${C.border}` }}>
                        {label}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {parents.map(c => {
                          const children = categories.filter(ch => ch.parent_id === c.id);
                          return (
                            <div key={c.id}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 13px', background: C.card, borderRadius: 10, border: `1px solid ${C.border}` }}>
                                <span style={{ fontWeight: 500 }}>{c.name_zh} / {c.name_en}</span>
                                <button type="button" onClick={() => deleteCategory(c.id)} style={{ background: 'none', border: 'none', color: C.expense, cursor: 'pointer', fontSize: 13 }}>✕</button>
                              </div>
                              {children.map(ch => (
                                <div key={ch.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 12px 7px 28px', background: '#fbfbfb', borderRadius: 6, border: '1px solid #f0f0f0', marginTop: 4 }}>
                                  <span style={{ fontSize: 13, color: C.inkSoft }}>
                                    <span style={{ color: '#bbb', marginRight: 6 }}>↳</span>
                                    {ch.name_zh} / {ch.name_en}
                                  </span>
                                  <button type="button" onClick={() => deleteCategory(ch.id)} style={{ background: 'none', border: 'none', color: C.expense, cursor: 'pointer', fontSize: 13 }}>✕</button>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Version footer */}
          <div style={{ textAlign: 'center', fontSize: 11, color: C.inkFaint, marginTop: 28, paddingBottom: isMobile ? 8 : 0 }}>
            EasyMoney {APP_VERSION}
          </div>
        </div>
      </div>

      </div>{/* 关闭：侧栏+主区 横向容器 */}

      {/* 手机版：右下角浮动 ＋ 记账按钮（导航已移到顶部） */}
      {isMobile && (
        <button type="button" onClick={() => {
          setShowExtras(false); setScanMsg(''); setTxnType('expense');
          if (selectedAccount) {
            setAcctId(selectedAccount.id);
          } else {
            let last = '';
            try { last = localStorage.getItem('em_last_account') || ''; } catch {}
            const lastAcct = accounts.find(a => a.id === last);
            if (lastAcct && lastAcct.account_type !== 'credit') {
              setAcctId(last);
            } else {
              const cashLike = accounts.find(a => ['checking', 'savings', 'cash'].includes(a.account_type));
              const firstNonCredit = accounts.find(a => a.account_type !== 'credit');
              setAcctId((cashLike || firstNonCredit || accounts[0])?.id || '');
            }
          }
          setShowModal(true);
        }} style={{
          position: 'fixed', bottom: 24, right: 20, width: 56, height: 56, borderRadius: '50%',
          border: 'none', cursor: 'pointer', background: primaryFill(), color: primaryText(),
          fontSize: 30, boxShadow: C.glow, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>+</button>
      )}

      {/* ── Duplicate review modal ── */}
      {showDupReview && (() => {
        const dups = transactions.filter(tx => tx.possible_duplicate);
        return (
          <div onClick={() => setShowDupReview(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: C.bg, borderRadius: 20, maxWidth: 600, width: '100%', maxHeight: '88vh',
              overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <div style={{ position: 'sticky', top: 0, background: C.bg, padding: '20px 22px 14px', borderBottom: `1px solid ${C.border}`, zIndex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>
                  ⚠️ {lang === 'zh' ? '检查疑似重复' : 'Review Duplicates'}
                </div>
                <div style={{ fontSize: 12.5, color: C.inkSoft, marginTop: 4 }}>
                  {lang === 'zh' ? '这些是银行同步进来、与你手动记录相似的交易。逐笔决定保留还是删除。' : 'These synced transactions look similar to your manual entries. Keep or delete each.'}
                </div>
              </div>
              <div style={{ padding: '12px 22px 22px' }}>
                {dups.length === 0 ? (
                  <div style={{ textAlign: 'center', color: C.inkFaint, fontSize: 14, padding: '30px 0' }}>
                    {lang === 'zh' ? '没有疑似重复了 🎉' : 'No more duplicates 🎉'}
                  </div>
                ) : dups.map(tx => {
                  const acct = accounts.find(a => a.id === tx.account_id);
                  const aCurr = getCurrencyForAccount(acct);
                  // Find the manual transaction it might duplicate (same account/amount/type, ±3 days)
                  const match = transactions.find(m => !m.plaid_transaction_id && m.account_id === tx.account_id
                    && parseFloat(m.amount) === parseFloat(tx.amount) && m.type === tx.type
                    && Math.abs((new Date(m.date) - new Date(tx.date)) / 86400000) <= 3);
                  return (
                    <div key={tx.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 12 }}>
                      {/* the synced (possibly duplicate) txn */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: match ? 8 : 0 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600 }}>🏦 {tx.description || '—'}</div>
                          <div style={{ fontSize: 11.5, color: C.inkFaint }}>{tx.date} · {acct?.name} · {lang === 'zh' ? '银行同步' : 'Synced'}</div>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: tx.type === 'income' ? C.income : C.expense }}>
                          {aCurr.symbol}{parseFloat(tx.amount).toLocaleString()}
                        </div>
                      </div>
                      {/* the manual txn it matches */}
                      {match && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: C.cardAlt, borderRadius: 8, marginBottom: 10 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, color: C.inkSoft }}>✍️ {match.description || '—'}</div>
                            <div style={{ fontSize: 11, color: C.inkFaint }}>{match.date} · {lang === 'zh' ? '你手动记的' : 'Manual entry'}</div>
                          </div>
                          <div style={{ fontSize: 12.5, color: C.inkSoft }}>{lang === 'zh' ? '可能是同一笔' : 'Likely same'}</div>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => keepDuplicate(tx.id)}
                          style={{ flex: 1, padding: '8px 0', background: C.card, color: C.ink, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 500 }}>
                          {lang === 'zh' ? '保留（不是重复）' : 'Keep'}
                        </button>
                        <button type="button" onClick={() => { deleteTransaction(tx); }}
                          style={{ flex: 1, padding: '8px 0', background: C.card, color: C.expense, border: `1px solid ${C.expense}`, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 500 }}>
                          {lang === 'zh' ? '删除这笔' : 'Delete this'}
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button type="button" onClick={() => setShowDupReview(false)}
                  style={{ width: '100%', padding: '11px 0', marginTop: 4, background: primaryFill(), color: primaryText(), border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {lang === 'zh' ? '完成' : 'Done'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Batch categorize modal ── */}
      {batchCat && (() => {
        const list = transactions.filter(tx =>
          tx.account_id === batchCat.accountId && !tx.category_id && tx.type !== 'transfer'
        );
        const allSelected = list.length > 0 && batchSelected.length === list.length;
        const toggleAll = () => setBatchSelected(allSelected ? [] : list.map(x => x.id));
        const toggleOne = (id) => setBatchSelected(prev =>
          prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        return (
          <div onClick={() => setBatchCat(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: C.bg, borderRadius: 20, maxWidth: 560, width: '100%', maxHeight: '88vh',
              overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <div style={{ position: 'sticky', top: 0, background: C.bg, padding: '20px 22px 14px', borderBottom: `1px solid ${C.border}`, zIndex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>
                  🏷️ {lang === 'zh' ? '批量分类' : 'Batch Categorize'}
                </div>
                <div style={{ fontSize: 12.5, color: C.inkSoft, marginTop: 4 }}>
                  {lang === 'zh' ? '选择未分类的交易，统一设置分类。' : 'Select uncategorized transactions and set a category.'}
                </div>
              </div>
              <div style={{ padding: '12px 22px 22px' }}>
                {list.length === 0 ? (
                  <div style={{ textAlign: 'center', color: C.inkFaint, fontSize: 14, padding: '30px 0' }}>
                    {lang === 'zh' ? '没有未分类的交易 🎉' : 'No uncategorized transactions 🎉'}
                  </div>
                ) : (
                  <>
                    {/* category picker + select all */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                      <select value={batchCatId} onChange={e => setBatchCatId(e.target.value)}
                        style={{ flex: 1, minWidth: 160, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.card, color: C.ink }}>
                        <option value="">{lang === 'zh' ? '-- 选择分类 --' : '-- Select category --'}</option>
                        {(() => {
                          // 按 父→子 层级渲染：母分类在前，紧跟其子分类（缩进）
                          const nameOf = (c) => lang === 'zh' ? c.name_zh : c.name_en;
                          const renderTree = (typeFilter) => {
                            const pool = categories.filter(typeFilter);
                            const parents = pool.filter(c => !c.parent_id);
                            const childrenOf = (pid) => pool.filter(c => c.parent_id === pid);
                            const opts = [];
                            parents.forEach(p => {
                              opts.push(<option key={p.id} value={p.id}>{nameOf(p)}</option>);
                              childrenOf(p.id).forEach(ch => {
                                opts.push(<option key={ch.id} value={ch.id}>{nameOf(p)} - {nameOf(ch)}</option>);
                              });
                            });
                            // 兜底：父分类已被过滤掉、但子分类还在的孤儿，也列出来
                            pool.filter(c => c.parent_id && !parents.some(p => p.id === c.parent_id)).forEach(orphan => {
                              opts.push(<option key={orphan.id} value={orphan.id}>{nameOf(orphan)}</option>);
                            });
                            return opts;
                          };
                          return (
                            <>
                              <optgroup label={lang === 'zh' ? '支出' : 'Expense'}>
                                {renderTree(c => c.type === 'expense' || c.type === 'both')}
                              </optgroup>
                              <optgroup label={lang === 'zh' ? '收入' : 'Income'}>
                                {renderTree(c => c.type === 'income')}
                              </optgroup>
                            </>
                          );
                        })()}
                      </select>
                      <button type="button" onClick={toggleAll}
                        style={{ padding: '9px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.inkSoft, cursor: 'pointer', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                        {allSelected ? (lang === 'zh' ? '取消全选' : 'Deselect all') : (lang === 'zh' ? '全选' : 'Select all')}
                      </button>
                    </div>
                    {/* transaction list */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, maxHeight: '42vh', overflow: 'auto' }}>
                      {list.map(tx => {
                        const checked = batchSelected.includes(tx.id);
                        const aCurr = getCurrencyForAccount(accounts.find(a => a.id === tx.account_id));
                        return (
                          <div key={tx.id} onClick={() => toggleOne(tx.id)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                              border: `1px solid ${checked ? C.primary : C.border}`, background: checked ? C.gradSoft : C.card }}>
                            <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, border: `1.5px solid ${checked ? C.primary : C.border}`, background: checked ? C.primary : 'transparent', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                              {checked ? '✓' : ''}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tx.description || '—'}</div>
                              <div style={{ fontSize: 11, color: C.inkFaint }}>{tx.date}</div>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: tx.type === 'income' ? C.income : C.expense }}>
                              {aCurr.symbol}{parseFloat(tx.amount).toLocaleString()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="button" onClick={() => setBatchCat(null)}
                        style={{ flex: 1, padding: '11px 0', background: C.card, color: C.inkSoft, border: `1px solid ${C.border}`, borderRadius: 10, cursor: 'pointer', fontSize: 14 }}>
                        {lang === 'zh' ? '取消' : 'Cancel'}
                      </button>
                      <button type="button" onClick={applyBatchCategory}
                        style={{ flex: 2, padding: '11px 0', background: primaryFill(), color: primaryText(), border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                        {lang === 'zh' ? `应用到 ${batchSelected.length} 笔` : `Apply to ${batchSelected.length}`}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Plaid: bank account mapping modal ── */}
      {plaidMapping && (
        <div onClick={() => !plaidBusy && setPlaidMapping(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.bg, borderRadius: 20, maxWidth: 560, width: '100%', maxHeight: '88vh',
            overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ position: 'sticky', top: 0, background: C.bg, padding: '20px 22px 14px', borderBottom: `1px solid ${C.border}`, zIndex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>
                🏦 {plaidMapping.institution_name || (lang === 'zh' ? '银行' : 'Bank')}
              </div>
              <div style={{ fontSize: 12.5, color: C.inkSoft, marginTop: 4 }}>
                {lang === 'zh' ? '选择每个银行账户对应到哪里。' : 'Choose where each bank account goes.'}
              </div>
            </div>
            <div style={{ padding: '12px 22px 22px' }}>
              {plaidMapping.rows.map((r, i) => {
                const setRow = (patch) => setPlaidMapping(pm => ({
                  ...pm, rows: pm.rows.map((x, j) => j === i ? { ...x, ...patch } : x),
                }));
                const typeLabel = r.suggested_type === 'credit' ? (lang === 'zh' ? '信用卡' : 'Credit')
                  : r.suggested_type === 'savings' ? (lang === 'zh' ? '储蓄' : 'Savings')
                  : (lang === 'zh' ? '支票' : 'Checking');
                return (
                  <div key={r.plaid_account_id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {r.name} {r.mask && <span style={{ color: C.inkFaint, fontWeight: 400 }}>···{r.mask}</span>}
                      </div>
                      <span style={{ fontSize: 11, color: C.primary, background: C.gradSoft, padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>{typeLabel}</span>
                    </div>
                    {/* action selector */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: r.action === 'existing' ? 10 : 0, flexWrap: 'wrap' }}>
                      {[
                        { v: 'new', zh: '新建账户', en: 'New' },
                        { v: 'existing', zh: '并入已有', en: 'Merge into' },
                        { v: 'skip', zh: '不导入', en: 'Skip' },
                      ].map(opt => (
                        <button key={opt.v} type="button" onClick={() => setRow({ action: opt.v })}
                          style={{
                            padding: '6px 12px', borderRadius: 8, fontSize: 12.5, cursor: 'pointer',
                            border: `1px solid ${r.action === opt.v ? C.primary : C.border}`,
                            background: r.action === opt.v ? C.primary : C.card,
                            color: r.action === opt.v ? '#fff' : C.inkSoft,
                            fontWeight: r.action === opt.v ? 600 : 400,
                          }}>
                          {lang === 'zh' ? opt.zh : opt.en}
                        </button>
                      ))}
                    </div>
                    {/* existing account picker */}
                    {r.action === 'existing' && (
                      <select value={r.existing_account_id} onChange={e => setRow({ existing_account_id: e.target.value })}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.card, color: C.ink }}>
                        <option value="">{lang === 'zh' ? '-- 选择已有账户 --' : '-- Select account --'}</option>
                        {accounts.filter(a => a.account_type !== 'crypto' && a.account_type !== 'stock').map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" disabled={plaidBusy} onClick={() => setPlaidMapping(null)}
                  style={{ flex: 1, padding: '11px 0', background: C.card, color: C.inkSoft, border: `1px solid ${C.border}`, borderRadius: 10, cursor: 'pointer', fontSize: 14 }}>
                  {lang === 'zh' ? '取消' : 'Cancel'}
                </button>
                <button type="button" disabled={plaidBusy} onClick={finalizeBank}
                  style={{ flex: 2, padding: '11px 0', background: primaryFill(), color: primaryText(), border: 'none', borderRadius: 10, cursor: plaidBusy ? 'default' : 'pointer', fontSize: 14, fontWeight: 600, opacity: plaidBusy ? 0.6 : 1 }}>
                  {plaidBusy ? (lang === 'zh' ? '处理中...' : 'Working...') : (lang === 'zh' ? '确认并同步' : 'Confirm & Sync')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── User Guide Modal ── */}
      {showGuide && (() => {
        const guide = lang === 'zh' ? [
          { icon: '📝', title: '1. 注册与登录', steps: [
            '点击登录框下方"还没有账号？去注册"。',
            '输入邮箱和密码（至少 6 位），再输入确认密码，点击注册。',
            '注册后需等待管理员审核，通过后即可登录。',
          ], tip: '右上角可切换深色/浅色主题和中/英文。' },
          { icon: '💱', title: '2. 设置默认币种', steps: [
            '首次登录时选择默认币种（如 CNY、USD）。',
            '所有账户余额会自动折算成默认币种显示总资产。',
            '以后可在设置里随时更改。',
          ], tip: '支持 30 种全球货币，自动按汇率折算。' },
          { icon: '🏦', title: '3. 创建账户', steps: [
            '在总览页"我的账户"点击添加账户。',
            '填写名称，选类型（支票/储蓄/现金/信用卡/股票/加密钱包）。',
            '设置初始余额、币种、颜色。信用卡可设额度。',
          ], tip: '不同颜色方便一眼区分账户。' },
          { icon: '✏️', title: '4. 记一笔账', steps: [
            '点击底部蓝色 ＋ 按钮（手机）或顶部"＋ 新增交易"。',
            '选择类型：支出 / 收入 / 转账。',
            '输入金额，选分类和账户，可选填日期、商家、备注。',
          ], tip: '转账和买入股票/加密货币不算支出，只是资产转换。' },
          { icon: '📷', title: '5. 扫小票自动记账', steps: [
            '在新增交易页点击"扫描小票自动填写"。',
            '拍照或选图，等 AI 识别几秒。',
            'AI 自动填好金额、商家、日期、分类，检查后保存。',
          ], tip: '中英文小票都能识别，光线足、票面平整更准。' },
          { icon: '📈', title: '6. 股票账户', steps: [
            '创建股票账户，可存入现金作为可用资金。',
            '买入时输入代码、股数、价格。美股实时更新，A股港股手动输价。',
            '自动算平均成本和盈亏，盈利绿色▲，亏损红色▼。',
          ] },
          { icon: '🪙', title: '7. 加密货币钱包', steps: [
            '创建加密钱包账户。',
            '买入时输入数量、单价、总金额（三字段联动）。',
            '自动追踪每种币的平均成本和盈亏。',
            '钱包间转币：选转账，选币种，输入转移的币数量，成本自动带过去。',
          ], tip: '用加密货币还信用卡也是用转账功能。' },
          { icon: '🎯', title: '8. 预算管理', steps: [
            '进入预算页，新增预算。',
            '选分类、金额、周期（每月/每周）。',
            '自动统计消费，显示已用和剩余，点开看明细。',
          ], tip: '买入股票、加密货币不计入预算消耗。' },
          { icon: '🔁', title: '9. 定期交易', steps: [
            '设置中找到定期交易，新增一条。',
            '设金额、账户、周期和提前生成天数。',
            '到期前自动生成待确认交易（琥珀黄高亮），确认后才记入。',
          ] },
          { icon: '📊', title: '10. 报表查看', steps: [
            '进入报表页，环形图看各分类支出/收入占比。',
            '总览页有 12 个月趋势图看收支变化。',
          ] },
          { icon: '💾', title: '11. 备份与恢复', steps: [
            '设置里点"导出 JSON（完整备份）"下载备份。',
            '点"导入 JSON"选备份文件，智能合并、不产生重复。',
          ], tip: 'CSV 导出只含交易明细，适合 Excel 查看。' },
          { icon: '⚙️', title: '12. 个性化', steps: [
            '主题、语言、默认币种随时切换。',
            '手机浏览器打开后"添加到主屏幕"，即可像 App 一样用。',
          ], tip: '支持手机、平板、电脑，自动适配。' },
        ] : [
          { icon: '📝', title: '1. Sign Up & Sign In', steps: [
            'Click "No account? Sign up" below the login box.',
            'Enter email and password (6+ chars), confirm password, click Sign Up.',
            'Wait for admin approval, then sign in.',
          ], tip: 'Top-right toggles dark/light theme and 中/EN language.' },
          { icon: '💱', title: '2. Set Default Currency', steps: [
            'Pick a default currency on first login (e.g. USD, CNY).',
            'All balances convert to it to show total assets.',
            'Change it anytime in Settings.',
          ], tip: '30 global currencies with auto exchange-rate conversion.' },
          { icon: '🏦', title: '3. Create Accounts', steps: [
            'On the overview, find "My Accounts" and click Add Account.',
            'Enter a name, pick a type (Checking/Savings/Cash/Credit/Stock/Crypto).',
            'Set starting balance, currency, color. Credit cards have a limit.',
          ], tip: 'Colors help tell accounts apart at a glance.' },
          { icon: '✏️', title: '4. Record a Transaction', steps: [
            'Tap the blue ＋ button (mobile) or "＋ New Transaction" (desktop).',
            'Choose a type: Expense / Income / Transfer.',
            'Enter amount, pick category and account, optionally add date/merchant/note.',
          ], tip: 'Transfers and buying stocks/crypto are not expenses — just asset conversion.' },
          { icon: '📷', title: '5. Scan Receipts with AI', steps: [
            'On the new transaction screen, tap "Scan Receipt".',
            'Take a photo or pick an image, wait a few seconds.',
            'AI fills amount, merchant, date, category. Review and save.',
          ], tip: 'Works for English and Chinese receipts.' },
          { icon: '📈', title: '6. Stock Accounts', steps: [
            'Create a Stock account, add cash as available funds.',
            'Buy: enter ticker, shares, price. US live; A-shares/HK manual.',
            'Auto-calculates average cost and P/L. Gains green ▲, losses red ▼.',
          ] },
          { icon: '🪙', title: '7. Crypto Wallets', steps: [
            'Create a Crypto Wallet account.',
            'Buy: enter quantity, unit price, total (fields are linked).',
            'Tracks each coin\u2019s average cost and P/L.',
            'Move coins between wallets via Transfer; cost basis carries over.',
          ], tip: 'Paying a credit card with crypto also uses Transfer.' },
          { icon: '🎯', title: '8. Budgets', steps: [
            'Go to Budget, add a budget.',
            'Choose category, amount, period (monthly/weekly).',
            'Tracks spending, shows used and remaining; tap for details.',
          ], tip: 'Buying stocks/crypto does not count toward budgets.' },
          { icon: '🔁', title: '9. Recurring Transactions', steps: [
            'In Settings, find Recurring Transactions, add one.',
            'Set amount, account, period, and days in advance.',
            'A pending transaction (amber) is auto-created; recorded after you confirm.',
          ] },
          { icon: '📊', title: '10. Reports', steps: [
            'Go to Reports for a donut chart of spending/income by category.',
            'The overview has a 12-month income/expense trend chart.',
          ] },
          { icon: '💾', title: '11. Backup & Restore', steps: [
            'In Settings, click "Export JSON (Full Backup)" to download.',
            'Click "Import JSON" to smart-merge — no duplicates created.',
          ], tip: 'CSV export holds transactions only, good for Excel.' },
          { icon: '⚙️', title: '12. Personalization', steps: [
            'Switch theme, language, and default currency anytime.',
            'Open in phone browser, "Add to Home Screen" to use like an app.',
          ], tip: 'Works on phone, tablet, desktop, adapting automatically.' },
        ];
        return (
          <div onClick={() => setShowGuide(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: C.bg, borderRadius: 20, maxWidth: 560, width: '100%', maxHeight: '88vh',
              overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              {/* Header */}
              <div style={{
                position: 'sticky', top: 0, background: C.bg, padding: '20px 22px 14px',
                borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1,
              }}>
                <div style={{ fontSize: 19, fontWeight: 800 }}>
                  📖 {lang === 'zh' ? '使用说明' : 'User Guide'}
                </div>
                <button type="button" onClick={() => setShowGuide(false)} style={{
                  background: C.card, border: `1px solid ${C.border}`, borderRadius: 9,
                  width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: C.inkSoft,
                }}>✕</button>
              </div>
              {/* Content */}
              <div style={{ padding: '8px 22px 24px' }}>
                {guide.map((sec, i) => (
                  <div key={i} style={{
                    background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
                    padding: '16px 18px', marginTop: 14,
                  }}>
                    <div style={{ fontSize: 15.5, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{sec.icon}</span>{sec.title}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {sec.steps.map((s, j) => (
                        <div key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <span style={{
                            flexShrink: 0, width: 20, height: 20, borderRadius: 6,
                            background: C.gradSoft, color: C.primary, fontSize: 11, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
                          }}>{j + 1}</span>
                          <span style={{ fontSize: 13.5, color: C.inkSoft, lineHeight: 1.55 }}>{s}</span>
                        </div>
                      ))}
                    </div>
                    {sec.tip && (
                      <div style={{
                        marginTop: 10, padding: '8px 12px', borderRadius: 8,
                        background: C.warnSoft, borderLeft: `3px solid ${C.warn}`,
                        fontSize: 12.5, color: C.ink,
                      }}>
                        💡 {sec.tip}
                      </div>
                    )}
                  </div>
                ))}
                <div style={{ textAlign: 'center', color: C.inkFaint, fontSize: 12, marginTop: 20 }}>
                  💰 EasyMoney · {lang === 'zh' ? '让记账变简单' : 'Money made simple'}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Add Transaction Modal ── */}
      {showModal && (() => {
        const selAcct = accounts.find(a => a.id === acctId);
        const isCredit = selAcct?.account_type === 'crypto' ? false : selAcct?.account_type === 'credit';
        const isCrypto = selAcct?.account_type === 'crypto';
        const curr = getCurrencyForAccount(selAcct);
        const typeColor = txnType === 'expense' ? C.expense : txnType === 'income' ? C.income : C.transfer;
        return (
        <Modal title={t.newTxn} onClose={() => setShowModal(false)}>
          {/* 批量记账入口 */}
          <div style={{ textAlign: 'right', marginBottom: 10, marginTop: -6 }}>
            <span onClick={() => { setShowModal(false); openBatch(); }}
              style={{ fontSize: 12.5, color: C.primary, cursor: 'pointer', fontWeight: 600 }}>
              {lang === 'zh' ? '📝 批量记多笔 →' : '📝 Batch entry →'}
            </span>
          </div>
          {/* Type tabs */}
          <div style={{ display: 'flex', marginBottom: 16, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            {(() => {
              return [
                ['expense', isCredit ? t.spend : t.expense],
                ['income', isCredit ? t.repay : t.income],
                ['transfer', t.transfer],
              ];
            })().map(([type, label]) => (
              <button type="button" key={type} onClick={() => setTxnType(type)}
                style={{
                  flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer', fontSize: 13.5,
                  background: txnType === type ? (type === 'expense' ? C.expenseSoft : type === 'income' ? C.incomeSoft : C.transferSoft) : 'transparent',
                  color: txnType === type ? (type === 'expense' ? C.expense : type === 'income' ? C.income : C.transfer) : C.inkSoft,
                  fontWeight: txnType === type ? 600 : 500,
                }}>
                {label}
              </button>
            ))}
          </div>

          {/* Scan receipt button (not for transfer) */}
          {txnType !== 'transfer' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '11px', borderRadius: 12, cursor: scanning ? 'default' : 'pointer',
                border: `1.5px dashed ${C.primary}`, background: C.gradSoft, color: C.primary,
                fontSize: 13.5, fontWeight: 600, opacity: scanning ? 0.6 : 1,
              }}>
                {scanning
                  ? (lang === 'zh' ? '识别中...' : 'Scanning...')
                  : <>📷 {lang === 'zh' ? '扫描小票自动填写' : 'Scan Receipt'}</>}
                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                  disabled={scanning}
                  onChange={e => { if (e.target.files[0]) scanReceipt(e.target.files[0]); e.target.value = ''; }} />
              </label>
              {scanMsg && (
                <div style={{ fontSize: 12, color: scanMsg.includes('失败') || scanMsg.toLowerCase().includes('fail') ? C.expense : C.income, marginTop: 6, textAlign: 'center' }}>
                  {scanMsg}
                </div>
              )}
            </div>
          )}

          {/* Big amount input */}
          {(() => {
            const toAcct = accounts.find(a => a.id === transferToAcctId);
            const isWalletToWallet = txnType === 'transfer' && isCrypto && toAcct?.account_type === 'crypto';
            const selCoin = cryptoHoldings.find(x => x.id === transferCryptoHoldingId);
            return (
          <div style={{ background: C.cardAlt, border: `1.5px solid ${C.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: C.inkFaint, marginBottom: 4 }}>
              {isCrypto && txnType !== 'transfer'
                ? (lang === 'zh' ? `数量（${selAcct.crypto_ticker || selAcct.crypto_symbol?.toUpperCase() || '枚'}）` : `Amount (${selAcct.crypto_ticker || 'coins'})`)
                : isWalletToWallet
                ? (lang === 'zh' ? `转移数量（${selCoin?.ticker || selCoin?.coin_id || '币'}）` : `Amount (${selCoin?.ticker || 'coins'})`)
                : t.amountLabel}
            </div>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0"
              style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 34, fontWeight: 700, textAlign: 'center', width: '100%', fontFamily: "'Sora', sans-serif", color: typeColor }} />
            {isCrypto && txnType !== 'transfer' && cryptoPrices[selAcct.crypto_symbol] && amount && (
              <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 2 }}>
                ≈ {curr.symbol}{(parseFloat(amount) * cryptoPrices[selAcct.crypto_symbol]).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            )}
            {isWalletToWallet && selCoin && amount && (
              <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 2 }}>
                ≈ {curr.symbol}{(parseFloat(amount) * (cryptoPrices[selCoin.coin_id] || parseFloat(selCoin.avg_cost || 0))).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                {' · '}{lang === 'zh' ? '可用' : 'avail'} {parseFloat(selCoin.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })}
              </div>
            )}
            {isCrypto && txnType === 'transfer' && !isWalletToWallet && transferCryptoHoldingId && amount && (() => {
              const h = cryptoHoldings.find(x => x.id === transferCryptoHoldingId);
              if (!h) return null;
              const p = cryptoPrices[h.coin_id] || parseFloat(h.avg_cost || 1);
              const coins = p > 0 ? parseFloat(amount) / p : 0;
              return (
                <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 2 }}>
                  ≈ {coins.toLocaleString(undefined, { maximumFractionDigits: 6 })} {h.ticker || h.coin_id}
                </div>
              );
            })()}
          </div>
            );
          })()}

          {/* Category chips (not for transfer) */}
          {txnType !== 'transfer' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: C.inkSoft, fontWeight: 600 }}>{t.category}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                {(() => {
                  const chips = txnCatChips;
                  if (chips.length === 0) {
                    return (
                      <div style={{ fontSize: 12, color: C.inkFaint, padding: '4px 0', lineHeight: 1.5 }}>
                        {lang === 'zh' ? '还没有分类。请退出登录后重新登录一次自动生成默认分类；或在“分类管理”里手动添加。' : 'No categories yet. Sign out and back in to auto-create defaults.'}
                      </div>
                    );
                  }
                  // 排序：钉住优先 → 使用频率
                  const rank = a => (pinnedCats.includes(a.id) ? 1000000 : 0) + (catUseCount[a.id] || 0);
                  const ordered = [...chips].sort((a, b) => rank(b) - rank(a));
                  // 确保当前已选的分类一定在默认可见区（避免选了却看不到）
                  const visible = ordered.slice(0, 4);
                  if (catId && !visible.some(c => c.id === catId)) {
                    const selChip = ordered.find(c => c.id === catId);
                    if (selChip) visible[3] = selChip;
                  }
                  const renderChip = (ch) => {
                    const sel = catId === ch.id;
                    const pinned = pinnedCats.includes(ch.id);
                    return (
                      <div key={ch.id} style={{ position: 'relative', display: 'inline-flex' }}>
                        <button type="button" onClick={() => setCatId(ch.id)}
                          style={{ padding: '9px 14px', borderRadius: 11, cursor: 'pointer', fontSize: 13,
                            border: sel ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                            background: sel ? primaryFill() : C.card, color: sel ? primaryText() : C.inkSoft,
                            boxShadow: sel ? C.glow : 'none', fontWeight: sel ? 600 : 500,
                            display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{catEmoji(ch.cat)}</span>{ch.label}
                        </button>
                        {/* 展开时：可点的图钉(灰=未钉 紫=已钉)；未展开时：仅已钉的显示小图钉 */}
                        {showAllCats ? (
                          <span onClick={(e) => { e.stopPropagation(); togglePinCat(ch.id); }}
                            title={pinned ? (lang === 'zh' ? '取消钉住' : 'Unpin') : (lang === 'zh' ? '钉住到前面' : 'Pin')}
                            style={{ position: 'absolute', top: -7, right: -7, fontSize: 10, cursor: 'pointer',
                              background: pinned ? C.gradSoft : C.card, border: `1px solid ${pinned ? C.primary : C.border}`,
                              borderRadius: '50%', width: 19, height: 19, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              filter: pinned ? 'none' : 'grayscale(1) opacity(0.5)' }}>📌</span>
                        ) : (pinned && (
                          <span style={{ position: 'absolute', top: -6, right: -6, fontSize: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: '50%', width: 17, height: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>📌</span>
                        ))}
                      </div>
                    );
                  };
                  return (showAllCats ? ordered : visible).map(renderChip);
                })()}
              </div>
              {txnCatChips.length > 4 && (
                <button type="button" onClick={() => setShowAllCats(v => !v)}
                  style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', fontSize: 12, fontWeight: 600, marginBottom: 16, padding: 0 }}>
                  {showAllCats ? (lang === 'zh' ? '收起 ▴　（点分类角上的📌钉住常用的）' : 'Less ▴') : (lang === 'zh' ? '更多分类 ▾' : 'More ▾')}
                </button>
              )}
            </>
          )}

          {/* Account chips */}
          <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 8, fontWeight: 600 }}>
            {txnType === 'transfer' ? t.transferFrom : t.account}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {accounts.length > 0 ? (() => {
              const rank = a => (pinnedAccts.includes(a.id) ? 1000000 : 0) + (acctUseCount[a.id] || 0);
              const ordered = [...accounts].sort((a, b) => rank(b) - rank(a));
              const visible = ordered.slice(0, 2);
              if (acctId && !visible.some(a => a.id === acctId)) {
                const sa = ordered.find(a => a.id === acctId);
                if (sa) visible[1] = sa;
              }
              const renderAcct = (a) => {
                const sel = acctId === a.id;
                const pinned = pinnedAccts.includes(a.id);
                return (
                  <div key={a.id} style={{ position: 'relative', display: 'inline-flex' }}>
                    <button type="button" onClick={() => { setAcctId(a.id); setTransferCryptoHoldingId(''); }}
                      style={{ padding: '9px 14px', borderRadius: 11, cursor: 'pointer', fontSize: 13,
                        border: sel ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                        background: sel ? primaryFill() : C.card, color: sel ? primaryText() : C.inkSoft,
                        boxShadow: sel ? C.glow : 'none', fontWeight: sel ? 600 : 500,
                        display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: a.color, flexShrink: 0 }} />
                      {a.name}
                    </button>
                    {showAllAccts ? (
                      <span onClick={(e) => { e.stopPropagation(); togglePinAcct(a.id); }}
                        title={pinned ? (lang === 'zh' ? '取消钉住' : 'Unpin') : (lang === 'zh' ? '钉住到前面' : 'Pin')}
                        style={{ position: 'absolute', top: -7, right: -7, fontSize: 10, cursor: 'pointer',
                          background: pinned ? C.gradSoft : C.card, border: `1px solid ${pinned ? C.primary : C.border}`,
                          borderRadius: '50%', width: 19, height: 19, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          filter: pinned ? 'none' : 'grayscale(1) opacity(0.5)' }}>📌</span>
                    ) : (pinned && (
                      <span style={{ position: 'absolute', top: -6, right: -6, fontSize: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: '50%', width: 17, height: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>📌</span>
                    ))}
                  </div>
                );
              };
              return (showAllAccts ? ordered : visible).map(renderAcct);
            })() : (
              <div style={{ fontSize: 12, color: C.inkFaint, padding: '4px 0', lineHeight: 1.5 }}>
                {lang === 'zh' ? '还没有账户。请先到“账户”页新增一个账户（如现金、银行卡），再来记账。' : 'No accounts yet. Add one on the Accounts page first.'}
              </div>
            )}
          </div>
          {accounts.length > 2 && (
            <button type="button" onClick={() => setShowAllAccts(v => !v)}
              style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', fontSize: 12, fontWeight: 600, marginBottom: txnType === 'transfer' ? 14 : 16, padding: 0 }}>
              {showAllAccts ? (lang === 'zh' ? '收起 ▴　（点账户角上的📌钉住常用的）' : 'Less ▴') : (lang === 'zh' ? '更多账户 ▾' : 'More ▾')}
            </button>
          )}

          {/* Coin selector: shown when transferring FROM a crypto wallet */}
          {txnType === 'transfer' && isCrypto && (() => {
            const walletCoins = cryptoHoldings.filter(h => h.account_id === acctId);
            if (walletCoins.length === 0) return null;
            return (
              <>
                <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 8, fontWeight: 600 }}>
                  {accounts.find(a => a.id === transferToAcctId)?.account_type === 'crypto'
                    ? (lang === 'zh' ? '转移哪种币' : 'Which coin to move')
                    : (lang === 'zh' ? '用哪种币付款' : 'Pay with coin')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                  {walletCoins.map(h => {
                    const price = cryptoPrices[h.coin_id] || 0;
                    const fiatVal = parseFloat(h.amount || 0) * price;
                    const sel = transferCryptoHoldingId === h.id;
                    return (
                      <button type="button" key={h.id} onClick={() => setTransferCryptoHoldingId(h.id)}
                        style={{
                          padding: '9px 14px', borderRadius: 11, cursor: 'pointer', fontSize: 13,
                          border: sel ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                          background: sel ? primaryFill() : C.card,
                          color: sel ? primaryText() : C.inkSoft,
                          boxShadow: sel ? C.glow : 'none',
                          fontWeight: sel ? 600 : 500,
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                        <span style={{ fontWeight: 700 }}>{h.ticker || h.coin_id}</span>
                        {fiatVal > 0 && (
                          <span style={{ fontSize: 11, opacity: 0.75 }}>
                            {curr.symbol}{fiatVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {/* Transfer target chips */}
          {txnType === 'transfer' && (
            <>
              <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 8, fontWeight: 600 }}>{t.transferTo}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {accounts.filter(a => a.id !== acctId).map(a => {
                  const sel = transferToAcctId === a.id;
                  return (
                    <button type="button" key={a.id} onClick={() => setTransferToAcctId(a.id)}
                      style={{
                        padding: '9px 14px', borderRadius: 11, cursor: 'pointer', fontSize: 13,
                        border: sel ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                        background: sel ? primaryFill() : C.card,
                        color: sel ? primaryText() : C.inkSoft,
                        boxShadow: sel ? C.glow : 'none',
                        fontWeight: sel ? 600 : 500,
                        display: 'flex', alignItems: 'center', gap: 7,
                      }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: a.color, flexShrink: 0 }} />
                      {a.name}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Collapsible extras: date, merchant, note */}
          {!showExtras ? (
            <button type="button" onClick={() => setShowExtras(true)}
              style={{ width: '100%', padding: '10px', border: `1.5px dashed ${C.border}`, borderRadius: 11, cursor: 'pointer', background: 'transparent', color: C.inkSoft, fontSize: 13, fontWeight: 500, marginBottom: 16 }}>
              + {lang === 'zh' ? '日期 / 商家 / 备注（选填）' : 'Date / Merchant / Note (optional)'}
            </button>
          ) : (
            <div style={{ marginBottom: 8 }}>
              <FormField label={t.dateLabel}>
                <input type="date" value={txnDate} onChange={e => setTxnDate(e.target.value)} style={S.input} />
              </FormField>
              <FormField label={t.merchantLabel}>
                <input type="text" value={merchant} onChange={e => setMerchant(e.target.value)} placeholder={lang === 'zh' ? '选填' : 'Optional'} style={S.input} />
              </FormField>
              <FormField label={lang === 'zh' ? '备注（选填）' : 'Note (optional)'}>
                <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder={lang === 'zh' ? '选填' : 'Optional'} style={S.input} />
              </FormField>
            </div>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" onClick={() => setShowModal(false)} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
            <button type="button" disabled={savingTxn} onClick={addTransaction} className="em-btn-primary" style={{ ...S.btn(true), padding: '9px 28px', opacity: savingTxn ? 0.6 : 1 }}>{savingTxn ? (lang === 'zh' ? '保存中...' : 'Saving...') : t.save}</button>
          </div>
        </Modal>
        );
      })()}

      {/* ── Batch Entry Modal (批量记账) ── */}
      {showBatch && (
        <Modal title={lang === 'zh' ? '📝 批量记账' : '📝 Batch Entry'} onClose={() => setShowBatch(false)}>
          <div style={{ fontSize: 12.5, color: C.inkFaint, marginBottom: 16 }}>
            {lang === 'zh' ? '一次记多笔，填完点"全部保存"。账户和日期整批共用。' : 'Record multiple at once. Account & date are shared.'}
          </div>

          {/* 共用设置 */}
          <div style={{ background: C.bg, borderRadius: 14, padding: 14, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', background: C.card, borderRadius: 10, padding: 3 }}>
              {[['expense', t.expense], ['income', t.income]].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setBatchType(v)}
                  style={{ flex: 1, padding: 8, border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
                    background: batchType === v ? C.gradSoft : 'transparent', color: batchType === v ? C.primary : C.inkSoft }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: C.inkFaint, display: 'block', marginBottom: 4 }}>{t.account}</label>
                <select value={batchAcctId} onChange={e => setBatchAcctId(e.target.value)}
                  style={{ width: '100%', padding: '9px 11px', border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: 13, background: C.card, color: C.ink }}>
                  {accounts.filter(a => a.account_type !== 'crypto' && a.account_type !== 'stock').map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: C.inkFaint, display: 'block', marginBottom: 4 }}>{t.date}</label>
                <input type="date" value={batchDate} onChange={e => setBatchDate(e.target.value)}
                  style={{ width: '100%', padding: '9px 11px', border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: 13, background: C.card, color: C.ink }} />
              </div>
            </div>
          </div>

          {/* 多行 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {batchLines.map((line, idx) => (
              <div key={idx} style={{ border: `1.5px solid ${C.border}`, borderRadius: 12, padding: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="number" inputMode="decimal" placeholder="0.00" value={line.amount}
                    onChange={e => updateBatchLine(idx, { amount: e.target.value })}
                    style={{ width: 110, padding: '9px 11px', border: `1.5px solid ${C.border}`, borderRadius: 9, fontSize: 15, fontWeight: 700, textAlign: 'right', color: batchType === 'income' ? C.income : C.expense, background: C.card }} />
                  <select value={line.catId} onChange={e => updateBatchLine(idx, { catId: e.target.value })}
                    style={{ flex: 1, padding: '9px 11px', border: `1.5px solid ${C.border}`, borderRadius: 9, fontSize: 13, background: C.card, color: C.ink }}>
                    <option value="">{lang === 'zh' ? '选分类' : 'Category'}</option>
                    {getCategoryOptions(batchType).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <button type="button" onClick={() => removeBatchLine(idx)}
                    style={{ width: 30, height: 30, border: 'none', background: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 17, flexShrink: 0, borderRadius: 8 }}>✕</button>
                </div>
                <span onClick={() => updateBatchLine(idx, { showMore: !line.showMore })}
                  style={{ fontSize: 11.5, color: C.primary, cursor: 'pointer', marginTop: 8, display: 'inline-block', fontWeight: 600 }}>
                  {line.showMore ? (lang === 'zh' ? '收起' : 'Less') : (lang === 'zh' ? '＋ 更多（商家/备注）' : '＋ More')}
                </span>
                {line.showMore && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input placeholder={t.merchantLabel} value={line.merchant}
                      onChange={e => updateBatchLine(idx, { merchant: e.target.value })}
                      style={{ width: '100%', padding: '8px 10px', border: `1.5px solid ${C.border}`, borderRadius: 9, fontSize: 12.5, background: C.card, color: C.ink }} />
                    <input placeholder={t.note} value={line.note}
                      onChange={e => updateBatchLine(idx, { note: e.target.value })}
                      style={{ width: '100%', padding: '8px 10px', border: `1.5px solid ${C.border}`, borderRadius: 9, fontSize: 12.5, background: C.card, color: C.ink }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <button type="button" onClick={addBatchLine}
            style={{ width: '100%', padding: 11, border: `1.5px dashed ${C.primary}`, background: C.gradSoft, color: C.primary, borderRadius: 12, cursor: 'pointer', fontSize: 13.5, fontWeight: 600, marginBottom: 16 }}>
            {lang === 'zh' ? '＋ 添加一行' : '＋ Add line'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, fontSize: 13, color: C.inkSoft }}>
              {(() => {
                const valid = batchLines.map(l => parseFloat(l.amount) || 0).filter(a => a > 0);
                const sum = valid.reduce((s, a) => s + a, 0);
                return <>{lang === 'zh' ? '共 ' : ''}<b>{valid.length}</b>{lang === 'zh' ? ' 笔，合计 ' : ' items · '}<b style={{ color: batchType === 'income' ? C.income : C.expense }}>{getCurrencyForAccount(accounts.find(a => a.id === batchAcctId)).symbol}{sum.toLocaleString(undefined, { maximumFractionDigits: 2 })}</b></>;
              })()}
            </div>
            <button type="button" onClick={() => setShowBatch(false)} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
            <button type="button" disabled={batchSaving} onClick={saveBatch} className="em-btn-primary" style={{ ...S.btn(true), opacity: batchSaving ? 0.6 : 1 }}>
              {batchSaving ? (lang === 'zh' ? '保存中...' : 'Saving...') : (lang === 'zh' ? '全部保存' : 'Save all')}
            </button>
          </div>
        </Modal>
      )}
      {editTxnMobile && (() => {
        const tx = editTxnMobile;
        const acct = accounts.find(a => a.id === tx.account_id);
        const isCrypto = acct?.account_type === 'crypto';
        const curr = getCurrencyForAccount(acct);
        const original = tx._original || tx; // 打开弹窗那一刻的原始数据快照，不受后续编辑影响
        const isAlreadyTransfer = original.type === 'transfer';
        const canConvertType = !isCrypto && !isAlreadyTransfer; // 已经是转账的不允许改成转账(避免重复处理两条腿)，加密货币交易不允许改类型
        return (
          <Modal title={lang === 'zh' ? '编辑交易' : 'Edit Transaction'} onClose={() => setEditTxnMobile(null)}>
            {canConvertType && (
              <FormField label={lang === 'zh' ? '类型' : 'Type'}>
                <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 11, overflow: 'hidden' }}>
                  {[['expense', t.expense || '支出'], ['income', t.income || '收入'], ['transfer', t.transfer || '转账']].map(([key, label]) => {
                    const on = (tx.type || 'expense') === key;
                    return (
                      <button key={key} type="button" onClick={() => setEditTxnMobile({ ...tx, type: key, category_id: key === 'transfer' ? null : tx.category_id })}
                        style={{ flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: on ? 600 : 500,
                          background: on ? (key === 'income' ? C.incomeSoft : key === 'transfer' ? C.transferSoft : C.expenseSoft) : 'transparent',
                          color: on ? (key === 'income' ? C.income : key === 'transfer' ? C.transfer : C.expense) : C.inkSoft }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </FormField>
            )}
            <FormField label={t.dateLabel}>
              <input type="date" value={tx.date} onChange={e => setEditTxnMobile({ ...tx, date: e.target.value })} style={S.input} />
            </FormField>
            <FormField label={isCrypto ? (lang === 'zh' ? '数量' : 'Quantity') : t.amountLabel}>
              <input type="number" inputMode="decimal"
                value={tx._newAmount !== undefined ? tx._newAmount : (isCrypto ? (tx.crypto_quantity ?? '') : tx.amount)}
                onChange={e => setEditTxnMobile({ ...tx, _newAmount: e.target.value })} style={S.input} />
            </FormField>
            {tx.type === 'transfer' ? (
              <FormField label={isAlreadyTransfer ? (lang === 'zh' ? '转账账户' : 'Other account') : (lang === 'zh' ? '转账到' : 'Transfer to')}>
                <select value={tx.transfer_to_account_id || ''} onChange={e => setEditTxnMobile({ ...tx, transfer_to_account_id: e.target.value })} style={S.input}>
                  <option value="">-- {lang === 'zh' ? '选择账户' : 'Select account'} --</option>
                  {accounts.filter(a => a.id !== tx.account_id).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </FormField>
            ) : (!isCrypto && (
              <FormField label={t.category}>
                <select value={tx.category_id || ''} onChange={e => setEditTxnMobile({ ...tx, category_id: e.target.value })} style={S.input}>
                  <option value="">-- {lang === 'zh' ? '选择分类' : 'Select'} --</option>
                  {getCategoryOptions(tx.type).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </FormField>
            ))}
            <FormField label={t.merchantLabel}>
              <input type="text" value={tx.description || ''} onChange={e => setEditTxnMobile({ ...tx, description: e.target.value })} style={S.input} />
            </FormField>
            <FormField label={lang === 'zh' ? '备注' : 'Note'}>
              <input type="text" value={tx.note || ''} onChange={e => setEditTxnMobile({ ...tx, note: e.target.value })} style={S.input} />
            </FormField>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <button type="button"
                onClick={async () => { await deleteTransaction({ ...tx, ...(tx._original || {}) }); setEditTxnMobile(null); }}
                style={{ padding: '9px 16px', background: 'none', border: `1.5px solid ${C.expense}`, borderRadius: 12, color: C.expense, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                {lang === 'zh' ? '删除' : 'Delete'}
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setEditTxnMobile(null)} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
                <button type="button" className="em-btn-primary" style={S.btn(true)}
                  onClick={async () => {
                    if (tx.type === 'transfer' && !tx.transfer_to_account_id) {
                      alert(lang === 'zh' ? '请选择转账到哪个账户。' : 'Please choose a destination account.');
                      return;
                    }
                    const updated = {
                      date: tx.date,
                      description: tx.description,
                      note: tx.note,
                      category_id: tx.category_id,
                      type: tx.type, // 用户想要的新类型
                      transfer_to_account_id: tx.transfer_to_account_id, // 用户想要的新转账对方账户
                    };
                    if (tx._newAmount !== undefined && tx._newAmount !== '') {
                      if (isCrypto) {
                        const qty = parseFloat(tx._newAmount);
                        const price = cryptoPrices[acct.crypto_symbol] || 0;
                        updated.amount = qty * price;
                        await supabase.from('transactions').update({ crypto_quantity: qty }).eq('id', tx.id);
                      } else {
                        updated.amount = parseFloat(tx._newAmount);
                      }
                    }
                    // saveEditTxn 需要"原始交易"(第一参数)和"新值"(第二参数, updated)分开比较，
                    // 但 editTxnMobile 是同一个状态对象在编辑过程中被直接改写，
                    // 所以这里要用打开弹窗时存下的 _original 快照还原出真正未被编辑过的原始对象
                    const originalTxn = { ...tx, ...(tx._original || {}) };
                    await saveEditTxn(originalTxn, updated);
                    setEditTxnMobile(null);
                  }}>{t.save}</button>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* ── Recurring Transaction Modal ── */}
      {showRecurringModal && (
        <Modal title={editingRecurring ? (lang === 'zh' ? '编辑定期交易' : 'Edit Schedule') : t.addRecurring} onClose={() => { setShowRecurringModal(false); setEditingRecurring(null); }}>
          {/* Type */}
          <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 11, overflow: 'hidden', marginBottom: 14 }}>
            {[['expense', t.expense || '支出'], ['income', t.income || '收入'], ['transfer', t.transfer || '转账']].map(([key, label]) => {
              const on = recurForm.type === key;
              return (
                <button key={key} type="button" onClick={() => setRecurForm({ ...recurForm, type: key })}
                  style={{ flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: on ? 600 : 500,
                    background: on ? (key === 'income' ? C.incomeSoft : key === 'transfer' ? C.gradSoft : 'rgba(255,107,91,0.1)') : 'transparent',
                    color: on ? (key === 'income' ? C.income : key === 'transfer' ? C.primary : C.expense) : C.inkSoft }}>
                  {label}
                </button>
              );
            })}
          </div>
          <FormField label={t.amountLabel || '金额'}>
            <input type="number" value={recurForm.amount} onChange={e => setRecurForm({ ...recurForm, amount: e.target.value })} placeholder="0" style={S.input} />
          </FormField>
          <FormField label={recurForm.type === 'transfer' ? (lang === 'zh' ? '转出账户' : 'From') : (lang === 'zh' ? '账户' : 'Account')}>
            <select value={recurForm.account_id} onChange={e => setRecurForm({ ...recurForm, account_id: e.target.value })} style={S.input}>
              <option value="">--</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </FormField>
          {recurForm.type === 'transfer' && (
            <FormField label={lang === 'zh' ? '转入账户' : 'To'}>
              <select value={recurForm.transfer_to_account_id} onChange={e => setRecurForm({ ...recurForm, transfer_to_account_id: e.target.value })} style={S.input}>
                <option value="">--</option>
                {accounts.filter(a => a.id !== recurForm.account_id).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </FormField>
          )}
          {recurForm.type !== 'transfer' && (
            <FormField label={t.category}>
              <select value={recurForm.category_id} onChange={e => setRecurForm({ ...recurForm, category_id: e.target.value })} style={S.input}>
                <option value="">--</option>
                {getCategoryOptions(recurForm.type).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </FormField>
          )}
          <FormField label={(lang === 'zh' ? '备注' : 'Description') + (lang === 'zh' ? '（选填）' : ' (optional)')}>
            <input type="text" value={recurForm.description} onChange={e => setRecurForm({ ...recurForm, description: e.target.value })} placeholder={lang === 'zh' ? '如 房租、工资' : 'e.g. Rent, Salary'} style={S.input} />
          </FormField>
          {/* Period */}
          <FormField label={t.budgetPeriod}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {[['daily', t.periodDaily], ['weekly', t.periodWeekly], ['monthly', t.periodMonthly], ['yearly', t.periodYearly]].map(([key, label]) => {
                const on = recurForm.period_type === key;
                return (
                  <button key={key} type="button" onClick={() => setRecurForm({ ...recurForm, period_type: key })}
                    style={{ flex: 1, padding: '8px 0', borderRadius: 9, cursor: 'pointer', fontSize: 12,
                      border: on ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                      background: on ? C.grad : C.card, color: on ? '#fff' : C.inkSoft, fontWeight: on ? 600 : 500 }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </FormField>
          {/* Period detail based on type */}
          {recurForm.period_type === 'weekly' && (
            <FormField label={lang === 'zh' ? '星期几' : 'Day of week'}>
              <select value={recurForm.period_day} onChange={e => setRecurForm({ ...recurForm, period_day: parseInt(e.target.value) })} style={S.input}>
                {[0, 1, 2, 3, 4, 5, 6].map(d => (
                  <option key={d} value={d}>{lang === 'zh' ? `星期${t.weekdayNames[d]}` : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d]}</option>
                ))}
              </select>
            </FormField>
          )}
          {recurForm.period_type === 'monthly' && (
            <FormField label={t.everyMonthDay}>
              <select value={recurForm.period_day} onChange={e => setRecurForm({ ...recurForm, period_day: parseInt(e.target.value) })} style={S.input}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}{t.recurDay}</option>)}
              </select>
            </FormField>
          )}
          {recurForm.period_type === 'yearly' && (
            <div style={{ display: 'flex', gap: 10 }}>
              <FormField label={lang === 'zh' ? '月' : 'Month'}>
                <select value={recurForm.period_month} onChange={e => setRecurForm({ ...recurForm, period_month: parseInt(e.target.value) })} style={S.input}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </FormField>
              <FormField label={lang === 'zh' ? '日' : 'Day'}>
                <select value={recurForm.period_day} onChange={e => setRecurForm({ ...recurForm, period_day: parseInt(e.target.value) })} style={S.input}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </FormField>
            </div>
          )}
          <FormField label={t.advanceDays}>
            <input type="number" value={recurForm.advance_days} onChange={e => setRecurForm({ ...recurForm, advance_days: e.target.value })} placeholder="0" style={S.input} />
            <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 4 }}>{t.advanceHint}</div>
          </FormField>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" onClick={() => { setShowRecurringModal(false); setEditingRecurring(null); }} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
            <button type="button" onClick={saveRecurring} className="em-btn-primary" style={S.btn(true)}>{t.save}</button>
          </div>
        </Modal>
      )}

      {/* ── Budget Add/Edit Modal ── */}
      {showBudgetModal && (
        <Modal title={editingBudget ? (lang === 'zh' ? '编辑预算' : 'Edit Budget') : t.addBudget} onClose={() => { setShowBudgetModal(false); setEditingBudget(null); }}>
          <FormField label={t.budgetCategory}>
            <select value={budgetForm.category_id} onChange={e => setBudgetForm({ ...budgetForm, category_id: e.target.value })} style={S.input}>
              <option value="">{t.budgetTotal}</option>
              {categories.filter(c => (c.type === 'expense' || c.type === 'both') && !c.parent_id).map(c => (
                <option key={c.id} value={c.id}>{getCatName(c.id)}</option>
              ))}
            </select>
          </FormField>
          <FormField label={t.budgetName + (lang === 'zh' ? '（选填）' : ' (optional)')}>
            <input type="text" value={budgetForm.name} onChange={e => setBudgetForm({ ...budgetForm, name: e.target.value })}
              placeholder={lang === 'zh' ? '如 日常开销' : 'e.g. Daily spending'} style={S.input} />
          </FormField>
          <FormField label={`${t.budgetAmount} (${defaultCurrency.code})`}>
            <input type="number" value={budgetForm.amount} onChange={e => setBudgetForm({ ...budgetForm, amount: e.target.value })} placeholder="0" style={S.input} />
          </FormField>
          <FormField label={t.budgetPeriod}>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['week', t.periodWeek], ['month', t.periodMonth], ['year', t.periodYear]].map(([key, label]) => {
                const on = budgetForm.period === key;
                return (
                  <button key={key} type="button" onClick={() => setBudgetForm({ ...budgetForm, period: key })}
                    style={{ flex: 1, padding: '9px 0', borderRadius: 10, cursor: 'pointer', fontSize: 13,
                      border: on ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                      background: on ? C.grad : C.card, color: on ? '#fff' : C.inkSoft, fontWeight: on ? 600 : 500 }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </FormField>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" onClick={() => { setShowBudgetModal(false); setEditingBudget(null); }} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
            <button type="button" onClick={saveBudget} className="em-btn-primary" style={S.btn(true)}>{t.save}</button>
          </div>
        </Modal>
      )}

      {/* ── Crypto Add/Buy/Sell Modal ── */}
      {showCryptoModal && (() => {
        const mode = cryptoModalMode;
        const h = cryptoModalHolding;
        const coinLabel = cryptoForm.ticker ? cryptoForm.ticker.toUpperCase() : '';
        const title = mode === 'add' ? (lang === 'zh' ? '添加币种' : 'Add Coin')
          : mode === 'buy' ? `${t.buyStock} ${coinLabel}` : `${t.sellStock} ${coinLabel}`;
        return (
          <Modal title={title} onClose={() => { setShowCryptoModal(false); setHoldingSearchResults([]); }}>
            {mode === 'add' && (
              <FormField label={lang === 'zh' ? '搜索币种' : 'Search coin'}>
                {cryptoForm.coin_id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: `2px solid ${C.income}`, borderRadius: 10, background: C.incomeSoft }}>
                    <span style={{ fontWeight: 700, color: C.income }}>{cryptoForm.ticker.toUpperCase()}</span>
                    <span style={{ color: C.inkSoft, fontSize: 12 }}>{cryptoForm.coin_name}</span>
                    <button type="button" onClick={() => setCryptoForm({ ...cryptoForm, coin_id: '', ticker: '', coin_name: '' })}
                      style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
                  </div>
                ) : (
                  <>
                    <input type="text" placeholder={lang === 'zh' ? '如 BTC、ethereum...' : 'e.g. BTC, ethereum...'}
                      onChange={e => searchCryptoForHolding(e.target.value)} style={S.input} />
                    {holdingSearchResults.length > 0 && (
                      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 4, overflow: 'hidden', background: C.card, boxShadow: C.shadowMd }}>
                        {holdingSearchResults.map(c => (
                          <div key={c.id}
                            onClick={() => { setCryptoForm({ ...cryptoForm, coin_id: c.id, ticker: c.symbol, coin_name: c.name }); setHoldingSearchResults([]); }}
                            style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${C.border}` }}>
                            {c.thumb && <img src={c.thumb} alt={c.name} style={{ width: 20, height: 20, borderRadius: '50%' }} />}
                            <span style={{ fontWeight: 600 }}>{c.symbol}</span>
                            <span style={{ color: C.inkSoft }}>{c.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </FormField>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <FormField label={lang === 'zh' ? '数量' : 'Amount'}>
                <input type="number" value={cryptoForm.amount} onChange={e => {
                  const amount = e.target.value;
                  const amt = parseFloat(amount);
                  let { price, total } = cryptoForm;
                  if (amt > 0 && parseFloat(price) > 0) {
                    total = String(amt * parseFloat(price));
                  } else if (amt > 0 && parseFloat(total) > 0) {
                    price = String(parseFloat(total) / amt);
                  }
                  setCryptoForm({ ...cryptoForm, amount, price, total });
                }} placeholder="0.00" style={S.input} />
              </FormField>
              <FormField label={`${lang === 'zh' ? '单价' : 'Price'} (${defaultCurrency.code})`}>
                <input type="number" value={cryptoForm.price} onChange={e => {
                  const price = e.target.value;
                  const pr = parseFloat(price);
                  const amt = parseFloat(cryptoForm.amount);
                  let total = cryptoForm.total;
                  if (pr > 0 && amt > 0) total = String(amt * pr);
                  setCryptoForm({ ...cryptoForm, price, total });
                }} placeholder="0.00" style={S.input} />
              </FormField>
            </div>
            <FormField label={`${lang === 'zh' ? '总金额（含手续费，填这个自动算单价）' : 'Total (incl. fees — auto-fills price)'} (${defaultCurrency.code})`}>
              <input type="number" value={cryptoForm.total} onChange={e => {
                const total = e.target.value;
                const tot = parseFloat(total);
                const amt = parseFloat(cryptoForm.amount);
                let price = cryptoForm.price;
                if (tot > 0 && amt > 0) price = String(tot / amt);
                setCryptoForm({ ...cryptoForm, total, price });
              }} placeholder="0.00" style={S.input} />
            </FormField>
            {cryptoForm.coin_id && cryptoPrices[cryptoForm.coin_id] && (
              <div style={{ fontSize: 11.5, color: C.inkFaint, marginBottom: 10 }}>
                {lang === 'zh' ? '当前价' : 'Current'}: {defaultCurrency.symbol}{cryptoPrices[cryptoForm.coin_id].toLocaleString()}
                <button type="button" onClick={() => {
                  const p = cryptoPrices[cryptoForm.coin_id];
                  const amt = parseFloat(cryptoForm.amount);
                  setCryptoForm({ ...cryptoForm, price: String(p), total: amt > 0 ? String(amt * p) : cryptoForm.total });
                }}
                  style={{ marginLeft: 8, background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: C.primary }}>
                  {lang === 'zh' ? '用现价' : 'Use'}
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setShowCryptoModal(false); setHoldingSearchResults([]); }} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
              <button type="button" disabled={cryptoSaving} className="em-btn-primary" style={S.btn(true)}
                onClick={() => {
                  if (mode === 'add') addCryptoHolding(selectedAccount.id);
                  else if (mode === 'buy') buyMoreCrypto(h);
                  else sellCrypto(h);
                }}>
                {cryptoSaving ? '...' : (mode === 'sell' ? t.sellStock : (mode === 'buy' ? t.buyStock : (lang === 'zh' ? '添加' : 'Add')))}
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Stock Add/Buy/Sell Modal ── */}
      {showStockModal && (() => {
        const mode = stockModalMode; // add | buy | sell
        const h = stockModalHolding;
        const title = mode === 'add' ? t.addStock : mode === 'buy' ? `${t.buyStock} ${h?.symbol}` : `${t.sellStock} ${h?.symbol}`;
        return (
          <Modal title={title} onClose={() => setShowStockModal(false)}>
            {mode === 'add' && (
              <>
                <FormField label={t.stockMarket}>
                  <select value={stockForm.market} onChange={e => setStockForm({ ...stockForm, market: e.target.value })} style={S.input}>
                    <option value="US">{t.usMarket}</option>
                    <option value="CN">{t.cnMarket}</option>
                    <option value="HK">{t.hkMarket}</option>
                  </select>
                </FormField>
                <FormField label={t.stockSymbol}>
                  <input type="text" value={stockForm.symbol} onChange={e => setStockForm({ ...stockForm, symbol: e.target.value })}
                    placeholder={stockForm.market === 'US' ? 'AAPL, TSLA, NVDA...' : (stockForm.market === 'CN' ? '600519' : '0700')} style={S.input} />
                </FormField>
                <FormField label={t.companyName + (lang === 'zh' ? '（选填）' : ' (optional)')}>
                  <input type="text" value={stockForm.company} onChange={e => setStockForm({ ...stockForm, company: e.target.value })}
                    placeholder={lang === 'zh' ? '如 苹果公司' : 'e.g. Apple Inc.'} style={S.input} />
                </FormField>
                {stockForm.market !== 'US' && (
                  <div style={{ fontSize: 11.5, color: C.inkFaint, marginBottom: 12, background: C.gradSoft, borderRadius: 8, padding: '8px 10px' }}>
                    {lang === 'zh' ? '非美股暂不支持实时价格，将使用你填的价格作为现价，可在持仓里手动更新。' : 'Non-US stocks use the price you enter; update manually later.'}
                  </div>
                )}
              </>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <FormField label={t.shares}>
                <input type="number" value={stockForm.shares} onChange={e => setStockForm({ ...stockForm, shares: e.target.value })} placeholder="0" style={S.input} />
              </FormField>
              <FormField label={t.pricePerShare}>
                <input type="number" value={stockForm.price} onChange={e => setStockForm({ ...stockForm, price: e.target.value })} placeholder="0.00" style={S.input} />
              </FormField>
            </div>
            {stockForm.shares && stockForm.price && (
              <div style={{ fontSize: 13, color: C.inkSoft, marginBottom: 14, textAlign: 'center' }}>
                {mode === 'sell' ? (lang === 'zh' ? '卖出金额' : 'Proceeds') : (lang === 'zh' ? '买入金额' : 'Cost')}:
                <span style={{ fontWeight: 700, marginLeft: 6, color: C.ink }}>
                  {currentAcctCurrency.symbol}{(parseFloat(stockForm.shares) * parseFloat(stockForm.price)).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowStockModal(false)} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
              <button type="button" disabled={stockSaving} className="em-btn-primary" style={S.btn(true)}
                onClick={() => {
                  if (mode === 'add') addStockHolding(selectedAccount.id);
                  else if (mode === 'buy') buyMoreStock(h);
                  else sellStock(h);
                }}>
                {stockSaving ? '...' : (mode === 'sell' ? t.sellStock : (mode === 'buy' ? t.buyStock : t.addStock))}
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Add Account Modal ── */}
      {showAddAccount && (
        <Modal title={lang === 'zh' ? '新增账户' : 'Add Account'} onClose={() => setShowAddAccount(false)}>
          <FormField label={t.acctName}>
            <input value={newAcctName} onChange={e => setNewAcctName(e.target.value)} style={S.input} />
          </FormField>

          <FormField label={lang === 'zh' ? '账号（选填）' : 'Account Number (optional)'}>
            <input value={newAcctNumber} onChange={e => setNewAcctNumber(e.target.value)} placeholder={lang === 'zh' ? '如银行卡号、信用卡号' : 'e.g. card number'} style={S.input} />
          </FormField>

          <FormField label={t.accountCurrency}>
            <select value={newAcctCurrency} onChange={e => setNewAcctCurrency(e.target.value)} style={S.input}>
              <option value="">{t.useDefault} ({defaultCurrency.code})</option>
              {currencies.map(c => <option key={c.code} value={c.code}>{c.symbol} {lang === 'zh' ? c.name_zh : c.name_en} ({c.code})</option>)}
            </select>
          </FormField>

          <FormField label={t.acctType}>
            <select value={newAcctType} onChange={e => { setNewAcctType(e.target.value); setNewCryptoTicker(''); setNewCreditLimit(''); setNewAcctBalance(''); }} style={S.input}>
              <option value="checking">{t.checking}</option>
              <option value="savings">{t.savings}</option>
              <option value="cash">{t.cash}</option>
              <option value="credit">{t.credit}</option>
              <option value="crypto">{t.crypto}</option>
              <option value="stock">{t.stock}</option>
            </select>
          </FormField>

          {/* Credit card: limit + current debt */}
          {newAcctType === 'credit' && (
            <>
              <FormField label={t.creditLimit}>
                <input type="number" value={newCreditLimit} onChange={e => setNewCreditLimit(e.target.value)} placeholder="0" style={S.input} />
              </FormField>
              <FormField label={t.owed}>
                <input type="number" value={newAcctBalance} onChange={e => setNewAcctBalance(e.target.value)} placeholder="0" style={S.input} />
                {newCreditLimit && (
                  <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 4 }}>
                    {t.availableCredit}: {(defaultCurrency.symbol)}{(parseFloat(newCreditLimit || 0) - parseFloat(newAcctBalance || 0)).toLocaleString()}
                  </div>
                )}
              </FormField>
            </>
          )}

          {/* Normal account (non-crypto, non-credit): initial balance */}
          {newAcctType !== 'crypto' && newAcctType !== 'credit' && newAcctType !== 'stock' && (
            <FormField label={t.initialBalance}>
              <input type="number" value={newAcctBalance} onChange={e => setNewAcctBalance(e.target.value)} placeholder="0" style={S.input} />
            </FormField>
          )}

          {newAcctType === 'stock' && (
            <div style={{ fontSize: 12.5, color: C.inkFaint, background: C.gradSoft, borderRadius: 10, padding: '10px 12px', marginBottom: 14, lineHeight: 1.5 }}>
              {lang === 'zh'
                ? '创建后进入该账户，点"添加股票"录入持仓。余额会根据股价自动计算。'
                : 'After creating, open the account and tap "Add Stock" to add holdings. Balance is calculated from stock prices.'}
            </div>
          )}

          {newAcctType === 'crypto' && (
            <div style={{ fontSize: 12.5, color: C.inkFaint, background: C.gradSoft, borderRadius: 10, padding: '10px 12px', marginBottom: 14, lineHeight: 1.5 }}>
              {lang === 'zh'
                ? '创建后进入该钱包，点"添加币种"录入持仓。余额会根据币价自动计算。'
                : 'After creating, open the wallet and tap "Add Coin" to add holdings. Balance is calculated from live prices.'}
            </div>
          )}

          <FormField label={t.color}>
            <input type="color" value={newAcctColor} onChange={e => setNewAcctColor(e.target.value)}
              style={{ width: 40, height: 32, padding: 2, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer' }} />
          </FormField>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={() => setShowAddAccount(false)} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
            <button type="button" onClick={addAccount} className="em-btn-primary" style={S.btn(true)}>{t.save}</button>
          </div>
        </Modal>
      )}

      {/* ── Edit Account Modal ── */}
      {showEditAccount && editAcct && (
        <Modal title={lang === 'zh' ? '编辑账户' : 'Edit Account'} onClose={() => { setShowEditAccount(false); setEditAcct(null); }}>
          <FormField label={t.acctName}>
            <input value={editAcct.name} onChange={e => setEditAcct({ ...editAcct, name: e.target.value })} style={S.input} />
          </FormField>

          <FormField label={lang === 'zh' ? '账号' : 'Account Number'}>
            <input value={editAcct.account_number} onChange={e => setEditAcct({ ...editAcct, account_number: e.target.value })}
              placeholder={lang === 'zh' ? '完整号码（显示时只露后四位）' : 'Full number (shown masked)'} style={S.input} />
          </FormField>

          {/* Credit card: edit limit and current debt */}
          {editAcct.account_type === 'credit' && (
            <>
              <FormField label={t.creditLimit}>
                <input type="number" value={editAcct.credit_limit} onChange={e => setEditAcct({ ...editAcct, credit_limit: e.target.value })} placeholder="0" style={S.input} />
              </FormField>
              <FormField label={t.owed}>
                <input type="number" value={editAcct.current_debt} onChange={e => setEditAcct({ ...editAcct, current_debt: e.target.value })} placeholder="0" style={S.input} />
                {editAcct.credit_limit && (
                  <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 4 }}>
                    {t.availableCredit}: {defaultCurrency.symbol}{(parseFloat(editAcct.credit_limit || 0) - parseFloat(editAcct.current_debt || 0)).toLocaleString()}
                  </div>
                )}
              </FormField>
            </>
          )}

          <FormField label={t.color}>
            <input type="color" value={editAcct.color} onChange={e => setEditAcct({ ...editAcct, color: e.target.value })}
              style={{ width: 44, height: 34, padding: 2, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', background: C.card }} />
          </FormField>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={() => { setShowEditAccount(false); setEditAcct(null); }} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
            <button type="button" onClick={saveEditAccount} className="em-btn-primary" style={S.btn(true)}>{t.save}</button>
          </div>
        </Modal>
      )}

      {/* ── Add Category Modal ── */}
      {showAddCategory && (
        <Modal title={lang === 'zh' ? '新增分类' : 'Add Category'} onClose={() => setShowAddCategory(false)}>
          <FormField label={t.categoryName}><input value={newCatZh} onChange={e => setNewCatZh(e.target.value)} style={S.input} /></FormField>
          <FormField label={t.categoryNameEn}><input value={newCatEn} onChange={e => setNewCatEn(e.target.value)} style={S.input} /></FormField>

          <FormField label={t.parentCategory}>
            <select value={newCatParent} onChange={e => setNewCatParent(e.target.value)} style={S.input}>
              <option value="">{t.noParent}</option>
              {categories.filter(c => !c.parent_id).map(c => (
                <option key={c.id} value={c.id}>
                  {lang === 'zh' ? c.name_zh : c.name_en}
                  {' '}({c.type === 'expense' ? t.expense : c.type === 'income' ? t.income : t.both})
                </option>
              ))}
            </select>
          </FormField>

          {/* Type selector only shown for top-level categories; subcategories inherit parent's type */}
          {!newCatParent && (
            <FormField label={t.categoryType}>
              <select value={newCatType} onChange={e => setNewCatType(e.target.value)} style={S.input}>
                <option value="expense">{t.expense}</option>
                <option value="income">{t.income}</option>
                <option value="both">{t.both}</option>
              </select>
            </FormField>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={() => { setShowAddCategory(false); setNewCatParent(''); setNewCatType('expense'); }} className="em-btn-ghost" style={S.btn()}>{t.cancel}</button>
            <button type="button" onClick={addCategory} className="em-btn-primary" style={S.btn(true)}>{t.save}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── TxnTable ────────────────────────────────────────────────────────────────
function TxnTable({ txns, t, lang, defaultCurrency, currencies, categories, accounts, getCatName, getAcctName, getCategoryOptions, onDelete, onSave, onEditMobile, onConfirmPending, editingTxn, setEditingTxn, showAccount, hideBalance = false, initialBalance = 0, cryptoPrices = {}, onInlineAdd, inlineAccountId, inlineIsCrypto, inlineCryptoSymbol, inlineCryptoTicker, isCreditAccount = false }) {
  const [editData, setEditData] = useState({});

  // Inline add row state
  const today = new Date().toISOString().split('T')[0];
  const [inlineDate, setInlineDate] = useState(today);
  const [inlineType, setInlineType] = useState('expense');
  const [inlineAmount, setInlineAmount] = useState('');
  const [inlineDesc, setInlineDesc] = useState('');
  const [inlineNote, setInlineNote] = useState('');
  const [inlineCatId, setInlineCatId] = useState('');

  async function submitInline() {
    if (!inlineAmount || parseFloat(inlineAmount) <= 0) return;
    if (!inlineIsCrypto && !inlineCatId) return;

    // Temporarily set the global state needed by addTransaction
    // Instead, we call supabase directly here for cleanliness
    const { supabase } = await import('./supabase');
    const isCrypto = inlineIsCrypto;
    const coinQty = parseFloat(inlineAmount);
    let parsedAmount = coinQty;

    if (isCrypto) {
      const price = cryptoPrices[inlineCryptoSymbol] || 0;
      parsedAmount = coinQty * price;
      // Update crypto_amount on account
      const acct = accounts.find(a => a.id === inlineAccountId);
      const currentQty = parseFloat(acct?.crypto_amount || 0);
      const newQty = inlineType === 'income' ? currentQty + coinQty : currentQty - coinQty;
      await supabase.from('accounts').update({ crypto_amount: newQty }).eq('id', inlineAccountId);
    }

    const { data: authData } = await supabase.auth.getUser();
    const uid = authData?.user?.id;

    const { error } = await supabase.from('transactions').insert({
      date: inlineDate,
      description: inlineDesc || '',
      note: inlineNote || '',
      category_id: inlineCatId || null,
      account_id: inlineAccountId,
      type: inlineType,
      amount: parsedAmount,
      crypto_quantity: isCrypto ? coinQty : null,
      user_id: uid,
    });

    if (!error) {
      const acct = accounts.find(a => a.id === inlineAccountId);
      if (acct) {
        const newBalance = inlineType === 'income'
          ? parseFloat(acct.balance) + parsedAmount
          : parseFloat(acct.balance) - parsedAmount;
        await supabase.from('accounts').update({ balance: newBalance }).eq('id', inlineAccountId);
      }
      // Reset inline row
      setInlineDate(today);
      setInlineType('expense');
      setInlineAmount('');
      setInlineDesc('');
      setInlineNote('');
      setInlineCatId('');
      // Trigger reload via onInlineAdd callback
      if (onInlineAdd) onInlineAdd();
    }
  }

  function getCurrForAcct(acctId) {
    const acct = accounts.find(a => a.id === acctId);
    if (!acct) return defaultCurrency;
    return currencies.find(c => c.code === acct.currency_code) || defaultCurrency;
  }

  function startEdit(tx) {
    setEditingTxn(tx.id);
    setEditData({ date: tx.date, description: tx.description || '', note: tx.note || '', category_id: tx.category_id, account_id: tx.account_id, type: tx.type, amount: tx.amount, transfer_to_account_id: tx.transfer_to_account_id || '' });
  }

  function cancelEdit() { setEditingTxn(null); setEditData({}); }

  const txnsWithBalance = (() => {
    const sorted = [...txns].reverse();
    const totalImpact = sorted.reduce((s, tx) => {
      if (tx.type === 'transfer') return s;
      return tx.type === 'income' ? s + parseFloat(tx.amount) : s - parseFloat(tx.amount);
    }, 0);
    let running = initialBalance - totalImpact;
    const result = sorted.map(tx => {
      if (tx.type !== 'transfer') {
        running = tx.type === 'income' ? running + parseFloat(tx.amount) : running - parseFloat(tx.amount);
      }
      return { ...tx, runningBalance: running };
    });
    return result.reverse();
  })();

  const inputS = { padding: '5px 7px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, width: '100%', boxSizing: 'border-box', background: C.card, color: C.ink };

  // If there are no transactions AND no inline-add capability, show the empty message.
  // When inline add is available (account detail page), still render the table so the
  // inline add row is visible.
  if (txns.length === 0 && !onInlineAdd) {
    return <div style={{ color: C.inkFaint, fontSize: 13, padding: '12px 0' }}>{t.noTxn}</div>;
  }

  const mobile = typeof window !== 'undefined' && window.innerWidth <= 640;

  // Emoji matcher (same logic as the modal) for card icons
  const cardEmoji = (cat) => {
    if (!cat) return '🏷️';
    const txt = `${cat.name_zh || ''} ${cat.name_en || ''}`.toLowerCase();
    const map = [
      [['早餐', 'breakfast'], '🥐'], [['咖啡', 'coffee'], '☕'],
      [['餐', '吃', '饭', 'food', 'dining', 'eat', 'restaurant', 'meal'], '🍔'],
      [['交通', '车', 'transport', 'taxi', 'bus', 'car', 'fuel', '油'], '🚗'],
      [['购物', 'shop', 'shopping', 'buy'], '🛒'], [['娱乐', 'entertain', 'game', 'fun', 'movie', '电影'], '🎮'],
      [['医', '药', 'medical', 'health'], '💊'], [['房', '租', 'rent', 'house', 'home'], '🏠'],
      [['工资', '薪', 'salary', 'wage', 'pay'], '💰'], [['投资', 'invest', 'stock', '收益'], '📈'],
      [['旅', 'travel', 'trip', 'hotel', 'flight', '机票'], '✈️'], [['教育', '学', 'edu', 'school', 'book', '书'], '📚'],
      [['礼', 'gift'], '🎁'], [['宠物', 'pet'], '🐾'], [['运动', '健身', 'sport', 'gym'], '🏃'],
      [['通讯', '话费', '网', 'phone', 'bill', '账单', '水电'], '📱'], [['服', '衣', 'cloth'], '👕'],
      [['初始', 'opening', 'balance'], '🏦'], [['收入', 'income'], '💵'], [['其他', '其它', 'other'], '📦'],
    ];
    for (const [keys, emoji] of map) { if (keys.some(k => txt.includes(k))) return emoji; }
    return '🏷️';
  };

  // ── Mobile: card list instead of wide table ──
  if (mobile) {
    return (
      <div>
        {/* Inline quick-add hint for account pages on mobile: use the + button instead */}
        {txnsWithBalance.length === 0 && (
          <div style={{ color: C.inkFaint, fontSize: 13, padding: '12px 0', textAlign: 'center' }}>{t.noTxn}</div>
        )}
        {txnsWithBalance.map(tx => {
          const cat = categories.find(c => c.id === tx.category_id);
          const curr = getCurrForAcct(tx.account_id);
          const isTransfer = tx.type === 'transfer';
          const isIncome = tx.type === 'income';
          const amtColor = isTransfer ? C.transfer : isIncome ? C.income : C.expense;
          const sign = isTransfer ? '' : isIncome ? '+' : '-';
          const isCryptoTx = tx.crypto_quantity != null;
          const amtText = isCryptoTx
            ? `${sign}${parseFloat(tx.crypto_quantity).toLocaleString(undefined, { maximumFractionDigits: 8 })}`
            : `${sign}${curr.symbol}${parseFloat(tx.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
          const catLabel = isTransfer ? t.transfer : (cat ? (lang === 'zh' ? cat.name_zh : cat.name_en) : '-');
          const parts = [catLabel];
          if (showAccount) parts.push(getAcctName(tx.account_id));
          parts.push(tx.date);
          return (
            <div key={tx.id}
              style={{ background: tx.pending ? (C.mode === 'dark' ? 'rgba(245,196,81,0.18)' : 'rgba(232,163,23,0.14)') : C.card, border: tx.pending ? `1.5px solid ${C.warn}` : `1px solid ${C.border}`, borderLeft: tx.pending ? `4px solid ${C.warn}` : `1px solid ${C.border}`, borderRadius: 14, padding: '12px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: C.shadowMd }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: C.gradSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                  {isTransfer ? '⇄' : cardEmoji(cat)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tx.description || catLabel}
                    {tx.pending && <span style={{ marginLeft: 6, fontSize: 10, color: C.warn, border: `1px solid ${C.warn}`, borderRadius: 5, padding: '1px 5px', fontWeight: 600 }}>{t.pendingLabel}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {parts.join(' · ')}{tx.note ? ` · ${tx.note}` : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: amtColor }}>{amtText}</span>
                {tx.pending && onConfirmPending && (
                  <button type="button" onClick={() => onConfirmPending(tx)}
                    style={{ background: C.income, color: '#fff', border: 'none', borderRadius: 7, padding: '4px 9px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✓ {t.confirmBtn}</button>
                )}
                <button type="button" onClick={() => onEditMobile && onEditMobile(tx)}
                  style={{ background: 'none', border: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 15, padding: 2 }}>✎</button>
              </div>
            </div>
          );
        })}
        {/* Tapping a card's ✎ opens the edit modal (editTxnMobile), which has both Save and Delete. */}
      </div>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ color: C.inkFaint, fontSize: 12 }}>
          <th style={{ textAlign: 'left', paddingBottom: 8, width: 100 }}>{t.date}</th>
          <th style={{ textAlign: 'left', paddingBottom: 8 }}>{t.merchant}</th>
          <th style={{ textAlign: 'left', paddingBottom: 8 }}>{t.note}</th>
          <th style={{ textAlign: 'left', paddingBottom: 8, width: 110 }}>{t.category}</th>
          {showAccount && <th style={{ textAlign: 'left', paddingBottom: 8, width: 100 }}>{t.account}</th>}
          <th style={{ textAlign: 'right', paddingBottom: 8, width: 120, color: C.income }}>{isCreditAccount ? t.repay : t.income}</th>
          <th style={{ textAlign: 'right', paddingBottom: 8, width: 120, color: C.expense }}>{isCreditAccount ? t.spend : t.expense}</th>
          {!hideBalance && <th style={{ textAlign: 'right', paddingBottom: 8, width: 120 }}>{t.balance}</th>}
          <th style={{ width: 70 }}></th>
        </tr>
      </thead>
      <tbody>
        {txnsWithBalance.map(tx => {
          const txCurr = getCurrForAcct(tx.account_id);
          const isTransfer = tx.type === 'transfer';
          const isInvestment = !!(tx.stock_action || tx.crypto_action); // asset conversion
          const transferToName = isTransfer ? getAcctName(tx.transfer_to_account_id) : '';

          const renderCryptoCoins = (txAmt, acct, sign, tx) => {
            if (acct?.account_type === 'crypto' && acct?.crypto_symbol) {
              const ticker = acct.crypto_ticker || acct.crypto_symbol.toUpperCase();
              // Prefer stored crypto_quantity (exact), fall back to price-based calculation
              if (tx?.crypto_quantity != null && parseFloat(tx.crypto_quantity) > 0) {
                return `${sign}${parseFloat(tx.crypto_quantity).toLocaleString(undefined, { maximumFractionDigits: 8 })} ${ticker}`;
              }
              const price = cryptoPrices[acct.crypto_symbol] || 1;
              const coins = parseFloat(txAmt) / price;
              return `${sign}${coins.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${ticker}`;
            }
            return `${sign}${txCurr.symbol}${parseFloat(txAmt).toLocaleString()}`;
          };

          return (
            <tr key={tx.id} style={{ borderTop: `1px solid ${C.border}`, background: tx.pending ? (C.mode === 'dark' ? 'rgba(245,196,81,0.18)' : 'rgba(232,163,23,0.16)') : editingTxn === tx.id ? C.warnSoft : isTransfer ? C.transferSoft : isInvestment ? C.gradSoft : 'transparent', boxShadow: tx.pending ? `inset 4px 0 0 ${C.warn}` : 'none' }}>
              {editingTxn === tx.id ? (
                <>
                  <td style={{ padding: '6px 4px 6px 0' }}><input type="date" value={editData.date} onChange={e => setEditData({ ...editData, date: e.target.value })} style={inputS} /></td>
                  <td style={{ padding: '6px 4px' }}><input value={editData.description} onChange={e => setEditData({ ...editData, description: e.target.value })} style={inputS} /></td>
                  <td style={{ padding: '6px 4px' }}><input value={editData.note} onChange={e => setEditData({ ...editData, note: e.target.value })} style={inputS} /></td>
                  <td style={{ padding: '6px 4px' }}>
                    {editData.type === 'transfer' ? (
                      <span style={{ color: C.inkFaint, fontSize: 12 }}>-</span>
                    ) : (
                      <select value={editData.category_id} onChange={e => setEditData({ ...editData, category_id: e.target.value })} style={inputS}>
                        {(getCategoryOptions ? getCategoryOptions(editData.type) : categories.filter(c => c.type === editData.type || c.type === 'both').map(c => ({ id: c.id, label: lang === 'zh' ? c.name_zh : c.name_en }))).map(o => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  {showAccount && (
                    <td style={{ padding: '6px 4px' }}>
                      {editData.type === 'transfer' ? (
                        <span style={{ color: C.inkFaint, fontSize: 12 }}>{getAcctName(editData.account_id)}</span>
                      ) : (
                        <select value={editData.account_id} onChange={e => setEditData({ ...editData, account_id: e.target.value })} style={inputS}>
                          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                      )}
                    </td>
                  )}
                  <td colSpan={2} style={{ padding: '6px 4px' }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {tx.type === 'transfer' ? (
                        <span style={{ ...inputS, width: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.transfer, background: C.transferSoft, border: 'none' }}>
                          {lang === 'zh' ? '转账' : 'Transfer'}
                        </span>
                      ) : (
                        <select value={editData.type} onChange={e => setEditData({ ...editData, type: e.target.value })} style={{ ...inputS, width: 70 }}>
                          <option value="expense">{t.expense}</option>
                          <option value="income">{t.income}</option>
                          <option value="transfer">{lang === 'zh' ? '转账' : 'Transfer'}</option>
                        </select>
                      )}
                      <input type="number" value={editData.amount} onChange={e => setEditData({ ...editData, amount: e.target.value })} style={{ ...inputS, textAlign: 'right', flex: 1, minWidth: 60 }} />
                      {editData.type === 'transfer' && (
                        <select value={editData.transfer_to_account_id || ''} onChange={e => setEditData({ ...editData, transfer_to_account_id: e.target.value })} style={{ ...inputS, width: '100%' }}>
                          <option value="">{lang === 'zh' ? '-- 转账到 --' : '-- Transfer to --'}</option>
                          {accounts.filter(a => a.id !== editData.account_id).map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </td>
                  {!hideBalance && <td style={{ padding: '6px 4px', color: C.inkFaint, textAlign: 'right' }}>-</td>}
                  <td style={{ padding: '6px 0 6px 4px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button type="button" onClick={() => onSave(tx, editData)} style={{ background: C.income, color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>✓</button>
                      <button type="button" onClick={cancelEdit} style={{ background: C.border, color: C.inkSoft, border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>✕</button>
                      <button type="button" onClick={() => { onDelete(tx); cancelEdit(); }} style={{ background: 'none', color: C.expense, border: `1px solid ${C.expense}`, borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 12, marginLeft: 20 }}>{lang === 'zh' ? '删除' : 'Del'}</button>
                    </div>
                  </td>
                </>
              ) : (
                <>
                  <td style={{ padding: '9px 0', color: C.inkFaint, fontSize: 12 }}>{tx.date}</td>
                  <td style={{ padding: '9px 4px' }}>
                    {isTransfer
                      ? <span style={{ color: C.transfer, fontSize: 12 }}>⇄ {lang === 'zh' ? `转账 → ${transferToName}` : `Transfer → ${transferToName}`}</span>
                      : tx.description || <span style={{ color: C.inkFaint }}>-</span>}
                  </td>
                  <td style={{ padding: '9px 4px', color: C.inkSoft, fontSize: 12 }}>
                    {tx.note || <span style={{ color: C.inkFaint }}>-</span>}
                  </td>
                  <td style={{ padding: '9px 4px' }}>
                    {isTransfer
                      ? <span style={{ background: C.transferSoft, color: C.transfer, padding: '2px 8px', borderRadius: 20, fontSize: 11 }}>{t.transfer}</span>
                      : isInvestment
                      ? <span style={{ background: C.gradSoft, color: C.primary, padding: '2px 8px', borderRadius: 20, fontSize: 11 }}>📈 {lang === 'zh' ? '投资' : 'Invest'}</span>
                      : <span style={{ background: C.bgDeep, padding: '2px 8px', borderRadius: 20, fontSize: 11 }}>{getCatName(tx.category_id)}</span>}
                  </td>
                  {showAccount && <td style={{ padding: '9px 4px', color: C.inkSoft, fontSize: 12 }}>{getAcctName(tx.account_id)}</td>}
                  <td style={{ padding: '9px 4px', textAlign: 'right', color: isInvestment ? C.primary : C.income, fontWeight: 500 }}>
                    {tx.type === 'income' && renderCryptoCoins(tx.amount, accounts.find(a => a.id === tx.account_id), '+', tx)}
                    {isTransfer && !tx.is_transfer_out && <span style={{ color: C.transfer }}>+{txCurr.symbol}{parseFloat(tx.amount).toLocaleString()}</span>}
                  </td>
                  <td style={{ padding: '9px 4px', textAlign: 'right', color: isInvestment ? C.primary : C.expense, fontWeight: 500 }}>
                    {tx.type === 'expense' && renderCryptoCoins(tx.amount, accounts.find(a => a.id === tx.account_id), '-', tx)}
                    {isTransfer && tx.is_transfer_out && <span style={{ color: C.transfer }}>-{txCurr.symbol}{parseFloat(tx.amount).toLocaleString()}</span>}
                  </td>
                  {!hideBalance && (
                    <td style={{ padding: '9px 4px', textAlign: 'right', fontWeight: 500, color: tx.runningBalance >= 0 ? C.ink : C.expense }}>
                      {isTransfer ? '-' : `${txCurr.symbol}${parseFloat(tx.runningBalance).toLocaleString()}`}
                    </td>
                  )}
                  <td style={{ padding: '9px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {tx.pending && onConfirmPending && (
                      <button type="button" onClick={() => onConfirmPending(tx)}
                        style={{ background: C.income, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, marginRight: 6 }}>✓ {t.confirmBtn}</button>
                    )}
                    <button type="button" onClick={() => startEdit(tx)} style={{ background: 'none', border: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 13 }}>✎</button>
                  </td>
                </>
              )}
            </tr>
          );
        })}

        {/* Empty hint when there are no transactions yet (inline add still shows below) */}
        {txns.length === 0 && onInlineAdd && (
          <tr>
            <td colSpan={9} style={{ color: C.inkFaint, fontSize: 12, padding: '10px 0', textAlign: 'center' }}>
              {t.noTxn}
            </td>
          </tr>
        )}

        {/* ── Inline add row (account detail only) ── */}
        {onInlineAdd && (
          <tr style={{ borderTop: `2px dashed ${C.border}`, background: C.cardAlt }}>
            <td style={{ padding: '7px 4px 7px 0' }}>
              <input type="date" value={inlineDate} onChange={e => setInlineDate(e.target.value)}
                style={{ padding: '5px 6px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, width: '100%', boxSizing: 'border-box', background: C.card, color: C.ink }} />
            </td>
            <td style={{ padding: '7px 4px' }}>
              <input value={inlineDesc} onChange={e => setInlineDesc(e.target.value)}
                placeholder={lang === 'zh' ? '商家（选填）' : 'Merchant (optional)'}
                style={{ padding: '5px 6px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, width: '100%', boxSizing: 'border-box', background: C.card, color: C.ink }} />
            </td>
            <td style={{ padding: '7px 4px' }}>
              <input value={inlineNote} onChange={e => setInlineNote(e.target.value)}
                placeholder={lang === 'zh' ? '备注（选填）' : 'Note (optional)'}
                style={{ padding: '5px 6px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, width: '100%', boxSizing: 'border-box', background: C.card, color: C.ink }} />
            </td>
            <td style={{ padding: '7px 4px' }}>
              <select value={inlineCatId} onChange={e => setInlineCatId(e.target.value)}
                style={{ padding: '5px 6px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, width: '100%', boxSizing: 'border-box', background: C.card, color: C.ink }}>
                <option value="">{lang === 'zh' ? '-- 分类 --' : '-- Cat --'}</option>
                {(getCategoryOptions ? getCategoryOptions(inlineType) : categories.filter(c => c.type === inlineType || c.type === 'both').map(c => ({ id: c.id, label: lang === 'zh' ? c.name_zh : c.name_en }))).map(o => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </td>
            {showAccount && <td />}
            <td colSpan={2} style={{ padding: '7px 4px' }}>
              <div style={{ display: 'flex', gap: 4 }}>
                <select value={inlineType} onChange={e => setInlineType(e.target.value)}
                  style={{ padding: '5px 4px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, width: 68, boxSizing: 'border-box',
                    background: inlineType === 'income' ? C.incomeSoft : C.expenseSoft,
                    color: inlineType === 'income' ? C.income : C.expense }}>
                  <option value="expense">{isCreditAccount ? t.spend : (lang === 'zh' ? '支出' : 'Exp')}</option>
                  <option value="income">{isCreditAccount ? t.repay : (lang === 'zh' ? '收入' : 'Inc')}</option>
                </select>
                <input type="number" value={inlineAmount} onChange={e => setInlineAmount(e.target.value)}
                  placeholder={inlineIsCrypto
                    ? (lang === 'zh' ? `数量 (${inlineCryptoTicker || '枚'})` : `Qty (${inlineCryptoTicker || 'coins'})`)
                    : '0.00'}
                  style={{ padding: '5px 6px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, flex: 1, boxSizing: 'border-box', textAlign: 'right', background: C.card, color: C.ink }} />
              </div>
              {inlineIsCrypto && cryptoPrices[inlineCryptoSymbol] && inlineAmount && (
                <div style={{ fontSize: 10, color: C.inkFaint, marginTop: 2, textAlign: 'right' }}>
                  ≈ {defaultCurrency.symbol}{(parseFloat(inlineAmount) * cryptoPrices[inlineCryptoSymbol]).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              )}
            </td>
            {!hideBalance && <td />}
            <td style={{ padding: '7px 0', textAlign: 'right' }}>
              <button type="button" onClick={submitInline}
                style={{ background: C.income, color: '#fff', border: 'none', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                +
              </button>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// ─── ReportsView (rich financial reports) ────────────────────────────────────
function ReportsView({ transactions, accounts, categories, t, lang, isMobile, defaultCurrency, convertToDefault, getCurrencyForAccount, getCatName, reportRange, setReportRange }) {
  // Color palette for pie slices
  const PALETTE = ['#7C5CFC', '#5B7CFA', '#12B886', '#FF6B5B', '#E8A317', '#36C5F0', '#E255A1', '#9B8CFF', '#2DD4BF', '#F59E0B', '#EC4899', '#8B5CF6'];

  // Filter transactions by selected time range
  const now = new Date();
  const inRange = (tx) => {
    const d = new Date(tx.date);
    if (reportRange === 'month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    if (reportRange === 'lastMonth') {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return d.getFullYear() === lm.getFullYear() && d.getMonth() === lm.getMonth();
    }
    if (reportRange === 'year') return d.getFullYear() === now.getFullYear();
    return true; // all
  };
  const txns = transactions.filter(inRange);
  const amtIn = (tx) => convertToDefault(tx.amount, getCurrencyForAccount(accounts.find(a => a.id === tx.account_id)).code);
  // Helper: buying/selling stocks or crypto is asset conversion, not real income/expense
  const isInvest = (tx) => !!(tx.stock_action || tx.crypto_action);

  // Expense by category
  const expenseByCat = {};
  txns.filter(tx => tx.type === 'expense' && !isInvest(tx)).forEach(tx => {
    const name = getCatName(tx.category_id) || (lang === 'zh' ? '未分类' : 'Uncategorized');
    expenseByCat[name] = (expenseByCat[name] || 0) + amtIn(tx);
  });
  const expenseData = Object.entries(expenseByCat).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const totalExpense = expenseData.reduce((s, d) => s + d.value, 0);

  // Income by category/source
  const incomeByCat = {};
  txns.filter(tx => tx.type === 'income' && !isInvest(tx)).forEach(tx => {
    const name = getCatName(tx.category_id) || (lang === 'zh' ? '其他收入' : 'Other Income');
    incomeByCat[name] = (incomeByCat[name] || 0) + amtIn(tx);
  });
  const incomeData = Object.entries(incomeByCat).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const totalIncome = incomeData.reduce((s, d) => s + d.value, 0);

  // Account balance distribution (current balances, computed from transactions)
  const netByAcct = {};
  for (const tx of transactions) {
    if (!tx.account_id) continue;
    let d = 0;
    if (tx.type === 'income') d = parseFloat(tx.amount || 0);
    else if (tx.type === 'expense') d = -parseFloat(tx.amount || 0);
    else if (tx.type === 'transfer') d = tx.is_transfer_out ? -parseFloat(tx.amount || 0) : parseFloat(tx.amount || 0);
    netByAcct[tx.account_id] = (netByAcct[tx.account_id] || 0) + d;
  }
  const acctData = accounts.map(a => {
    const net = netByAcct[a.id] || 0;
    let bal;
    if (a.account_type === 'crypto') bal = parseFloat(a.balance || 0);
    else if (a.account_type === 'credit') bal = parseFloat(a.credit_limit || 0) - (parseFloat(a.credit_limit || 0) + net); // = -net = 欠款额
    else bal = net;
    const val = convertToDefault(Math.abs(bal), getCurrencyForAccount(a).code);
    return { name: a.name, value: val, color: a.color };
  }).filter(d => d.value > 0).sort((a, b) => b.value - a.value);

  const sym = defaultCurrency.symbol;
  const fmt = (n) => `${sym}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  const cardStyle = { background: C.card, borderRadius: 18, padding: isMobile ? 16 : 20, boxShadow: C.shadowMd, border: `1px solid ${C.border}`, marginBottom: 16 };
  const titleStyle = { fontWeight: 600, marginBottom: 16, fontSize: 14 };

  // A reusable donut + legend block
  const Donut = ({ data, total }) => {
    if (data.length === 0) return <div style={{ color: C.inkFaint, fontSize: 13, padding: '12px 0' }}>{t.rptNoData}</div>;
    return (
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: 16 }}>
        <div style={{ width: isMobile ? '100%' : 200, height: 180, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={75} paddingAngle={2}>
                {data.map((d, i) => <Cell key={i} fill={d.color || PALETTE[i % PALETTE.length]} stroke="none" />)}
              </Pie>
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, color: C.ink, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ flex: 1, width: '100%' }}>
          {data.map((d, i) => {
            const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
            return (
              <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color || PALETTE[i % PALETTE.length], flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                <span style={{ fontWeight: 600 }}>{fmt(d.value)}</span>
                <span style={{ color: C.inkFaint, fontSize: 12, width: 38, textAlign: 'right' }}>{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Time range filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['month', t.rptThisMonth], ['lastMonth', t.rptLastMonth], ['year', t.rptThisYear], ['all', t.rptAll]].map(([key, label]) => {
          const on = reportRange === key;
          return (
            <button key={key} type="button" onClick={() => setReportRange(key)}
              style={{
                flex: isMobile ? 1 : 'none', padding: '8px 16px', borderRadius: 11, cursor: 'pointer', fontSize: 13,
                border: on ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                background: on ? C.grad : C.card, color: on ? '#fff' : C.inkSoft,
                fontWeight: on ? 600 : 500, boxShadow: on ? C.glow : 'none', whiteSpace: 'nowrap',
              }}>
              {label}
            </button>
          );
        })}
      </div>

      {/* Income vs Expense summary */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: isMobile ? 10 : 14, marginBottom: 16 }}>
        <div style={{ ...cardStyle, marginBottom: 0 }}>
          <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 6 }}>{t.rptIncome}</div>
          <div style={{ fontSize: isMobile ? 17 : 20, fontWeight: 700, color: C.income }}>{fmt(totalIncome)}</div>
        </div>
        <div style={{ ...cardStyle, marginBottom: 0 }}>
          <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 6 }}>{t.rptExpense}</div>
          <div style={{ fontSize: isMobile ? 17 : 20, fontWeight: 700, color: C.expense }}>{fmt(totalExpense)}</div>
        </div>
        <div style={{ ...cardStyle, marginBottom: 0, gridColumn: isMobile ? '1 / -1' : 'auto' }}>
          <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 6 }}>{t.rptNetSavings}</div>
          <div style={{ fontSize: isMobile ? 17 : 20, fontWeight: 700, color: totalIncome - totalExpense >= 0 ? C.income : C.expense }}>{fmt(totalIncome - totalExpense)}</div>
        </div>
      </div>

      {/* Expense by category */}
      <div style={cardStyle}>
        <div style={titleStyle}>{t.rptExpenseByCat}</div>
        <Donut data={expenseData} total={totalExpense} />
      </div>

      {/* Income by source */}
      <div style={cardStyle}>
        <div style={titleStyle}>{t.rptIncomeBySource}</div>
        <Donut data={incomeData} total={totalIncome} />
      </div>

      {/* Account distribution */}
      <div style={cardStyle}>
        <div style={titleStyle}>{t.rptAccountDist}</div>
        <Donut data={acctData} total={acctData.reduce((s, d) => s + d.value, 0)} />
      </div>
    </div>
  );
}

// ─── CryptoHoldingsPanel ─────────────────────────────────────────────────────
function CryptoHoldingsPanel({ account, holdings, cryptoPrices, t, lang, isMobile, currency, trades, onAdd, onBuy, onSell, onDeleteHolding, onRefresh, onDeleteTrade }) {
  const sym = currency.symbol;
  const fmt = (n) => `${sym}${parseFloat(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtCoin = (n) => parseFloat(n || 0).toLocaleString(undefined, { maximumFractionDigits: 8 });

  // Totals
  let totalValue = 0, totalCost = 0;
  holdings.forEach(h => {
    const price = cryptoPrices[h.coin_id] || 0;
    totalValue += parseFloat(h.amount || 0) * price;
    totalCost += parseFloat(h.amount || 0) * parseFloat(h.avg_cost || 0);
  });
  const totalPnl = totalValue - totalCost;
  const totalPnlPct = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;

  return (
    <div>
      {/* Total value hero */}
      <div style={{ background: C.grad, borderRadius: 18, padding: isMobile ? '16px 18px' : '20px 24px', marginBottom: 14, color: '#fff', boxShadow: C.glow }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginBottom: 4 }}>{t.totalAssetsAcct}</div>
        <div className="display" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 10 }}>{fmt(totalValue)}</div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5 }}>
          <span>{t.totalCost}: <b>{fmt(totalCost)}</b></span>
          <span>{t.unrealizedPnl}:
            <b style={{ marginLeft: 4, color: totalPnl >= 0 ? '#5FFFD6' : '#FF8872' }}>
              {totalPnl >= 0 ? '▲ +' : '▼ '}{fmt(Math.abs(totalPnl))} ({totalPnl >= 0 ? '+' : ''}{totalPnlPct.toFixed(2)}%)
            </b>
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" onClick={onRefresh} style={{ flex: isMobile ? 1 : 'none', padding: '8px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, color: C.inkSoft }}>↻ {lang === 'zh' ? '刷新币价' : 'Refresh'}</button>
        <button type="button" onClick={onAdd} className="em-btn-primary" style={{ ...S.btn(true), flex: isMobile ? 1 : 'none', padding: '8px 14px', fontSize: 12.5 }}>+ {lang === 'zh' ? '添加币种' : 'Add Coin'}</button>
      </div>

      {/* Section label */}
      <div style={{ fontSize: 12, color: C.inkFaint, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '4px 2px 10px' }}>{t.stockHoldings}</div>

      {/* Holdings list */}
      {holdings.length === 0 ? (
        <div style={{ background: C.card, borderRadius: 18, padding: 24, textAlign: 'center', color: C.inkFaint, fontSize: 13.5, boxShadow: C.shadowMd, border: `1px solid ${C.border}`, marginBottom: 20 }}>
          {lang === 'zh' ? '暂无持仓，点"添加币种"开始' : 'No coins yet. Tap "Add Coin" to start'}
        </div>
      ) : (
        holdings.map(h => {
          const price = cryptoPrices[h.coin_id] || 0;
          const amount = parseFloat(h.amount || 0);
          const cost = parseFloat(h.avg_cost || 0);
          const value = amount * price;
          const pnl = (price - cost) * amount;
          const pnlPct = cost > 0 ? ((price - cost) / cost) * 100 : 0;
          return (
            <div key={h.id} style={{ background: C.card, borderRadius: 16, padding: 16, marginBottom: 10, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 16 }}>{h.ticker || h.coin_id}</span>
                    {h.coin_name && <span style={{ fontSize: 11.5, color: C.inkFaint }}>{h.coin_name}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(value)}</div>
                  <div style={{
                    display: 'inline-block', marginTop: 4,
                    fontSize: 12, fontWeight: 700,
                    color: pnl >= 0 ? C.income : C.expense,
                    background: pnl >= 0 ? C.incomeSoft : C.expenseSoft,
                    padding: '2px 8px', borderRadius: 8,
                  }}>
                    {pnl >= 0 ? '▲ +' : '▼ '}{fmt(Math.abs(pnl))} ({pnl >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: C.inkSoft, marginBottom: 12 }}>
                <span>{lang === 'zh' ? '数量' : 'Amount'}: <b style={{ color: C.ink }}>{fmtCoin(amount)}</b></span>
                <span>{t.avgCost}: <b style={{ color: C.ink }}>{fmt(cost)}</b></span>
                <span>{t.currentPrice}: <b style={{ color: C.ink }}>{price ? fmt(price) : '-'}</b></span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => onBuy(h)} style={{ flex: isMobile ? 1 : 'none', padding: '7px 14px', background: C.income, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{t.buyStock}</button>
                <button type="button" onClick={() => onSell(h)} style={{ flex: isMobile ? 1 : 'none', padding: '7px 14px', background: C.expense, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{t.sellStock}</button>
                <button type="button" onClick={() => onDeleteHolding(h)} style={{ padding: '7px 12px', background: 'none', color: C.inkFaint, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>🗑</button>
              </div>
            </div>
          );
        })
      )}

      {/* Trade history */}
      <div style={{ fontSize: 12, color: C.inkFaint, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '18px 2px 10px' }}>{t.tradeHistory}</div>
      <div style={{ background: C.card, borderRadius: 16, padding: 16, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
        {(!trades || trades.length === 0) ? (
          <div style={{ color: C.inkFaint, fontSize: 13, textAlign: 'center', padding: '8px 0' }}>{t.noTxn}</div>
        ) : (
          trades.map(tx => {
            const isBuy = tx.crypto_action === 'buy';
            const isSell = tx.crypto_action === 'sell';
            const isTransfer = tx.type === 'transfer';
            const isTransferIn = isTransfer && !tx.is_transfer_out;
            const isIncome = tx.type === 'income';
            const moneyIn = isSell || isIncome || isTransferIn;
            const color = moneyIn ? C.income : C.expense;
            const sign = moneyIn ? '+' : '-';
            let label = tx.description || (isTransfer ? (isTransferIn ? (lang === 'zh' ? '转入' : 'Transfer in') : (lang === 'zh' ? '转出' : 'Transfer out')) : '');
            let detail = tx.date;
            if (isBuy || isSell) {
              detail = `${tx.date} · ${parseFloat(tx.crypto_amount).toLocaleString(undefined, { maximumFractionDigits: 8 })} @ ${fmt(tx.crypto_price)}`;
            }
            return (
              <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
                  <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2 }}>{detail}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color }}>{sign}{fmt(tx.amount)}</div>
                    {isSell && tx.realized_pnl != null && (
                      <div style={{ fontSize: 11, color: parseFloat(tx.realized_pnl) >= 0 ? C.income : C.expense }}>
                        {lang === 'zh' ? '盈亏' : 'P&L'} {parseFloat(tx.realized_pnl) >= 0 ? '+' : ''}{fmt(tx.realized_pnl)}
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => onDeleteTrade(tx)} style={{ background: 'none', border: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 14, padding: 2 }}>✕</button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── StockHoldingsPanel ──────────────────────────────────────────────────────
function StockHoldingsPanel({ account, holdings, stockPrices, t, lang, isMobile, currency, trades, getCatName, onAdd, onBuy, onSell, onDeleteHolding, onRefresh, onUpdateManualPrice, onDeposit, onWithdraw, onDeleteTrade }) {
  const sym = currency.symbol;
  const fmt = (n) => `${sym}${parseFloat(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  const cash = parseFloat(account.cash_balance || 0);

  // Compute totals
  let totalValue = 0, totalCost = 0;
  holdings.forEach(h => {
    const price = (stockPrices[h.symbol]?.current) || h.current_price || h.avg_cost || 0;
    totalValue += parseFloat(h.shares || 0) * price;
    totalCost += parseFloat(h.shares || 0) * parseFloat(h.avg_cost || 0);
  });
  const totalPnl = totalValue - totalCost;
  const totalPnlPct = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;
  const grandTotal = cash + totalValue;

  return (
    <div>
      {/* Total value hero */}
      <div style={{ background: C.grad, borderRadius: 18, padding: isMobile ? '16px 18px' : '20px 24px', marginBottom: 14, color: '#fff', boxShadow: C.glow }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginBottom: 4 }}>{t.totalAssetsAcct}</div>
        <div className="display" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 10 }}>{fmt(grandTotal)}</div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5 }}>
          <span>{t.cashBalance}: <b>{fmt(cash)}</b></span>
          <span>{t.marketValue}: <b>{fmt(totalValue)}</b></span>
          <span>{t.unrealizedPnl}:
            <b style={{ marginLeft: 4, color: totalPnl >= 0 ? '#5FFFD6' : '#FF8872' }}>
              {totalPnl >= 0 ? '▲ +' : '▼ '}{fmt(Math.abs(totalPnl))} ({totalPnl >= 0 ? '+' : ''}{totalPnlPct.toFixed(2)}%)
            </b>
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" onClick={onDeposit} style={{ flex: isMobile ? 1 : 'none', padding: '8px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, color: C.income, fontWeight: 600 }}>↓ {t.depositCash}</button>
        <button type="button" onClick={onWithdraw} style={{ flex: isMobile ? 1 : 'none', padding: '8px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, color: C.expense, fontWeight: 600 }}>↑ {t.withdrawCash}</button>
        <button type="button" onClick={onRefresh} style={{ flex: isMobile ? 1 : 'none', padding: '8px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, color: C.inkSoft }}>↻ {t.refreshPrices}</button>
        <button type="button" onClick={onAdd} className="em-btn-primary" style={{ ...S.btn(true), flex: isMobile ? 1 : 'none', padding: '8px 14px', fontSize: 12.5 }}>+ {t.addStock}</button>
      </div>

      {/* Section label: holdings */}
      <div style={{ fontSize: 12, color: C.inkFaint, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '4px 2px 10px' }}>{t.stockHoldings}</div>

      {/* Holdings list */}
      {holdings.length === 0 ? (
        <div style={{ background: C.card, borderRadius: 18, padding: 24, textAlign: 'center', color: C.inkFaint, fontSize: 13.5, boxShadow: C.shadowMd, border: `1px solid ${C.border}`, marginBottom: 20 }}>
          {t.noHoldings}
        </div>
      ) : (
        holdings.map(h => {
          const live = stockPrices[h.symbol]?.current;
          const price = live || h.current_price || h.avg_cost || 0;
          const shares = parseFloat(h.shares || 0);
          const cost = parseFloat(h.avg_cost || 0);
          const value = shares * price;
          const pnl = (price - cost) * shares;
          const pnlPct = cost > 0 ? ((price - cost) / cost) * 100 : 0;
          const changePct = stockPrices[h.symbol]?.changePct;
          return (
            <div key={h.id} style={{ background: C.card, borderRadius: 16, padding: 16, marginBottom: 10, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 16 }}>{h.symbol}</span>
                    <span style={{ fontSize: 10, color: C.inkFaint, border: `1px solid ${C.border}`, borderRadius: 5, padding: '1px 5px' }}>
                      {h.market === 'US' ? t.usMarket : h.market === 'CN' ? t.cnMarket : t.hkMarket}
                    </span>
                    {typeof changePct === 'number' && (
                      <span style={{ fontSize: 11, color: changePct >= 0 ? C.income : C.expense }}>
                        {changePct >= 0 ? '▲' : '▼'} {Math.abs(changePct).toFixed(2)}%
                      </span>
                    )}
                  </div>
                  {h.company_name && <div style={{ fontSize: 11.5, color: C.inkFaint, marginTop: 2 }}>{h.company_name}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(value)}</div>
                  <div style={{
                    display: 'inline-block', marginTop: 4,
                    fontSize: 12, fontWeight: 700,
                    color: pnl >= 0 ? C.income : C.expense,
                    background: pnl >= 0 ? C.incomeSoft : C.expenseSoft,
                    padding: '2px 8px', borderRadius: 8,
                  }}>
                    {pnl >= 0 ? '▲ +' : '▼ '}{fmt(Math.abs(pnl))} ({pnl >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: C.inkSoft, marginBottom: 12 }}>
                <span>{t.shares}: <b style={{ color: C.ink }}>{shares.toLocaleString()}</b></span>
                <span>{t.avgCost}: <b style={{ color: C.ink }}>{fmt(cost)}</b></span>
                <span>{t.currentPrice}: <b style={{ color: C.ink }}>{fmt(price)}</b>{h.manual_price ? ` (${t.manualPrice})` : ''}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => onBuy(h)} style={{ flex: isMobile ? 1 : 'none', padding: '7px 14px', background: C.income, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{t.buyStock}</button>
                <button type="button" onClick={() => onSell(h)} style={{ flex: isMobile ? 1 : 'none', padding: '7px 14px', background: C.expense, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{t.sellStock}</button>
                {h.manual_price && (
                  <button type="button" onClick={() => {
                    const np = window.prompt(lang === 'zh' ? '输入最新股价' : 'Enter latest price', String(h.current_price || h.avg_cost));
                    if (np !== null) onUpdateManualPrice(h, np);
                  }} style={{ flex: isMobile ? 1 : 'none', padding: '7px 14px', background: C.card, color: C.inkSoft, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
                    {lang === 'zh' ? '改价' : 'Set Price'}
                  </button>
                )}
                <button type="button" onClick={() => onDeleteHolding(h)} style={{ padding: '7px 12px', background: 'none', color: C.inkFaint, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>🗑</button>
              </div>
            </div>
          );
        })
      )}

      {/* Trade history */}
      <div style={{ fontSize: 12, color: C.inkFaint, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '18px 2px 10px' }}>{t.tradeHistory}</div>
      <div style={{ background: C.card, borderRadius: 16, padding: 16, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
        {(!trades || trades.length === 0) ? (
          <div style={{ color: C.inkFaint, fontSize: 13, textAlign: 'center', padding: '8px 0' }}>{t.noTxn}</div>
        ) : (
          trades.map(tx => {
            const isBuy = tx.stock_action === 'buy';
            const isSell = tx.stock_action === 'sell';
            const isTransfer = tx.type === 'transfer';
            const isTransferIn = isTransfer && !tx.is_transfer_out;
            const isIncome = tx.type === 'income';
            // Money in (green, +): sell, income, transfer-in. Money out (red, -): buy, expense, transfer-out.
            const moneyIn = isSell || isIncome || isTransferIn;
            const color = moneyIn ? C.income : C.expense;
            const sign = moneyIn ? '+' : '-';
            let label = tx.description || (isTransfer ? (isTransferIn ? (lang === 'zh' ? '转入' : 'Transfer in') : (lang === 'zh' ? '转出' : 'Transfer out')) : '');
            if (isTransfer && !tx.description) label = isTransferIn ? (lang === 'zh' ? '转入现金' : 'Transfer in') : (lang === 'zh' ? '转出' : 'Transfer out');
            let detail = tx.date;
            if (isBuy || isSell) {
              detail = `${tx.date} · ${parseFloat(tx.stock_shares).toLocaleString()} ${lang === 'zh' ? '股' : 'sh'} @ ${fmt(tx.stock_price)}`;
            }
            return (
              <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
                  <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2 }}>{detail}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color }}>{sign}{fmt(tx.amount)}</div>
                    {isSell && tx.realized_pnl != null && (
                      <div style={{ fontSize: 11, color: parseFloat(tx.realized_pnl) >= 0 ? C.income : C.expense }}>
                        {lang === 'zh' ? '盈亏' : 'P&L'} {parseFloat(tx.realized_pnl) >= 0 ? '+' : ''}{fmt(tx.realized_pnl)}
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => onDeleteTrade(tx)} style={{ background: 'none', border: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 14, padding: 2 }}>✕</button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── BudgetView ──────────────────────────────────────────────────────────────
function BudgetView({ budgets, categories, t, lang, isMobile, defaultCurrency, getCatName, budgetSpent, budgetTxns, accounts, getAcctName, getCurrencyForAccount, convertToDefault, onAdd, onEdit, onDelete, onGoReports }) {
  const sym = defaultCurrency.symbol;
  const fmt = (n) => `${sym}${parseFloat(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const periodLabel = (p) => p === 'week' ? t.periodWeek : p === 'year' ? t.periodYear : t.periodMonth;
  const [expanded, setExpanded] = useState(null); // budget id whose details are shown

  return (
    <div>
      {/* Header actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8 }}>
        {isMobile && (
          <button type="button" onClick={onGoReports} style={{ padding: '8px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, color: C.inkSoft }}>📊 {t.reports}</button>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={onAdd} className="em-btn-primary" style={{ ...S.btn(true), padding: '9px 16px', fontSize: 13 }}>+ {t.addBudget}</button>
      </div>

      {budgets.length === 0 ? (
        <div style={{ background: C.card, borderRadius: 18, padding: 36, textAlign: 'center', color: C.inkFaint, fontSize: 13.5, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
          {t.noBudgets}
        </div>
      ) : (
        budgets.map(b => {
          const spent = budgetSpent(b);
          const amount = parseFloat(b.amount || 0);
          const pct = amount > 0 ? (spent / amount) * 100 : 0;
          const remaining = amount - spent;
          const over = spent > amount;
          const near = !over && pct >= 80;
          const barColor = over ? C.expense : near ? '#E8A317' : C.income;
          const title = b.category_id ? getCatName(b.category_id) : (b.name || t.budgetTotal);
          const isOpen = expanded === b.id;
          const txns = isOpen ? budgetTxns(b) : [];
          return (
            <div key={b.id} style={{ background: C.card, borderRadius: 16, padding: 18, marginBottom: 12, boxShadow: C.shadowMd, border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div onClick={() => setExpanded(isOpen ? null : b.id)} style={{ cursor: 'pointer', flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: C.inkFaint, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>▶</span>
                    {title}
                  </div>
                  <div style={{ fontSize: 11.5, color: C.inkFaint, marginTop: 2, marginLeft: 17 }}>
                    {periodLabel(b.period)}{b.name && b.category_id ? ` · ${b.name}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={() => onEdit(b)} style={{ background: 'none', border: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 14 }}>✎</button>
                  <button type="button" onClick={() => onDelete(b)} style={{ background: 'none', border: 'none', color: C.inkFaint, cursor: 'pointer', fontSize: 14 }}>🗑</button>
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ height: 10, background: C.bgDeep, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: barColor, borderRadius: 6, transition: 'width 0.3s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                <span style={{ color: C.inkSoft }}>
                  {t.spent} <b style={{ color: C.ink }}>{fmt(spent)}</b> / {fmt(amount)}
                </span>
                <span style={{ fontWeight: 600, color: over ? C.expense : C.income }}>
                  {over
                    ? `${t.overBudget} ${fmt(Math.abs(remaining))}`
                    : `${t.remaining} ${fmt(remaining)}`}
                  <span style={{ color: C.inkFaint, fontWeight: 400, marginLeft: 6 }}>({pct.toFixed(0)}%)</span>
                </span>
              </div>

              {/* Expanded transaction detail */}
              {isOpen && (
                <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                  {txns.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: C.inkFaint, textAlign: 'center', padding: '8px 0' }}>{t.noTxn}</div>
                  ) : (
                    txns.map(tx => {
                      const acct = accounts.find(a => a.id === tx.account_id);
                      const curr = getCurrencyForAccount(acct);
                      const inDefault = convertToDefault(tx.amount, curr.code);
                      return (
                        <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {tx.description || (b.category_id ? title : getCatName(tx.category_id))}
                            </div>
                            <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 2 }}>
                              {tx.date} · {getAcctName(tx.account_id)}{!b.category_id ? ` · ${getCatName(tx.category_id)}` : ''}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 10 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.expense }}>
                              -{curr.symbol}{parseFloat(tx.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </div>
                            {curr.code !== defaultCurrency.code && (
                              <div style={{ fontSize: 10.5, color: C.inkFaint }}>≈ {fmt(inDefault)}</div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ─── Modal ───────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  const mobile = typeof window !== 'undefined' && window.innerWidth <= 640;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,27,46,0.45)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: mobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 100 }}>
      <div className="em-pop" style={{
        background: C.card, padding: mobile ? '20px 16px' : 26,
        borderRadius: mobile ? '22px 22px 0 0' : 20,
        width: mobile ? '100%' : 360, maxWidth: '100%',
        maxHeight: mobile ? '92vh' : '90vh', overflow: 'auto',
        boxShadow: C.shadowLg, border: `1px solid ${C.border}`,
      }}>
        {mobile && <div style={{ width: 40, height: 4, background: C.border, borderRadius: 3, margin: '0 auto 14px' }} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div className="display" style={{ fontWeight: 700, fontSize: 17 }}>{title}</div>
          <button type="button" onClick={onClose} style={{ background: C.bgDeep, border: 'none', width: 30, height: 30, borderRadius: 9, fontSize: 16, cursor: 'pointer', color: C.inkSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── FormField ───────────────────────────────────────────────────────────────
function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 6, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

// ─── IncomeExpenseChart (recharts) ───────────────────────────────────────────
function LineChartSVG({ data, lang, currency, t }) {
  const fmtAxis = v => {
    if (Math.abs(v) >= 1000000) return (v / 1000000).toFixed(1) + 'M';
    if (Math.abs(v) >= 1000) return (v / 1000).toFixed(0) + 'k';
    return v.toString();
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const income = payload.find(p => p.dataKey === 'income')?.value ?? 0;
    const expense = payload.find(p => p.dataKey === 'expense')?.value ?? 0;
    return (
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px', fontSize: 12, boxShadow: C.shadowMd, color: C.ink }}>
        <div style={{ color: C.inkSoft, marginBottom: 4 }}>{label}</div>
        <div style={{ color: C.income }}>
          {lang === 'zh' ? '收入' : 'Income'}: {currency.symbol}{income.toLocaleString()}
        </div>
        <div style={{ color: C.expense }}>
          {lang === 'zh' ? '支出' : 'Expense'}: {currency.symbol}{expense.toLocaleString()}
        </div>
        <div style={{ color: C.inkSoft, marginTop: 4, borderTop: '1px solid #f0f0f0', paddingTop: 4 }}>
          {lang === 'zh' ? '结余' : 'Net'}: {currency.symbol}{(income - expense).toLocaleString()}
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: '100%', height: 300 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.inkFaint }} tickLine={false} axisLine={{ stroke: C.border }} />
          <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: C.inkFaint }} tickLine={false} axisLine={false} width={50} />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            formatter={v => v === 'income' ? (lang === 'zh' ? '收入' : 'Income') : (lang === 'zh' ? '支出' : 'Expense')}
            wrapperStyle={{ fontSize: 12 }}
          />
          <Line type="monotone" dataKey="income" stroke={C.chartIncome} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="expense" stroke={C.chartExpense} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── AdminPanel ──────────────────────────────────────────────────────────────
function AdminPanel({ lang, theme = 'light', onBack, onSignOut }) {
  const [users, setUsers] = useState([]);
  const [requireApproval, setRequireApproval] = useState(true);
  const [loading, setLoading] = useState(true);

  const T = {
    zh: {
      title: '管理后台', back: '← 返回账本', signOut: '退出登录',
      userMgmt: '用户管理', email: '邮箱', role: '角色', status: '状态', registered: '注册时间', lastLogin: '最后登录', never: '从未',
      actions: '操作', approve: '批准', disable: '停用', enable: '启用',
      pending: '待审核', active: '已启用', disabled: '已停用',
      admin: '管理员', user: '用户',
      plan: '会员', proLabel: 'Pro', freeLabel: '免费', giftPro: '设为Pro', revokePro: '取消Pro', proGift: 'Pro(赠送)', proPaid: 'Pro(付费)',
      settings: '系统设置', requireApprovalLabel: '新用户需管理员批准',
      requireApprovalHint: '关闭后，新注册用户可立即使用；开启则需你在此批准。',
      noUsers: '暂无用户', loading: '载入中...',
    },
    en: {
      title: 'Admin Panel', back: '← Back to app', signOut: 'Sign Out',
      userMgmt: 'User Management', email: 'Email', role: 'Role', status: 'Status', registered: 'Registered', lastLogin: 'Last Login', never: 'Never',
      actions: 'Actions', approve: 'Approve', disable: 'Disable', enable: 'Enable',
      pending: 'Pending', active: 'Active', disabled: 'Disabled',
      admin: 'Admin', user: 'User',
      plan: 'Plan', proLabel: 'Pro', freeLabel: 'Free', giftPro: 'Grant Pro', revokePro: 'Revoke Pro', proGift: 'Pro (gift)', proPaid: 'Pro (paid)',
      settings: 'System Settings', requireApprovalLabel: 'New users need admin approval',
      requireApprovalHint: 'Off: new users can use immediately. On: you approve them here.',
      noUsers: 'No users yet', loading: 'Loading...',
    },
  }[lang];

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const [{ data: profs }, { data: settings }] = await Promise.all([
      supabase.from('profiles').select('*').order('created_at', { ascending: false }),
      supabase.from('app_settings').select('*').eq('key', 'require_approval').single(),
    ]);
    setUsers(profs || []);
    setRequireApproval(settings?.value !== 'false');
    setLoading(false);
  }

  async function setStatus(id, status) {
    await supabase.from('profiles').update({ status }).eq('id', id);
    load();
  }

  async function setPlan(id, plan) {
    // plan: 'pro' (admin gift) or 'free'
    await supabase.from('profiles').update({
      plan,
      plan_source: plan === 'pro' ? 'gift' : null,
      plan_expires: null, // admin gift = no expiry
    }).eq('id', id);
    load();
  }

  async function toggleApproval() {
    const newVal = requireApproval ? 'false' : 'true';
    setRequireApproval(!requireApproval); // optimistic update
    const { error } = await supabase.from('app_settings')
      .upsert({ key: 'require_approval', value: newVal }, { onConflict: 'key' });
    if (error) {
      console.error('toggleApproval error:', error);
      setRequireApproval(requireApproval); // revert on failure
      alert(lang === 'zh' ? '保存失败，请重试。' : 'Save failed, please try again.');
    }
  }

  const statusLabel = s => s === 'active' ? T.active : s === 'pending' ? T.pending : T.disabled;
  const statusColor = s => s === 'active' ? C.income : s === 'pending' ? '#C99700' : C.expense;

  // Format a timestamp as "YYYY-MM-DD HH:mm" in local time
  const fmtTime = ts => {
    if (!ts) return T.never;
    const d = new Date(ts);
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: 28, color: C.ink }}>
      <ThemeStyles mode={theme} key={theme} />
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button type="button" onClick={onBack}
              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: C.inkSoft }}>
              {T.back}
            </button>
            <div style={{ fontSize: 20, fontWeight: 600 }}>⚙ {T.title}</div>
          </div>
          <button type="button" onClick={onSignOut}
            style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: C.expense }}>
            {T.signOut}
          </button>
        </div>

        {/* System settings */}
        <div style={{ background: C.card, borderRadius: 16, padding: 20, marginBottom: 20, border: `1px solid ${C.border}`, boxShadow: C.shadowMd }}>
          <div style={{ fontWeight: 500, marginBottom: 14 }}>{T.settings}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 14 }}>{T.requireApprovalLabel}</div>
              <div style={{ fontSize: 12, color: C.inkFaint, marginTop: 2 }}>{T.requireApprovalHint}</div>
            </div>
            <button type="button" onClick={toggleApproval}
              style={{
                width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                background: requireApproval ? C.income : C.inkFaint, position: 'relative', transition: 'background 0.2s',
              }}>
              <span style={{
                position: 'absolute', top: 3, left: requireApproval ? 27 : 3,
                width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
              }} />
            </button>
          </div>
        </div>

        {/* User management */}
        <div style={{ background: C.card, borderRadius: 16, padding: 20, border: `1px solid ${C.border}`, boxShadow: C.shadowMd }}>
          <div style={{ fontWeight: 500, marginBottom: 14 }}>{T.userMgmt}</div>
          {loading ? (
            <div style={{ color: C.inkFaint, fontSize: 13 }}>{T.loading}</div>
          ) : users.length === 0 ? (
            <div style={{ color: C.inkFaint, fontSize: 13 }}>{T.noUsers}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: C.inkFaint, fontSize: 12, textAlign: 'left' }}>
                  <th style={{ padding: '8px 4px' }}>{T.email}</th>
                  <th style={{ padding: '8px 4px', width: 80 }}>{T.role}</th>
                  <th style={{ padding: '8px 4px', width: 80 }}>{T.status}</th>
                  <th style={{ padding: '8px 4px', width: 90 }}>{T.plan}</th>
                  <th style={{ padding: '8px 4px', width: 140 }}>{T.registered}</th>
                  <th style={{ padding: '8px 4px', width: 140 }}>{T.lastLogin}</th>
                  <th style={{ padding: '8px 4px', width: 150 }}>{T.actions}</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: '10px 4px' }}>{u.email}</td>
                    <td style={{ padding: '10px 4px' }}>
                      {u.role === 'admin'
                        ? <span style={{ color: C.transfer, fontWeight: 500 }}>{T.admin}</span>
                        : T.user}
                    </td>
                    <td style={{ padding: '10px 4px' }}>
                      <span style={{ color: statusColor(u.status), fontWeight: 500 }}>{statusLabel(u.status)}</span>
                    </td>
                    <td style={{ padding: '10px 4px' }}>
                      {u.plan === 'pro' ? (
                        <span style={{
                          color: '#fff', background: C.transfer || '#8B6FE8', fontWeight: 600,
                          fontSize: 11, padding: '2px 8px', borderRadius: 6,
                        }}>
                          {u.plan_source === 'paid' ? T.proPaid : T.proGift}
                        </span>
                      ) : (
                        <span style={{ color: C.inkFaint, fontSize: 12 }}>{T.freeLabel}</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 4px', color: C.inkSoft, fontSize: 12 }}>{fmtTime(u.created_at)}</td>
                    <td style={{ padding: '10px 4px', color: C.inkSoft, fontSize: 12 }}>{fmtTime(u.last_sign_in_at)}</td>
                    <td style={{ padding: '10px 4px' }}>
                      {u.role !== 'admin' && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {u.status !== 'active' && (
                            <button type="button" onClick={() => setStatus(u.id, 'active')}
                              style={{ background: C.income, color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                              {u.status === 'pending' ? T.approve : T.enable}
                            </button>
                          )}
                          {u.status !== 'disabled' && (
                            <button type="button" onClick={() => setStatus(u.id, 'disabled')}
                              style={{ background: C.card, color: C.expense, border: `1px solid ${C.expense}`, borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                              {T.disable}
                            </button>
                          )}
                          {u.plan === 'pro' ? (
                            <button type="button" onClick={() => setPlan(u.id, 'free')}
                              style={{ background: C.card, color: C.inkSoft, border: `1px solid ${C.border}`, borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                              {T.revokePro}
                            </button>
                          ) : (
                            <button type="button" onClick={() => setPlan(u.id, 'pro')}
                              style={{ background: C.transfer || '#8B6FE8', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                              {T.giftPro}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
